import React, { useState, useEffect, useRef, useMemo } from 'react';
import { message, Popover } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useProjectStore } from '../hooks/useProjectStore';
import { fractureAPI, wellboreAPI, formationAPI, mechanicsAPI } from '../services/api';
import { useAgentCommands } from '../services/agent/registry';
import type {
  P3DSimulationReadiness, PublishedNaturalFractureModel, SimulationJob,
} from '../services/api';
import type {
  StageResult, FracSimulation as Sim, FracDesign as Design,
  SurveyPoint, FormationLayerRow, HorizonSurfaceRow, SimulationOptions,
} from './fracsim/types';
import { effColor } from './fracsim/types';
import StressInfoCard from './fracsim/StressInfoCard';
import {
  buildSimulationPlayback,
  appendCommittedPlaybackFrame,
  getPlaybackFrameAtTime,
} from './fracsim/simulationPlayback';
import type {
  CommittedSimulationFrame,
  SimulationPlaybackCluster,
  SimulationPlaybackFrame,
} from './fracsim/simulationPlayback';
import { useSimulationClock, type PlaybackSpeed } from './fracsim/useSimulationClock';
import { getDisplayWidthM, resolveFractureVerticalExtents } from './fracsim/simulationFractureGeometry';
import {
  buildFractureRenderTemplates,
  fractureRenderTopologyKey,
  naturalFractureDisplayStyle,
  resolveHydraulicFractureRenderState,
  resolveNaturalFractureVisualState,
  type NaturalFractureVisualState,
} from './fracsim/fractureRenderScope';
import {
  RESULT_GROUPS,
  RESULT_PROPERTIES,
  calculateResultRange,
  branchResultValue,
  branchSegmentResultValue,
  categoryColor,
  resultCategories,
  resultColor,
  resultGradient,
  surfaceNodeResultValue,
  resultTicks,
  type ResultPropertyId,
  type ResultRange,
  type ResultRangeMethod,
  type ResultRangeScope,
} from './fracsim/resultProperties';
import ScenarioWorkspace from './fracsim/ScenarioWorkspace';
import FracStageDetailPage from './fracsim/FracStageDetailPage';
import { solverOptionsOnly } from './fracsim/simulationOptions';
import { loadFracSimState, saveFracSimState } from './fracsim/fracSimPersistence';
import { frameBelongsToJob, streamingFrameIndex } from './fracsim/simulationStreaming';
import {
  FRACSIM_WORKFLOW_STEPS,
  defaultSubSection,
  isSubSectionForWorkflow,
  scenarioWorkflowStepFor,
  workflowSections,
  type FracSimSubSection,
  type FracSimWorkflowStep,
  type ScenarioWorkflowStep,
} from './fracsim/fracSimNavigation';
import { comparisonGlobalRange, interactionDetailRows } from './fracsim/scenarioAnalysis';
import { loadActiveEvaluationRun } from './sweetspot/sweetSpotEvaluationStorage';
import { parseSimulationHandoff, simulationHandoffUrl, summarizeSimulationDesignInput } from './fracsim/designSimulationHandoff';
import NaturalFractureDesignDialog from './fracsim/NaturalFractureDesignDialog';
import DynamicSimulationCurves from './fracsim/DynamicSimulationCurves';
import {
  defaultNaturalFractureDesign,
  generateCustomNaturalFractures,
  interpolateSurveyXY,
  naturalFractureViewerRecords,
  naturalFractureSourceLabel,
  normalizeNaturalFractureDesign,
  type NaturalFractureDesignDTO,
  type RawGeologyNaturalFracture,
} from './fracsim/naturalFractureDesign';

// ═══════════════════════════════════════════════
// FracSight 压裂模拟 — P3D 拟三维缝网 (Simonson 平衡高度 + 三层应力对比)
// ═══════════════════════════════════════════════

const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.06)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  statBorder: '#ECEEF1', well: '#2C3E50', sim: '#6A4C93',
};

const panelLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, color: '#4F6680',
};
const panelControl: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', fontSize: 11, padding: '5px 6px',
  borderRadius: 3, border: '1px solid #D6DAE0', background: '#fff', color: '#1E2A3A',
};
const checkLabel: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#4F6680',
};
const panelButton: React.CSSProperties = {
  padding: '6px 8px', borderRadius: 3, border: '1px solid #D6DAE0',
  background: '#F4F6F9', color: '#4F6680', fontSize: 10, cursor: 'pointer',
};
const helpText: React.CSSProperties = {
  padding: '7px 8px', background: '#F4F6F9', borderRadius: 3, fontSize: 10,
  color: '#4F6680', lineHeight: 1.55,
};
const clickHelpButtonStyle: React.CSSProperties = {
  width: 18, height: 18, padding: 0, borderRadius: '50%', border: '1px solid #9BAABC',
  background: '#fff', color: '#65788C', fontSize: 11, lineHeight: '16px', cursor: 'pointer',
};
function ClickHelp({ content, label = '查看说明' }: { content: React.ReactNode; label?: string }) {
  return <Popover trigger="click" placement="bottomLeft" content={<div style={{ maxWidth: 280, lineHeight: 1.65, color: '#40566B' }}>{content}</div>}>
    <button type="button" aria-label={label} style={clickHelpButtonStyle}>?</button>
  </Popover>;
}
const toolbarButton: React.CSSProperties = {
  padding: '4px 7px', borderRadius: 3, border: '1px solid #D6DAE0',
  background: '#fff', color: '#4F6680', fontSize: 10, cursor: 'pointer',
};
const DEFAULT_NF_FRACTURE_SET: Record<string, unknown> = {
  id: 'NF-SET-1', name: 'Background Set 1', enabled: true,
  intensityDefinition: 'FRACTURE_COUNT', fractureCount: 100,
  meanDipDirectionDeg: 90, directionDispersionDeg: 10,
  meanDipDeg: 70, dipDispersionDeg: 8,
  lengthDistribution: 'UNIFORM', minimumLengthM: 5,
  representativeLengthM: 15, maximumLengthM: 30, aspectRatio: 2,
  frictionCoefficient: 0.6, cohesionPa: 0,
  randomSeedOffset: 0,
};
const NF_VISUAL_LABELS: Record<NaturalFractureVisualState, string> = {
  UNACTIVATED: '天然裂缝（未激活）',
  OPEN: 'OPEN / 已开启沟通',
  CROSS: 'CROSS / 穿越',
  DEFLECT: 'DEFLECT / 转向',
  ARREST: 'ARREST / 停止',
  INTERACTED: '已交互',
};
const interactionRenderKey = (interaction: Record<string, any>, index = 0) => String(
  interaction.interactionId ?? interaction.sourceNaturalFractureId
  ?? interaction.naturalFractureId
  ?? `${interaction.stage ?? ''}:${interaction.cluster ?? ''}:${interaction.time ?? ''}:${index}`,
);
const toolbarSelect: React.CSSProperties = {
  maxWidth: 178, padding: '3px 5px', borderRadius: 3, border: '1px solid #D6DAE0',
  background: '#fff', color: '#1E2A3A', fontSize: 10,
};
const toolbarInput: React.CSSProperties = {
  padding: '3px 4px', borderRadius: 3, border: '1px solid #D6DAE0', fontSize: 10,
};
const toolbarCheck: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 2, color: '#4F6680', fontSize: 10,
};
const toolbarActionButton = (
  tone: 'VIEW' | 'INFO' | 'RESULT' | 'ANALYSIS' | 'EXPORT',
  active = false,
): React.CSSProperties => {
  const colors = {
    VIEW: { background: '#EDF4FA', border: '#AFC3D5', color: '#315F87', active: '#DCEAF5' },
    INFO: { background: '#F3EFF8', border: '#B9AAD0', color: '#684D89', active: '#E7DFF1' },
    RESULT: { background: '#FFF4E8', border: '#D9B184', color: '#945E28', active: '#FBE6CD' },
    ANALYSIS: { background: '#ECF7F3', border: '#9EC8B9', color: '#2D705C', active: '#D9EFE7' },
    EXPORT: { background: '#F1F4F7', border: '#BFC9D3', color: '#465B70', active: '#E2E8EE' },
  }[tone];
  return {
    ...toolbarButton,
    background: active ? colors.active : colors.background,
    borderColor: colors.border,
    color: colors.color,
    fontWeight: active ? 700 : 600,
  };
};
const miniTd: React.CSSProperties = {
  padding: '3px 2px', textAlign: 'right', color: '#1E2A3A', borderBottom: '1px solid #ECEEF1', fontFamily: 'monospace',
};

// 模块级缓存
const FS_DESIGNS: Record<number, Design[]> = {};
const FS_SIMS: Record<number, Sim[]> = {};
const FS_SURVEY: Record<number, SurveyPoint[]> = {};
const FS_LAYERS: Record<number, FormationLayerRow[]> = {};
const FS_HORIZONS: Record<number, HorizonSurfaceRow[]> = {};

type StressProfileRow = {
  depth?: number;
  stress_direction?: number | null;
  youngs_modulus?: number | null;
  poissons_ratio?: number | null;
  dynamic_youngs_modulus?: number | null;
  dynamic_poissons_ratio?: number | null;
  sigma_h?: number | null;
  tensile_strength?: number | null;
  depth_reference?: string;
};

type InputLoadStatus = 'LOADING' | 'READY' | 'EMPTY' | 'ERROR';

const seedHeightSourceLabel = (source?: string): string => ({
  explicit: '用户显式输入',
  geomechanical_sublayer: '地质力学子层',
  perforated_interval: '射孔层段',
  formation_fallback: '地层厚度回退值',
}[source ?? ''] ?? '不可用');

const formatResultTick = (value: number, range: ResultRange): string => {
  if (!range.available) return '—';
  const span = Math.abs(range.max - range.min);
  return value.toFixed(span < 1 ? 2 : 1);
};

const niceGridStep = (span: number, targetLines: number): number => {
  if (!Number.isFinite(span) || span <= 0) return 0;
  const raw = span / Math.max(1, targetLines);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return factor * magnitude;
};

const formatMetres = (value: number): string => (
  Math.abs(value) >= 1000
    ? Math.round(value).toLocaleString('zh-CN')
    : Number.isInteger(value) ? String(value) : value.toFixed(1)
);

type SceneBackground = 'LIGHT' | 'DARK';
type WellboreRangeMode = 'STIMULATED' | 'UPSTREAM_500' | 'UPSTREAM_1000' | 'FULL' | 'CUSTOM';
type AvailableFractureModel = SimulationOptions['model_type'];
type SimulationRunState = 'IDLE' | 'STARTING' | 'LIVE' | 'PLAYBACK' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

const fractureModelLabel = (modelType: string): string => ({
  PKN: 'PKN 模型',
  P3D: 'P3D 模型',
  UFM: 'UFM 模型',
}[String(modelType).toUpperCase()] ?? String(modelType));

export default function FracSimPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const handoff = useMemo(() => parseSimulationHandoff(location.search), [location.search]);
  const restoredState = useMemo(() => loadFracSimState(projectId), [projectId]);

  const [designs, setDesigns] = useState<Design[]>(projectId ? FS_DESIGNS[projectId] ?? [] : []);
  const [designId, setDesignId] = useState<number | null>(restoredState.designId);
  const [sims, setSims] = useState<Sim[]>([]);
  const [simId, setSimId] = useState<number | null>(restoredState.simulationId);
  const [selStage, setSelStage] = useState<number | null>(null);
  const [selectedStageNumbers, setSelectedStageNumbers] = useState<number[]>([]);
  const [stageSelectionDesignId, setStageSelectionDesignId] = useState<number | null>(null);
  const [loading, setLoading] = useState(!(projectId && FS_DESIGNS[projectId]));
  const [designLoadStatus, setDesignLoadStatus] = useState<InputLoadStatus>(
    projectId && FS_DESIGNS[projectId] ? (FS_DESIGNS[projectId].length ? 'READY' : 'EMPTY') : 'LOADING',
  );
  const [busy, setBusy] = useState(false);
  const [streamJobId, setStreamJobId] = useState<string | null>(restoredState.streamJobId);
  const [streamJob, setStreamJob] = useState<SimulationJob | null>(null);
  const [simulationRunState, setSimulationRunState] = useState<SimulationRunState>('IDLE');
  const [streamViewerMode, setStreamViewerMode] = useState<'LIVE' | 'PLAYBACK'>('LIVE');
  const [streamFrames, setStreamFrames] = useState<SimulationPlaybackFrame[]>([]);
  const [streamFrameIndex, setStreamFrameIndex] = useState(0);
  const [pendingContextRun, setPendingContextRun] = useState<{
    designId: number;
    modelType: AvailableFractureModel;
    requestId: number;
  } | null>(null);
  const streamEventIdRef = useRef(0);
  const simulationRunStateRef = useRef<SimulationRunState>('IDLE');
  const streamContextRef = useRef<{ jobId: string; projectId: number; designId: number; simulationId: number } | null>(null);
  const simulationContextVersionRef = useRef(0);
  const claimedContextRunRef = useRef<number | null>(null);
  const simulationLoadRequestRef = useRef(0);
  const activeDesignRef = useRef<number | null>(designId);
  const [readiness, setReadiness] = useState<P3DSimulationReadiness | null>(null);
  const [apertureExaggeration, setApertureExaggeration] = useState(100);
  const [resultVariableId, setResultVariableId] = useState<ResultPropertyId>(restoredState.resultProperty);
  const [rangeScope, setRangeScope] = useState<ResultRangeScope>(restoredState.rangeScope);
  const [rangeMethod, setRangeMethod] = useState<ResultRangeMethod>(restoredState.rangeMethod);
  const [manualMin, setManualMin] = useState(restoredState.manualMin);
  const [manualMax, setManualMax] = useState(restoredState.manualMax);
  const [selectedPreset, setSelectedPreset] = useState(restoredState.reservoirPreset);
  const [activeWorkflowStep, setActiveWorkflowStep] = useState<FracSimWorkflowStep>(restoredState.activeWorkflowStep);
  const [activeSubSection, setActiveSubSection] = useState<FracSimSubSection>(restoredState.activeSubSection);
  const [showHydraulicFractures, setShowHydraulicFractures] = useState(true);
  const [showWellbore, setShowWellbore] = useState(true);
  const [wellboreRangeMode, setWellboreRangeMode] = useState<WellboreRangeMode>('UPSTREAM_500');
  const [wellboreCustomUpstreamM, setWellboreCustomUpstreamM] = useState(500);
  const [showGeologyGrid, setShowGeologyGrid] = useState(true);
  const [sceneBackground, setSceneBackground] = useState<SceneBackground>('LIGHT');
  const [showNaturalFractures, setShowNaturalFractures] = useState(restoredState.naturalFractureDisplay);
  const [naturalFractureColor, setNaturalFractureColor] = useState(restoredState.naturalFractureColor);
  const [showStressDirection, setShowStressDirection] = useState(false);
  const [showInteractionPoints, setShowInteractionPoints] = useState(restoredState.interactionPointDisplay);
  const [selectedInteraction, setSelectedInteraction] = useState<Record<string, unknown> | null>(null);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [activeBottomDock, setActiveBottomDock] = useState<'RESULTS' | 'STAGE' | 'CURVES' | null>(null);
  const [hoveredFracture, setHoveredFracture] = useState<{ clusterId?: string; stageNo: number; x: number; y: number } | null>(null);
  const [resetViewNonce, setResetViewNonce] = useState(0);
  const [seedHeightInput, setSeedHeightInput] = useState('');
  const [pendingPlaybackSimId, setPendingPlaybackSimId] = useState<number | null>(null);
  const runStartedAtRef = useRef<number | null>(null);
  const firstFrameReceivedAtRef = useRef<number | null>(null);
  const firstFrameRenderedRef = useRef(false);
  const [survey, setSurvey] = useState<SurveyPoint[]>(projectId ? FS_SURVEY[projectId] ?? [] : []);
  const handledPlaybackResultRef = useRef<string | null>(null);
  const persistedProjectRef = useRef(projectId);
  const skipPersistProjectRef = useRef<number | undefined>();
  const materialDefaultsAppliedRef = useRef('');
  const [layers, setLayers] = useState<FormationLayerRow[]>(projectId ? FS_LAYERS[projectId] ?? [] : []);
  const [horizons, setHorizons] = useState<HorizonSurfaceRow[]>(projectId ? FS_HORIZONS[projectId] ?? [] : []);
  const [geologyLoadStatus, setGeologyLoadStatus] = useState<InputLoadStatus>(
    projectId && (FS_LAYERS[projectId] || FS_HORIZONS[projectId])
      ? ((FS_LAYERS[projectId]?.length || FS_HORIZONS[projectId]?.length) ? 'READY' : 'EMPTY')
      : 'LOADING',
  );
  const [mechanicsLoadStatus, setMechanicsLoadStatus] = useState<InputLoadStatus>('LOADING');
  const [natFracs, setNatFracs] = useState<any[]>([]);
  const [rawGeologyNaturalFractures, setRawGeologyNaturalFractures] = useState<RawGeologyNaturalFracture[]>([]);
  const [geologyFractureLoadStatus, setGeologyFractureLoadStatus] = useState<InputLoadStatus>('LOADING');
  const [naturalFractureDesignOpen, setNaturalFractureDesignOpen] = useState(false);
  const [naturalFractureDesign, setNaturalFractureDesign] = useState<NaturalFractureDesignDTO>(() => (
    normalizeNaturalFractureDesign(
      (restoredState.simulationOptions.natural_fracture_design as NaturalFractureDesignDTO | undefined)
        ?? defaultNaturalFractureDesign([], { centerX: 0, centerY: 0, centerZ: 0, widthM: 500, heightM: 300 }),
    )
  ));
  const [nfModels, setNfModels] = useState<PublishedNaturalFractureModel[]>([]);
  const [selectedNfModelId, setSelectedNfModelId] = useState<number | null>(restoredState.naturalFractureModelId);
  const [selectedNfModel, setSelectedNfModel] = useState<PublishedNaturalFractureModel | null>(null);
  const [naturalFracturePhysics, setNaturalFracturePhysics] = useState(restoredState.naturalFracturePhysics);
  const [selectedOptimizationVariables, setSelectedOptimizationVariables] = useState<string[]>(restoredState.optimizationVariables);
  const [topN, setTopN] = useState<3 | 5 | 10>(restoredState.topN);
  const [comparisonSchemeIds, setComparisonSchemeIds] = useState<string[]>(restoredState.comparisonSchemeIds);
  const [designMode, setDesignMode] = useState(restoredState.designMode);
  const [engineeringConstraints, setEngineeringConstraints] = useState(restoredState.engineeringConstraints);
  const [objectiveTemplate, setObjectiveTemplate] = useState(restoredState.objectiveTemplate);
  const [activeSweetSpotRun, setActiveSweetSpotRun] = useState<any | null>(null);
  const [comparisonSimulationIds, setComparisonSimulationIds] = useState<number[]>([]);
  const [comparisonRangeMode, setComparisonRangeMode] = useState<'PER_SCHEME' | 'COMPARISON_GLOBAL' | 'MANUAL'>('COMPARISON_GLOBAL');
  const [stressRows, setStressRows] = useState<StressProfileRow[]>([]);
  const [opts, setOpts] = useState<SimulationOptions>({
    fluid_n: 0.5, fluid_k: 0.5, leakoff_c: 0.00015,
    youngs_modulus: 35, poissons_ratio: 0.22, frac_height: 35,
    model_type: 'P3D' as 'P3D' | 'PKN' | 'UFM',
    surface_model: 'DIRECTIONAL_ADAPTIVE_SURFACE_V1',
    wing_mode: 'SYMMETRIC_WINGS',
    n_steps: 20,
    natural_fracture_source_mode: 'GEOLOGY',
    nf_search_margin_factor: 1.3,
    nf_random_seed: 20260908,
    natural_fracture_sets: [DEFAULT_NF_FRACTURE_SET],
    ...restoredState.simulationOptions,
  });
  const scenarioWorkflowStep = scenarioWorkflowStepFor(activeWorkflowStep, activeSubSection);
  const currentWorkflow = FRACSIM_WORKFLOW_STEPS.find((step) => step.id === activeWorkflowStep)
    ?? FRACSIM_WORKFLOW_STEPS[0];
  const currentWorkflowSections = workflowSections[activeWorkflowStep];
  const currentSubSection = currentWorkflowSections.find((section) => section.id === activeSubSection)
    ?? currentWorkflowSections[0];

  useEffect(() => {
    simulationRunStateRef.current = simulationRunState;
  }, [simulationRunState]);

  useEffect(() => {
    if (opts.model_type !== 'UFM') return;
    setNaturalFracturePhysics(true);
    setShowNaturalFractures(true);
  }, [opts.model_type]);

  const selectWorkflowStep = (nextStep: FracSimWorkflowStep) => {
    setActiveWorkflowStep(nextStep);
    setActiveSubSection((current) => isSubSectionForWorkflow(nextStep, current)
      ? current
      : defaultSubSection(nextStep));
  };

  const selectScenarioWorkflowStep = (nextStep: ScenarioWorkflowStep) => {
    if (nextStep === 'SENSITIVITY') {
      setActiveWorkflowStep('OPTIMIZATION');
      setActiveSubSection('敏感性分析');
      return;
    }
    selectWorkflowStep(nextStep);
  };

  const loadDesigns = async () => {
    if (!projectId) { setLoading(false); return; }
    const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);
    const { data } = await fractureAPI.listDesigns(projectId);
    const ds: Design[] = un(data);
    setDesigns(ds);
    FS_DESIGNS[projectId] = ds;
    setDesignLoadStatus(ds.length ? 'READY' : 'EMPTY');
    const act = ds.find((d) => d.is_active) ?? ds[0];
    setDesignId((current) => ds.some((item) => item.id === current) ? current : act?.id ?? null);
  };

  const loadSims = async (did: number) => {
    const requestId = ++simulationLoadRequestRef.current;
    const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);
    if (FS_SIMS[did]) {
      setSims(FS_SIMS[did]);
      setSimId((current) => FS_SIMS[did].some((item) => item.id === current) ? current : FS_SIMS[did][0]?.id ?? null);
    }
    const { data } = await fractureAPI.listSimulations({ design: did });
    if (requestId !== simulationLoadRequestRef.current) return;
    const ss: Sim[] = un(data);
    setSims(ss);
    FS_SIMS[did] = ss;
    setSimId((current) => ss.some((item) => item.id === current) ? current : ss[0]?.id ?? null);
  };

  useEffect(() => {
    if (!projectId) return;
    const cachedDesigns = FS_DESIGNS[projectId];
    setDesigns(cachedDesigns ?? []);
    setDesignLoadStatus(cachedDesigns ? (cachedDesigns.length ? 'READY' : 'EMPTY') : 'LOADING');
    if (!cachedDesigns) setLoading(true);
    loadDesigns().catch(() => {
      setDesignLoadStatus('ERROR');
      message.error('压裂方案加载失败');
    })
      .finally(() => setLoading(false));

    // 拉测斜和地层 (3D 场景需要), 都用进程缓存
    const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);
    setSurvey(FS_SURVEY[projectId] ?? []);
    setLayers(FS_LAYERS[projectId] ?? []);
    setHorizons(FS_HORIZONS[projectId] ?? []);
    if (!FS_SURVEY[projectId]) {
      wellboreAPI.get3DTrajectory(projectId).then(({ data }) => {
        const pts: SurveyPoint[] = un(data);
        FS_SURVEY[projectId] = pts;
        setSurvey(pts);
      }).catch(() => {});
    }
    const cachedLayers = FS_LAYERS[projectId];
    const cachedHorizons = FS_HORIZONS[projectId];
    setGeologyLoadStatus(cachedLayers || cachedHorizons
      ? ((cachedLayers?.length || cachedHorizons?.length) ? 'READY' : 'EMPTY')
      : 'LOADING');
    Promise.allSettled([
      formationAPI.getLayers(projectId),
      formationAPI.listHorizons(projectId),
    ]).then(([layerResult, horizonResult]) => {
      const ls: FormationLayerRow[] = layerResult.status === 'fulfilled' ? un(layerResult.value.data) : cachedLayers ?? [];
      const hs: HorizonSurfaceRow[] = horizonResult.status === 'fulfilled' ? un(horizonResult.value.data) : cachedHorizons ?? [];
      if (layerResult.status === 'fulfilled') FS_LAYERS[projectId] = ls;
      if (horizonResult.status === 'fulfilled') FS_HORIZONS[projectId] = hs;
      setLayers(ls);
      setHorizons(hs);
      setGeologyLoadStatus(ls.length || hs.length
        ? 'READY'
        : layerResult.status === 'rejected' || horizonResult.status === 'rejected' ? 'ERROR' : 'EMPTY');
    });
    // 天然裂缝
    setMechanicsLoadStatus('LOADING');
    mechanicsAPI.getProfile(projectId).then(({ data }) => {
      const rows = Array.isArray(data) ? data : (data as any)?.results ?? [];
      setStressRows(rows as StressProfileRow[]);
      setMechanicsLoadStatus(rows.length ? 'READY' : 'EMPTY');
    }).catch(() => {
      setStressRows([]);
      setMechanicsLoadStatus('ERROR');
    });

    fractureAPI.listPublishedNaturalFractureModels(projectId).then(({ data }) => {
      const models = data.results ?? [];
      setNfModels(models);
      setSelectedNfModelId((current) => models.some((model) => model.modelId === current) ? current : models[0]?.modelId ?? null);
    }).catch(() => {
      setNfModels([]);
      setSelectedNfModelId(null);
    });
    setGeologyFractureLoadStatus('LOADING');
    formationAPI.getAllNaturalFractures(projectId).then(({ data }) => {
      const fractures = un(data) as RawGeologyNaturalFracture[];
      setRawGeologyNaturalFractures(fractures);
      setGeologyFractureLoadStatus(fractures.length ? 'READY' : 'EMPTY');
      setNaturalFractureDesign((current) => ({
        ...current,
        raw_geology_fractures: fractures.map((fracture) => ({ ...fracture })),
      }));
    }).catch(() => {
      setRawGeologyNaturalFractures([]);
      setGeologyFractureLoadStatus('ERROR');
    });
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setActiveSweetSpotRun(null);
      return;
    }
    loadActiveEvaluationRun(projectId)
      .then((run) => setActiveSweetSpotRun(run?.status === 'active' ? run : null))
      .catch(() => setActiveSweetSpotRun(null));
  }, [projectId]);

  useEffect(() => {
    if (persistedProjectRef.current === projectId) return;
    persistedProjectRef.current = projectId;
    skipPersistProjectRef.current = projectId;
    setDesignId(restoredState.designId);
    setSimId(restoredState.simulationId);
    setStreamJobId(restoredState.streamJobId);
    setStreamJob(null);
    setStreamFrames([]);
    setSimulationRunState('IDLE');
    setStreamViewerMode('LIVE');
    setSelectedPreset(restoredState.reservoirPreset);
    setResultVariableId(restoredState.resultProperty);
    setRangeScope(restoredState.rangeScope);
    setRangeMethod(restoredState.rangeMethod);
    setManualMin(restoredState.manualMin);
    setManualMax(restoredState.manualMax);
    setSelectedNfModelId(restoredState.naturalFractureModelId);
    setNaturalFracturePhysics(restoredState.naturalFracturePhysics);
    setShowNaturalFractures(restoredState.naturalFractureDisplay);
    setNaturalFractureColor(restoredState.naturalFractureColor);
    setShowInteractionPoints(restoredState.interactionPointDisplay);
    setSelectedOptimizationVariables(restoredState.optimizationVariables);
    setTopN(restoredState.topN);
    setComparisonSchemeIds(restoredState.comparisonSchemeIds);
    setDesignMode(restoredState.designMode);
    setActiveWorkflowStep(restoredState.activeWorkflowStep);
    setActiveSubSection(restoredState.activeSubSection);
    setEngineeringConstraints(restoredState.engineeringConstraints);
    setObjectiveTemplate(restoredState.objectiveTemplate);
    setOpts((current) => ({ ...current, ...restoredState.simulationOptions }));
    setNaturalFractureDesign(normalizeNaturalFractureDesign(
      (restoredState.simulationOptions.natural_fracture_design as NaturalFractureDesignDTO | undefined)
        ?? defaultNaturalFractureDesign([], { centerX: 0, centerY: 0, centerZ: 0, widthM: 500, heightM: 300 }),
    ));
  }, [projectId, restoredState]);

  useEffect(() => {
    if (!projectId || persistedProjectRef.current !== projectId) return;
    if (skipPersistProjectRef.current === projectId) {
      skipPersistProjectRef.current = undefined;
      return;
    }
    saveFracSimState(projectId, {
      designId, simulationId: simId, streamJobId, reservoirPreset: selectedPreset,
      resultProperty: resultVariableId, rangeScope, rangeMethod, manualMin, manualMax,
      naturalFractureModelId: selectedNfModelId, naturalFracturePhysics,
      naturalFractureDisplay: showNaturalFractures, naturalFractureColor,
      interactionPointDisplay: showInteractionPoints,
      optimizationVariables: selectedOptimizationVariables, topN, comparisonSchemeIds,
      designMode, workflowStep: scenarioWorkflowStep, activeWorkflowStep, activeSubSection,
      engineeringConstraints, objectiveTemplate,
      simulationOptions: opts,
    });
  }, [projectId, designId, simId, streamJobId, selectedPreset, resultVariableId, rangeScope, rangeMethod, manualMin, manualMax,
    selectedNfModelId, naturalFracturePhysics, showNaturalFractures, naturalFractureColor, showInteractionPoints,
    selectedOptimizationVariables, topN, comparisonSchemeIds, designMode, scenarioWorkflowStep,
    activeWorkflowStep, activeSubSection,
    engineeringConstraints, objectiveTemplate, opts]);

  useEffect(() => {
    if (!projectId || !selectedNfModelId) {
      setSelectedNfModel(null);
      setNatFracs([]);
      return;
    }
    fractureAPI.getPublishedNaturalFractureModel(projectId, selectedNfModelId, designId ?? undefined, false)
      .then(({ data }) => {
        setSelectedNfModel(data);
        // Global Published NF is metadata only here. Rendering/solver input
        // comes exclusively from the spatially extracted Stage-local model.
        setNatFracs([]);
      })
      .catch(() => {
        setSelectedNfModel(null);
        setNatFracs([]);
      });
  }, [projectId, designId, selectedNfModelId]);

  useEffect(() => {
    if (activeDesignRef.current === designId) return;
    activeDesignRef.current = designId;
    simulationLoadRequestRef.current += 1;
    setSims([]);
    setSimId(null);
    setSelStage(null);
    setSelectedInteraction(null);
    setStreamJobId(null);
    setStreamJob(null);
    streamContextRef.current = null;
    streamEventIdRef.current = 0;
    setStreamFrames([]);
    setStreamFrameIndex(0);
    setStreamViewerMode('LIVE');
    setSimulationRunState('IDLE');
    simulationClock.pause();
    simulationClock.seek(0);
  }, [designId]);

  useEffect(() => {
    if (designId) loadSims(designId).catch(() => {});
  }, [designId]);

  useEffect(() => {
    if (!handoff.designId || designLoadStatus === 'LOADING' || designLoadStatus === 'ERROR') return;
    const target = designs.some((item) => item.id === handoff.designId) ? handoff.designId : null;
    if (target !== designId) setDesignId(target);
  }, [handoff.designId, designs, designLoadStatus]);

  useEffect(() => {
    if (!streamJobId || !projectId) return undefined;
    const controller = new AbortController();
    let active = true;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let frameFlushAnimation: number | null = null;
    let pendingCommittedFrames: CommittedSimulationFrame[] = [];

    const applyJob = (job: SimulationJob) => {
      if (!active || job.projectId !== projectId) return false;
      setStreamJob(job);
      streamContextRef.current = {
        jobId: job.jobId, projectId: job.projectId, designId: job.designId, simulationId: job.simulationId,
      };
      setSimId(job.simulationId);
      if (job.status === 'PENDING') setSimulationRunState('STARTING');
      else if (job.status === 'RUNNING' && simulationRunStateRef.current !== 'PLAYBACK') setSimulationRunState('LIVE');
      else if (job.status === 'COMPLETED') setSimulationRunState('COMPLETED');
      else if (job.status === 'FAILED') setSimulationRunState('FAILED');
      else if (job.status === 'CANCELLED') setSimulationRunState('CANCELLED');
      return true;
    };

    const appendFrame = (raw: Record<string, any>) => {
      const expected = streamContextRef.current;
      if (!expected || !frameBelongsToJob(raw, expected)) return;
      if (firstFrameReceivedAtRef.current === null) firstFrameReceivedAtRef.current = performance.now();
      pendingCommittedFrames.push(raw as CommittedSimulationFrame);
      if (frameFlushAnimation !== null) return;
      // SSE may deliver many committed steps in one network chunk.  Preserve
      // every real frame for replay, but update React/Three.js at most once per
      // browser paint to avoid rapid clear/rebuild flashes.
      frameFlushAnimation = requestAnimationFrame(() => {
        frameFlushAnimation = null;
        if (!active) return;
        const queued = pendingCommittedFrames;
        pendingCommittedFrames = [];
        if (!queued.length) return;
        setStreamFrames((current) => queued.reduce(
          (frames, frame) => appendCommittedPlaybackFrame(frames, frame),
          current,
        ));
        const latest = queued[queued.length - 1];
        setStreamJob((current) => current ? {
          ...current,
          progress: Number(latest.progress ?? current.progress),
          currentTime: Number(latest.time ?? current.currentTime),
          totalTime: Number(latest.totalTime ?? current.totalTime),
          currentStep: Number(latest.globalStep ?? current.currentStep),
          totalSteps: Number(latest.totalGlobalSteps ?? current.totalSteps),
          metrics: latest.performance
            ? { ...(current.metrics ?? {}), lastStepPerformance: latest.performance }
            : current.metrics,
        } : current);
      });
    };

    const refreshSimulation = async (simulationId: number) => {
      const { data } = await fractureAPI.getSimulation(simulationId);
      if (!active) return null;
      const completed = data as Sim;
      setSims((current) => {
        const next = [completed, ...current.filter((item) => item.id !== completed.id)];
        if (designId) FS_SIMS[designId] = next;
        return next;
      });
      return completed;
    };

    const showCompletedResultPlayback = (completed: Sim) => {
      const completedPlayback = buildSimulationPlayback(completed.results ?? []);
      pendingCommittedFrames = [];
      if (frameFlushAnimation !== null) {
        cancelAnimationFrame(frameFlushAnimation);
        frameFlushAnimation = null;
      }
      setStreamFrames([]);
      setStreamFrameIndex(0);
      setStreamViewerMode('LIVE');
      setStreamJob(null);
      setSimulationRunState('IDLE');
      simulationClock.pause();
      simulationClock.seek(completedPlayback.durationS);
    };

    const connect = async () => {
      try {
        const { data: initial } = await fractureAPI.getSimulationJob(streamJobId);
        if (!applyJob(initial)) return;
        const initialSimulation = await refreshSimulation(initial.simulationId).catch(() => null);
        if (initial.lastConvergedFrame && ['COMPLETED', 'FAILED', 'CANCELLED'].includes(initial.status)) {
          appendFrame(initial.lastConvergedFrame);
        }
        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(initial.status)) {
          if (initial.status === 'COMPLETED' && initialSimulation) {
            showCompletedResultPlayback(initialSimulation);
          }
          setStreamJobId(null);
          return;
        }
        await fractureAPI.streamSimulationEvents(streamJobId, (event) => {
          if (!active || (event.id && event.id <= streamEventIdRef.current)) return;
          if (event.id) streamEventIdRef.current = event.id;
          if (event.type === 'frame') {
            appendFrame(event.data);
            if (simulationRunStateRef.current !== 'PLAYBACK') setSimulationRunState('LIVE');
          } else if (event.type === 'progress') {
            setStreamJob((current) => current ? {
              ...current,
              progress: Number(event.data.progress ?? current.progress),
              currentTime: Number(event.data.currentTime ?? current.currentTime),
              totalTime: Number(event.data.totalTime ?? current.totalTime),
              currentStep: Number(event.data.currentStep ?? current.currentStep),
              totalSteps: Number(event.data.totalSteps ?? current.totalSteps),
            } : current);
          } else if (event.type === 'job_completed') {
            setSimulationRunState('COMPLETED');
          } else if (event.type === 'job_failed') {
            setSimulationRunState('FAILED');
            message.error(event.data.failureReason || '模拟求解失败，已保留最后收敛帧');
          } else if (event.type === 'job_cancelled') {
            setSimulationRunState('CANCELLED');
            message.info('模拟任务已在安全时间步边界取消');
          } else if (event.type === 'warning' && event.data.warning) {
            message.warning(String(event.data.warning));
          } else if (event.type === 'stream_reset') {
            const recovered = event.data.job as SimulationJob | undefined;
            const expected = streamContextRef.current;
            const recoveredFrame = recovered?.lastConvergedFrame;
            if (expected && recoveredFrame && frameBelongsToJob(recoveredFrame, expected)) {
              setStreamFrames(appendCommittedPlaybackFrame(
                [], recoveredFrame as CommittedSimulationFrame,
              ));
            }
          }
        }, { lastEventId: streamEventIdRef.current, signal: controller.signal });
        if (!active) return;
        const { data: finalJob } = await fractureAPI.getSimulationJob(streamJobId);
        applyJob(finalJob);
        if (finalJob.status === 'COMPLETED') {
          const completed = await refreshSimulation(finalJob.simulationId);
          if (completed) showCompletedResultPlayback(completed);
          message.success(`${opts.model_type} 模拟完成，正式结果已生成`);
        }
        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(finalJob.status)) setStreamJobId(null);
        else reconnectTimer = setTimeout(connect, 1200);
      } catch (error: any) {
        if (!active || error?.name === 'AbortError') return;
        reconnectTimer = setTimeout(connect, 1200);
      }
    };

    connect();
    return () => {
      active = false;
      controller.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (frameFlushAnimation !== null) cancelAnimationFrame(frameFlushAnimation);
      pendingCommittedFrames = [];
    };
  }, [streamJobId, projectId]);

  useEffect(() => {
    setSelectedInteraction(null);
  }, [simId]);

  useEffect(() => {
    if (!designId || !designs.some((design) => design.id === designId)) { setReadiness(null); return; }
    fractureAPI.getSimulationReadiness(designId, solverOptionsOnly(opts))
      .then(({ data }) => setReadiness(data))
      .catch(() => setReadiness(null));
  }, [designId, designs, opts]);

  const sim = sims.find((s) => s.id === simId) ?? null;
  const modelSimulations = useMemo(() => sims
    .filter((item) => String(item.model_type).toUpperCase() === opts.model_type)
    .slice()
    .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime()),
  [sims, opts.model_type]);
  const selectedDesign = designs.find((design) => design.id === designId) ?? null;
  const designInputSummary = selectedDesign ? summarizeSimulationDesignInput(selectedDesign) : null;
  const handoffIssue = handoff.error ?? (handoff.designId && designLoadStatus !== 'LOADING' && designLoadStatus !== 'ERROR'
    && !designs.some((item) => item.id === handoff.designId)
    ? '指定的设计方案不属于当前项目或已被删除，请返回压裂设计重新选择。'
    : null);
  const treatmentStageOptions = useMemo(() => [...(selectedDesign?.stages ?? [])]
    .sort((left, right) => (
      Math.max(Number(right.top_perf), Number(right.bottom_perf))
      - Math.max(Number(left.top_perf), Number(left.bottom_perf))
    ) || right.stage_number - left.stage_number), [selectedDesign]);
  const streamJobActive = streamJob?.status === 'PENDING' || streamJob?.status === 'RUNNING';

  useEffect(() => {
    if (!selectedDesign) return;
    const handoff = (selectedDesign.design_data?.simulation_handoff ?? {}) as Record<string, any>;
    const defaults = (handoff.solver_material_defaults ?? {}) as Record<string, unknown>;
    const key = `${selectedDesign.id}:${String(handoff.updated_at ?? '')}`;
    if (materialDefaultsAppliedRef.current === key) return;
    materialDefaultsAppliedRef.current = key;
    const positive = (value: unknown) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : undefined;
    const fluidN = positive(defaults.fluid_n), fluidK = positive(defaults.fluid_k), leakoff = positive(defaults.leakoff_c);
    if (fluidN || fluidK || leakoff) setOpts(current => ({
      ...current,
      ...(fluidN ? { fluid_n: fluidN } : {}),
      ...(fluidK ? { fluid_k: fluidK } : {}),
      ...(leakoff ? { leakoff_c: leakoff } : {}),
    }));
  }, [selectedDesign]);

  useEffect(() => {
    if (!selectedDesign) {
      setSelectedStageNumbers([]);
      setStageSelectionDesignId(null);
      return;
    }
    setSelectedStageNumbers(treatmentStageOptions.map((stage) => stage.stage_number));
    setStageSelectionDesignId(selectedDesign.id);
  }, [selectedDesign?.id, treatmentStageOptions.map((stage) => stage.stage_number).join(',')]);

  useEffect(() => {
    const stages = selectedDesign?.stages ?? [];
    if (!survey.length || !stages.length) return;
    const intervalMds = stages.flatMap((stage) => [Number(stage.top_perf), Number(stage.bottom_perf)]).filter(Number.isFinite);
    if (!intervalMds.length) return;
    const minMd = Math.min(...intervalMds);
    const maxMd = Math.max(...intervalMds);
    const points = [
      interpolateSurveyXY(survey, minMd),
      ...survey.filter((point) => point.md > minMd && point.md < maxMd)
        .map((point) => ({ x: point.ew, y: point.ns, z: point.tvd })),
      interpolateSurveyXY(survey, maxMd),
    ].filter(Boolean) as Array<{ x: number; y: number; z: number }>;
    if (!points.length) return;
    const minX = Math.min(...points.map((point) => point.x));
    const maxX = Math.max(...points.map((point) => point.x));
    const minY = Math.min(...points.map((point) => point.y));
    const maxY = Math.max(...points.map((point) => point.y));
    const nextBoundary = {
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      centerZ: points.reduce((sum, point) => sum + point.z, 0) / points.length,
      widthM: Math.max(500, maxX - minX + 300),
      heightM: Math.max(300, maxY - minY + 300),
    };
    setNaturalFractureDesign((current) => {
      const previous = current.design_metadata.boundary;
      const unchanged = (Object.keys(nextBoundary) as Array<keyof typeof nextBoundary>)
        .every((key) => Math.abs(nextBoundary[key] - previous[key]) < 1e-6);
      if (unchanged) return current;
      const regenerated = current.source_mode === 'CUSTOM' && current.custom_fractures.length
        ? generateCustomNaturalFractures(
          current.fracture_sets.map((set) => ({ ...set, regenerate: true })),
          nextBoundary,
          current.design_metadata.random_seed,
        )
        : current.custom_fractures;
      return {
        ...current,
        custom_fractures: regenerated,
        design_metadata: { ...current.design_metadata, boundary: nextBoundary },
      };
    });
  }, [survey, selectedDesign]);

  useEffect(() => {
    if (sim && String(sim.model_type).toUpperCase() === opts.model_type) return;
    setSimId(modelSimulations[0]?.id ?? null);
    setSelStage(null);
  }, [opts.model_type, modelSimulations, sim]);
  const hasGeologyModelData = layers.length > 0 || horizons.length > 0 || stressRows.length > 0;
  const inheritedMechanics = useMemo(() => {
    const rows = stressRows.filter((row) => Number.isFinite(Number(row.depth)));
    if (!rows.length) return null;
    const designMidpoints = (selectedDesign?.stages ?? [])
      .map((stage) => (Number(stage.top_perf) + Number(stage.bottom_perf)) / 2)
      .filter(Number.isFinite);
    const targetLayer = layers.find((layer) => layer.is_target) ?? layers[0];
    const targetDepth = designMidpoints.length
      ? designMidpoints.reduce((sum, value) => sum + value, 0) / designMidpoints.length
      : targetLayer ? (Number(targetLayer.top_depth) + Number(targetLayer.bottom_depth)) / 2 : Number(rows[0].depth);
    const row = rows.reduce((nearest, candidate) => (
      Math.abs(Number(candidate.depth) - targetDepth) < Math.abs(Number(nearest.depth) - targetDepth)
        ? candidate : nearest
    ));
    const youngsModulus = row.youngs_modulus != null && Number.isFinite(Number(row.youngs_modulus))
      ? Number(row.youngs_modulus)
      : row.dynamic_youngs_modulus != null && Number.isFinite(Number(row.dynamic_youngs_modulus)) ? Number(row.dynamic_youngs_modulus) : null;
    const poissonsRatio = row.poissons_ratio != null && Number.isFinite(Number(row.poissons_ratio))
      ? Number(row.poissons_ratio)
      : row.dynamic_poissons_ratio != null && Number.isFinite(Number(row.dynamic_poissons_ratio)) ? Number(row.dynamic_poissons_ratio) : null;
    const shmin = row.sigma_h != null && Number.isFinite(Number(row.sigma_h)) ? Number(row.sigma_h) : null;
    const tensileStrength = row.tensile_strength != null && Number.isFinite(Number(row.tensile_strength)) ? Number(row.tensile_strength) : null;
    const kic = tensileStrength !== null && tensileStrength > 0.05
      ? Math.max(0.3, Math.min(10, tensileStrength * 0.1)) : null;
    const stressProfile = rows
      .filter((item) => item.sigma_h != null && Number.isFinite(Number(item.sigma_h)))
      .sort((a, b) => Number(a.depth) - Number(b.depth));
    const currentIndex = stressProfile.indexOf(row);
    const adjacentStresses = currentIndex >= 0
      ? [stressProfile[currentIndex - 1]?.sigma_h, stressProfile[currentIndex + 1]?.sigma_h]
        .filter((value): value is number => value != null && Number.isFinite(Number(value)))
      : [];
    const stressBarrierMpa = shmin !== null && adjacentStresses.length
      ? Math.max(...adjacentStresses.map((value) => Math.abs(Number(value) - shmin))) : null;
    return { youngsModulus, poissonsRatio, shmin, kic, stressBarrierMpa };
  }, [stressRows, selectedDesign, layers]);

  const stages = useMemo(
    () => (sim?.results ?? []).slice().sort((a, b) => a.stage_number - b.stage_number),
    [sim]
  );

  // 工程建议
  const stressAzimuth = useMemo(() => {
    const candidates = stressRows.filter((row) => Number.isFinite(Number(row.stress_direction)));
    if (!candidates.length) return null;
    const targetMd = stages.length
      ? stages.reduce((sum, stage) => sum + stage.perf_md, 0) / stages.length
      : null;
    const row = targetMd === null
      ? candidates[0]
      : candidates.reduce((best, candidate) => Math.abs(Number(candidate.depth ?? 0) - targetMd) < Math.abs(Number(best.depth ?? 0) - targetMd) ? candidate : best);
    const raw = Number(row.stress_direction);
    return ((raw % 360) + 360) % 360;
  }, [stressRows, stages]);

  const recommendations = useMemo(() => {
    if (!sim) return [];
    const tips: { text: string; level: 'warn' | 'info' | 'ok' }[] = [];
    const eff = sim.avg_efficiency;
    const propRatio = sim.total_half_length > 0 ? sim.total_propped_length / sim.total_half_length : 0;
    const heightGrowth = stages[0]?.height_growth_up ?? 0;
    const h0 = stages[0]?.stress_layers?.h0_m ?? 30;
    const uniformity = (sim.summary?.effectiveness_score as number) ?? 50;

    if (eff < 25) tips.push({ text: `液效仅 ${eff}%, 建议提高液体黏度或降低滤失系数`, level: 'warn' });
    else if (eff < 45) tips.push({ text: `液效 ${eff}%, 可考虑优化液体体系以提高效率`, level: 'info' });
    else tips.push({ text: `液效 ${eff}% 良好`, level: 'ok' });

    if (propRatio < 0.5) tips.push({ text: `支撑比例仅 ${(propRatio*100).toFixed(0)}%, 建议增大排量或使用更细支撑剂`, level: 'warn' });
    else if (propRatio < 0.75) tips.push({ text: `支撑比例 ${(propRatio*100).toFixed(0)}%, 可通过提高黏度进一步改善`, level: 'info' });
    else tips.push({ text: `支撑比例 ${(propRatio*100).toFixed(0)}% 良好`, level: 'ok' });

    if (heightGrowth > h0 * 1.5) tips.push({ text: `缝高增长 ${heightGrowth.toFixed(1)}m 偏大, 考虑调整射孔位置`, level: 'warn' });
    else if (stages[0]?.height_growth_up !== undefined && stages[0].height_growth_up! > 0) tips.push({ text: '缝高受到 K_IC 和应力差的遏止, 遏止效果良好', level: 'ok' });

    if (uniformity < 40) tips.push({ text: '有效性偏低, 建议综合优化液体/支撑剂/射孔参数', level: 'warn' });
    else if (uniformity >= 70) tips.push({ text: `有效性 ${uniformity} 分 (${sim.summary?.effectiveness_grade || '-'}), 模拟结果可靠`, level: 'ok' });

    return tips;
  }, [sim, stages]);

  // 后端结果只在适配层中转换；Viewer 始终只消费 currentFrame。
  const playback = useMemo(() => buildSimulationPlayback(stages), [stages]);
  const resultVariable = RESULT_PROPERTIES[resultVariableId];
  const seedHeightTraceStage = stages.find((stage) => stage.stage_number === selStage) ?? stages[0];
  const seedHeightTrace = seedHeightTraceStage?.cluster_formations?.[0];
  const modelDiagnostics = seedHeightTraceStage?.diagnostics ?? sim?.summary?.diagnostics ?? [];
  const diagnosticCodes = new Set(modelDiagnostics.map((item) => item.code));
  const modelConfidence = seedHeightTraceStage?.confidence?.prediction
    ?? sim?.summary?.confidence?.prediction
    ?? seedHeightTraceStage?.confidence?.overall
    ?? sim?.summary?.confidence?.overall;
  const simulationMode = sim?.summary?.simulation_mode ?? seedHeightTraceStage?.confidence?.simulation_mode ?? 'Predictive';
  const calibrationStatus = sim?.summary?.calibration_status ?? seedHeightTraceStage?.confidence?.calibration_status ?? 'Not Calibrated';
  const simulationClock = useSimulationClock(playback.durationS);
  const historicalFrame = useMemo(
    () => getPlaybackFrameAtTime(playback.frames, simulationClock.currentTimeS),
    [playback.frames, simulationClock.currentTimeS],
  );
  const streamDisplayIndex = streamingFrameIndex(
    streamViewerMode,
    streamFrameIndex,
    streamFrames.length,
  );
  const currentFrame = streamFrames.length && simulationRunState !== 'IDLE'
    ? streamFrames[streamDisplayIndex]
    : historicalFrame;
  useEffect(() => {
    if (!streamFrames.length || firstFrameRenderedRef.current || runStartedAtRef.current === null) return undefined;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (firstFrameRenderedRef.current || runStartedAtRef.current === null) return;
        firstFrameRenderedRef.current = true;
        const renderedAt = performance.now();
        const clickToFirstFrameMs = renderedAt - runStartedAtRef.current;
        const frameToPaintMs = firstFrameReceivedAtRef.current === null
          ? null : renderedAt - firstFrameReceivedAtRef.current;
        setStreamJob((current) => current ? {
          ...current,
          metrics: {
            ...(current.metrics ?? {}),
            clickToFirstRenderedFrameSeconds: Number((clickToFirstFrameMs / 1000).toFixed(3)),
            frontendFrameToPaintSeconds: frameToPaintMs === null ? null : Number((frameToPaintMs / 1000).toFixed(3)),
          },
        } : current);
      });
    });
    return () => { cancelAnimationFrame(firstFrame); cancelAnimationFrame(secondFrame); };
  }, [streamFrames.length]);
  const perSchemeResultRange = useMemo(
    () => calculateResultRange(playback, resultVariableId, {
      scope: rangeScope,
      currentFrame,
      method: comparisonRangeMode === 'MANUAL' ? 'MANUAL' : rangeMethod,
      manualMin: Number(manualMin),
      manualMax: Number(manualMax),
    }),
    [playback, resultVariableId, rangeScope, currentFrame, rangeMethod, manualMin, manualMax, comparisonRangeMode],
  );
  const comparisonSimulations = useMemo(
    () => sims.filter((item) => comparisonSimulationIds.includes(item.id)),
    [sims, comparisonSimulationIds],
  );
  const sharedComparisonRange = useMemo(
    () => comparisonGlobalRange(comparisonSimulations, resultVariableId, rangeMethod, Number(manualMin), Number(manualMax)),
    [comparisonSimulations, resultVariableId, rangeMethod, manualMin, manualMax],
  );
  const resultRange = comparisonRangeMode === 'COMPARISON_GLOBAL' && comparisonSimulations.length > 1
    ? sharedComparisonRange
    : perSchemeResultRange;
  const colorBarTicks = useMemo(() => resultTicks(resultRange), [resultRange]);
  const categoricalValues = useMemo(
    () => resultCategories(currentFrame, resultVariableId),
    [currentFrame, resultVariableId],
  );
  const nfInteractions = useMemo(() => {
    const naturalFracture = sim?.summary?.natural_fracture as { interactions?: any[] } | undefined;
    return Array.isArray(naturalFracture?.interactions) ? naturalFracture.interactions : [];
  }, [sim]);
  const displayedInteractions = useMemo(() => {
    if (opts.model_type !== 'UFM') return [];
    const live = (currentFrame?.clusters ?? []).flatMap((cluster) => {
      const wings = cluster.surfaceFrame?.wings;
      return wings ? Object.values(wings).flatMap((wing) => wing.naturalFractureInteractions ?? []) : [];
    });
    const deduplicated = new Map<string, any>();
    [...nfInteractions, ...live].forEach((interaction: any, index) => {
      const key = String(
        interaction.interactionId ?? interaction.sourceNaturalFractureId
        ?? interaction.naturalFractureId ?? `${interaction.stage ?? ''}-${interaction.cluster ?? ''}-${index}`,
      );
      deduplicated.set(key, interaction);
    });
    return [...deduplicated.values()];
  }, [currentFrame, nfInteractions, opts.model_type]);
  const resultStageLocalNfModels = useMemo(() => {
    const naturalFracture = sim?.summary?.natural_fracture as { stageLocalModels?: any[] } | undefined;
    return Array.isArray(naturalFracture?.stageLocalModels) ? naturalFracture.stageLocalModels : [];
  }, [sim]);
  // A completed simulation must render the exact Stage-Local NF model used by
  // the solver. Preview geometry is only a fallback before a result exists.
  const displayedStageLocalNfModels = useMemo(
    () => opts.model_type === 'UFM' ? resultStageLocalNfModels : [],
    [opts.model_type, resultStageLocalNfModels],
  );
  const designedNaturalFractures = useMemo(
    () => naturalFractureViewerRecords(naturalFractureDesign),
    [naturalFractureDesign],
  );
  const displayedNaturalFractures = useMemo(() => (
    opts.model_type !== 'UFM' ? [] : displayedStageLocalNfModels.length
      ? displayedStageLocalNfModels.flatMap((model: any) => model.fractures ?? [])
      : designedNaturalFractures.length ? designedNaturalFractures : natFracs
  ), [opts.model_type, displayedStageLocalNfModels, designedNaturalFractures, natFracs]);
  const passiveNaturalFractureColor = Number.parseInt(naturalFractureColor.slice(1), 16);
  const playbackClusters = useMemo(
    () => currentFrame?.clusters
      ?? streamFrames[streamFrames.length - 1]?.clusters
      ?? playback.frames[0]?.clusters
      ?? [],
    [currentFrame, playback.frames, streamFrames],
  );
  const naturalFractureLegendStates = useMemo(() => {
    const branches = playbackClusters.flatMap((cluster) => cluster.surfaceFrame?.branches ?? []);
    const states = new Set<NaturalFractureVisualState>();
    displayedNaturalFractures.forEach((fracture: any) => states.add(resolveNaturalFractureVisualState(
      fracture.id, displayedInteractions, branches, currentFrame?.timeS,
    )));
    return [...states];
  }, [currentFrame?.timeS, displayedNaturalFractures, displayedInteractions, playbackClusters]);
  const viewerStages = useMemo(() => stages.length ? stages : (selectedDesign?.stages ?? []).map((stage) => ({
    stage_number: stage.stage_number,
    top_perf: stage.top_perf,
    bottom_perf: stage.bottom_perf,
    perf_md: (stage.top_perf + stage.bottom_perf) / 2,
    half_length: 0,
    propped_half_length: 0,
    height: 0,
    w0_mm: 0,
    w_avg_mm: 0,
    net_pressure_mpa: 0,
    efficiency_pct: 0,
    conductivity: 0,
    fluid_volume: stage.fluid_volume,
    proppant_mass: stage.proppant_mass,
    outline: [],
  } as StageResult)), [stages, selectedDesign]);
  const statusStageId = selStage;
  const statusClusters = currentFrame?.clusters.filter(
    (cluster) => statusStageId === null || String(cluster.stageId) === String(statusStageId),
  ) ?? [];
  const activeClusterCount = statusClusters.filter((cluster) => cluster.isInitiated).length;
  const statusClusterTotal = statusClusters.length;
  const hoveredCluster = hoveredFracture
    ? currentFrame?.clusters.find((cluster) => cluster.clusterId === hoveredFracture.clusterId)
      ?? currentFrame?.clusters.find((cluster) => Number(cluster.stageId) === hoveredFracture.stageNo)
    : undefined;
  const geologyGridReference = useMemo(() => {
    const candidates = horizons.filter((horizon) => (
      Number.isInteger(horizon.nx) && horizon.nx > 1
      && Number.isInteger(horizon.ny) && horizon.ny > 1
      && Number.isFinite(horizon.dx) && horizon.dx > 0
      && Number.isFinite(horizon.dy) && horizon.dy > 0
      && Array.isArray(horizon.grid_z) && horizon.grid_z.length === horizon.nx * horizon.ny
    ));
    const reference = candidates.reduce<HorizonSurfaceRow | null>((largest, horizon) => {
      if (!largest) return horizon;
      const area = (horizon.nx - 1) * horizon.dx * (horizon.ny - 1) * horizon.dy;
      const largestArea = (largest.nx - 1) * largest.dx * (largest.ny - 1) * largest.dy;
      return area > largestArea ? horizon : largest;
    }, null);
    if (reference) {
      let minDepth = Number.POSITIVE_INFINITY;
      let maxDepth = Number.NEGATIVE_INFINITY;
      reference.grid_z.forEach((depth) => {
        if (depth === null || !Number.isFinite(depth)) return;
        minDepth = Math.min(minDepth, depth);
        maxDepth = Math.max(maxDepth, depth);
      });
      const maxFractureHeight = Math.max(0, ...stages.map((stage) => Number(stage.height) || 0));
      const depthSpan = maxFractureHeight > 0
        ? maxFractureHeight * 0.75
        : Number.isFinite(minDepth) && Number.isFinite(maxDepth) ? maxDepth - minDepth : 0;
      const zStep = niceGridStep(depthSpan, 12);
      return `坐标间隔 X ${formatMetres(reference.dx)} m · Y ${formatMetres(reference.dy)} m${zStep ? ` · Z ${formatMetres(zStep)} m` : ''}`;
    }
    const modelLayers = layers.filter((layer) => (
      Number.isFinite(layer.extent_x) && Number(layer.extent_x) > 0
      && Number.isFinite(layer.extent_y) && Number(layer.extent_y) > 0
    ));
    if (!modelLayers.length) return null;
    const extentX = Math.max(...modelLayers.map((layer) => Number(layer.extent_x)));
    const extentY = Math.max(...modelLayers.map((layer) => Number(layer.extent_y)));
    const top = Math.min(...modelLayers.map((layer) => layer.top_depth));
    const bottom = Math.max(...modelLayers.map((layer) => layer.bottom_depth));
    const maxFractureHeight = Math.max(0, ...stages.map((stage) => Number(stage.height) || 0));
    const verticalReferenceSpan = maxFractureHeight > 0 ? maxFractureHeight * 0.75 : bottom - top;
    return `坐标间隔 X ${formatMetres(niceGridStep(extentX, 32))} m · Y ${formatMetres(niceGridStep(extentY, 32))} m · Z ${formatMetres(niceGridStep(verticalReferenceSpan, 12))} m`;
  }, [horizons, layers, stages]);
  const inheritedClusterCount = (selectedDesign?.stage_count ?? 0) * (selectedDesign?.cluster_per_stage ?? 0);
  const inheritedStageCount = selectedDesign?.stages?.length || selectedDesign?.stage_count || stages.length;
  const currentFormation = seedHeightTraceStage?.cluster_formations?.[0];
  const currentStress = seedHeightTraceStage?.stress_layers;
  const stressBarrier = currentFormation
    && (currentFormation.upper_shmin_mpa != null || currentFormation.lower_shmin_mpa != null)
    ? Math.max(
      Math.abs(Number(currentFormation.upper_shmin_mpa ?? currentFormation.base_shmin_mpa ?? 0) - Number(currentFormation.base_shmin_mpa ?? 0)),
      Math.abs(Number(currentFormation.lower_shmin_mpa ?? currentFormation.base_shmin_mpa ?? 0) - Number(currentFormation.base_shmin_mpa ?? 0)),
    )
    : inheritedMechanics?.stressBarrierMpa ?? null;
  const resultRows = useMemo(() => stages
    .filter((stage) => selStage === null || stage.stage_number === selStage)
    .flatMap((stage) => {
      const clusterResults = stage.clusters?.length ? stage.clusters : [null];
      return clusterResults.map((cluster, index) => {
        const clusterId = cluster?.cluster_id ?? `S${stage.stage_number}-C${index + 1}`;
        const live = currentFrame?.clusters.find((item) => item.clusterId === clusterId);
        const raw = (cluster ?? {}) as Record<string, unknown>;
        const halfLength = live?.lengthM ?? Number(raw.half_length_m ?? raw.half_length ?? stage.half_length);
        const height = live?.heightM ?? Number(raw.height_m ?? raw.height ?? stage.height);
        const widthMm = live?.actualWidthMm ?? Number(raw.width_mm ?? raw.w0_mm ?? stage.w0_mm);
        const netPressure = live?.netPressureMpa ?? Number(raw.net_pressure_mpa ?? stage.net_pressure_mpa);
        const conductivity = live?.conductivity ?? Number(raw.conductivity ?? stage.conductivity);
        const initiated = live?.isInitiated
          ?? (typeof raw.is_initiated === 'boolean' ? raw.is_initiated : halfLength > 0 && widthMm > 0);
        return {
          stageNumber: stage.stage_number,
          clusterNumber: (cluster?.cluster_index ?? index) + 1,
          clusterId,
          halfLength,
          height,
          widthMm,
          netPressure,
          conductivity,
          initiated,
        };
      });
    }), [stages, selStage, currentFrame]);
  const totalSolverClusters = stages.reduce(
    (sum, stage) => sum + (stage.clusters?.length || 1), 0,
  );


  useEffect(() => {
    if (!sim) return;
    const playbackResultKey = `${sim.id}:${playback.frames.length}:${playback.durationS}`;
    if (handledPlaybackResultRef.current === playbackResultKey) return;
    handledPlaybackResultRef.current = playbackResultKey;
    if (pendingPlaybackSimId === sim.id) {
      if (playback.hasTimeSeries) {
        simulationClock.setPlaybackSpeed(2000);
        simulationClock.replay();
      }
      else {
        simulationClock.pause();
        simulationClock.seek(0);
        message.warning('模拟已完成，但结果中没有可回放时间步');
      }
      setPendingPlaybackSimId(null);
      return;
    }
    simulationClock.pause();
    simulationClock.seek(playback.durationS);
  }, [sim?.id, playback.durationS, playback.hasTimeSeries, pendingPlaybackSimId]);

  const runSim = async () => {
    if (handoffIssue) { message.error(handoffIssue); return; }
    if (!designId || !selectedDesign) { message.warning('请先加载并选择有效压裂方案'); return; }
    const availableStageNumbers = new Set((selectedDesign.stages ?? []).map((stage) => stage.stage_number));
    const treatmentStageNumbers = selectedStageNumbers.filter((stageNumber) => availableStageNumbers.has(stageNumber));
    if (!treatmentStageNumbers.length) { message.warning('请至少选择一个压裂段'); return; }
    const runContextVersion = simulationContextVersionRef.current;
    const isUfm = opts.model_type === 'UFM';
    if (isUfm && designedNaturalFractures.length === 0) {
      message.warning('UFM 模型需要先配置可用的天然裂缝');
      setNaturalFractureDesignOpen(true);
      return;
    }
    const trimmedSeedHeight = seedHeightInput.trim();
    if (opts.model_type === 'P3D' && trimmedSeedHeight) {
      const seedHeight = Number(trimmedSeedHeight);
      if (!Number.isFinite(seedHeight) || seedHeight <= 0) {
        message.warning('初始缝高人工覆盖值必须大于 0 m');
        return;
      }
    }
    setBusy(true);
    setSimulationRunState('STARTING');
    setStreamViewerMode('LIVE');
    setStreamFrames([]);
    setStreamFrameIndex(0);
    runStartedAtRef.current = performance.now();
    firstFrameReceivedAtRef.current = null;
    firstFrameRenderedRef.current = false;
    streamEventIdRef.current = 0;
    try {
      const simulationOptions = {
        ...solverOptionsOnly(opts),
        selected_stage_numbers: treatmentStageNumbers,
        surface_model: isUfm ? 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' : opts.model_type === 'P3D' ? opts.surface_model : 'OFF',
        wing_mode: isUfm ? 'INDEPENDENT_WINGS_V1' : 'SYMMETRIC_WINGS',
        p3d_seed_height_m: opts.model_type === 'P3D' && trimmedSeedHeight ? Number(trimmedSeedHeight) : undefined,
        natural_fractures_enabled: isUfm,
        unified_fracture_network: isUfm,
        natural_fracture_branch: isUfm,
        ...(isUfm ? {
          natural_fracture_model_id: selectedNfModelId ?? undefined,
          natural_fracture_source_mode: naturalFractureDesign.source_mode,
          natural_fracture_design: naturalFractureDesign,
          nf_search_margin_factor: opts.nf_search_margin_factor ?? 1.3,
          nf_random_seed: opts.nf_random_seed ?? 20260908,
          natural_fracture_sets: opts.natural_fracture_sets,
          nf_p3d_predictors: Object.fromEntries(stages.map((stage) => [String(stage.stage_number), {
            halfLengthM: stage.half_length,
            upperGrowthM: stage.height_growth_up,
            lowerGrowthM: stage.height_growth_down,
            heightM: stage.height,
          }])),
        } : {}),
      };
      const readinessResponse = await fractureAPI.getSimulationReadiness(designId, simulationOptions);
      if (runContextVersion !== simulationContextVersionRef.current) return;
      setReadiness(readinessResponse.data);
      if (!readinessResponse.data.simulationReady) {
        const missing = readinessResponse.data.missingRequired.map((item) => item.label || item.code).join('、');
        message.error(`缺少模拟必需数据：${missing}`);
        return;
      }
      if (readinessResponse.data.availableCalibration.length === 0) {
        message.info(`未关联现场标定数据，本次采用 ${opts.model_type} 预测模式。`);
      }
      simulationClock.setPlaybackSpeed(2000);
      simulationClock.pause();
      simulationClock.seek(0);
      const { data: job } = await fractureAPI.startAsyncSimulation({ design_id: designId, options: simulationOptions });
      if (runContextVersion !== simulationContextVersionRef.current) {
        await fractureAPI.cancelSimulationJob(job.jobId).catch(() => undefined);
        return;
      }
      setStreamJob(job);
      setStreamJobId(job.jobId);
      setSimId(job.simulationId);
      const pendingResponse = await fractureAPI.getSimulation(job.simulationId).catch(() => null);
      if (pendingResponse) {
        const pendingSimulation = pendingResponse.data as Sim;
        setSims((current) => {
          const next = [pendingSimulation, ...current.filter((item) => item.id !== pendingSimulation.id)];
          FS_SIMS[designId] = next;
          return next;
        });
      }
      message.info(`${opts.model_type} 异步任务已启动，共 ${treatmentStageNumbers.length} 个压裂段；首个真实时间步收敛后将立即显示`);
      return true;
    } catch (err: any) {
      setSimulationRunState('FAILED');
      const d = err?.response?.data;
      const detail = typeof d === 'string' ? d : d?.detail;
      message.error(detail || (err?.response ? '模拟请求未完成，请检查方案数据' : '模拟服务未响应，请确认后端服务可用'));
    } finally {
      setBusy(false);
    }
  };

  const cancelSimulation = async () => {
    if (!streamJob) return;
    try {
      const { data } = await fractureAPI.cancelSimulationJob(streamJob.jobId);
      setStreamJob(data);
      if (data.status === 'CANCELLED') setSimulationRunState('CANCELLED');
      else message.info('已请求取消；求解器将在当前安全时间步结束后停止');
    } catch {
      message.error('取消任务失败，请稍后重试');
    }
  };

  useEffect(() => {
    if (!pendingContextRun) return undefined;
    if (designId !== pendingContextRun.designId || opts.model_type !== pendingContextRun.modelType) return undefined;
    if (selectedDesign?.id !== pendingContextRun.designId) return undefined;
    if (stageSelectionDesignId !== pendingContextRun.designId || selectedStageNumbers.length === 0) return undefined;
    if (claimedContextRunRef.current === pendingContextRun.requestId) return undefined;
    claimedContextRunRef.current = pendingContextRun.requestId;
    setPendingContextRun((current) => (
      current?.requestId === pendingContextRun.requestId ? null : current
    ));
    void runSim();
    return undefined;
  }, [pendingContextRun, designId, opts.model_type, selectedDesign?.id, stageSelectionDesignId, selectedStageNumbers]);

  const exportSim = () => {
    if (!sim) return;
    const blob = new Blob([JSON.stringify(sim, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `fracsim_${sim.design_name}_${sim.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  // 爱压 AI 操作模式: 复用本页的方案切换、选段和「运行模拟」按钮逻辑
  useAgentCommands({
    'fracsim.select_design': async (args) => {
      const target = designs.find((d) => d.id === Number(args.design_id));
      if (!target) throw new Error(`没有编号为 ${args.design_id} 的方案, 可选: ${designs.map((d) => `#${d.id}`).join('、') || '无'}`);
      if (target.id === designId) return `#${target.id} 已是当前模拟方案`;
      await switchSimulationContext(target.id, opts.model_type);
      return `已切换到方案 #${target.id}, 页面将自动启动全部段计算`;
    },
    'fracsim.select_stages': (args) => {
      if (!selectedDesign) throw new Error('请先选择模拟方案');
      const available = treatmentStageOptions.map((stage) => stage.stage_number);
      const requested = Array.isArray(args.stages) && args.stages.length ? (args.stages as number[]) : available;
      const missing = requested.filter((n) => !available.includes(n));
      if (missing.length) throw new Error(`方案中没有第 ${missing.join('、')} 段, 可选 ${available.join('、')}`);
      setSelectedStageNumbers(requested);
      return `已选择 ${requested.length} 个压裂段: ${requested.join('、')}`;
    },
    'fracsim.run': async () => {
      if (busy || streamJobActive) throw new Error('已有模拟正在运行');
      if (readiness?.simulationReady === false) throw new Error(`模拟输入不完整, 缺少 ${readiness.missingRequired.length} 个必需项`);
      if (!(await runSim())) throw new Error('模拟未启动, 原因见页面提示');
      return '模拟已启动, 结果会实时显示在页面上';
    },
  }, () => [
    `压裂模拟: 方案 ${selectedDesign ? `#${selectedDesign.id} ${selectedDesign.name}` : '未选择'} (${opts.model_type})`,
    `可选方案: ${designs.map((d) => `#${d.id} ${d.name}`).join('; ') || '无'}`,
    `已选压裂段: ${selectedStageNumbers.join('、') || '无'} / 共 ${treatmentStageOptions.length} 段`,
    readiness ? (readiness.simulationReady ? '输入就绪' : `输入缺少 ${readiness.missingRequired.length} 项`) : '就绪状态未知',
    busy || streamJobActive ? '模拟运行中' : '空闲',
  ].join('\n'));

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;
  // 不再全屏"加载中" - 顶栏立刻渲染

  type NumKey = 'fluid_n' | 'fluid_k' | 'leakoff_c' | 'youngs_modulus' | 'poissons_ratio' | 'frac_height';
  const TOOLTIPS: Record<NumKey, string> = {
    fluid_n: '幂律指数 (0~1), 越小剪切稀释越强', fluid_k: '稠度系数 Pa·sⁿ, 滑溜水~0.3, 线性胶~1.5',
    leakoff_c: 'Carter滤失系数 m/√s, 页岩~1e-4, 致密砂~2e-4',
    youngs_modulus: '杨氏模量 GPa, 页岩~35, 砂岩~25, 煤层~8',
    poissons_ratio: '泊松比, 页岩~0.20, 砂岩~0.25',
    frac_height: 'PKN固定缝高 m, P3D自动计算',
  };
  const numIn = (k: NumKey, label: string, step = 0.01) => (
    <label title={TOOLTIPS[k]} style={{ fontSize: 11, color: TH.textSec, display: 'flex', alignItems: 'center', gap: 3, cursor: 'help' }}>
      {label}
      <input type="number" step={step} value={opts[k]}
        onChange={(e) => setOpts({ ...opts, [k]: Number(e.target.value) })}
        style={{ width: 58, fontSize: 11, padding: '2px 4px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} />
    </label>
  );
  const sidebarNumIn = (k: NumKey, label: string, step = 0.01) => (
    <label title={TOOLTIPS[k]} style={panelLabel}>{label}
      <input type="number" step={step} value={opts[k]}
        onChange={(event) => setOpts({ ...opts, [k]: Number(event.target.value) })}
        style={panelControl} />
    </label>
  );

  // 储层参数预设
  const PRESETS: Record<string, Partial<SimulationOptions>> = {
    '页岩气':  { fluid_n: 0.65, fluid_k: 0.3, leakoff_c: 0.00008, youngs_modulus: 38, poissons_ratio: 0.20, frac_height: 40 },
    '致密油':  { fluid_n: 0.55, fluid_k: 0.5, leakoff_c: 0.00015, youngs_modulus: 30, poissons_ratio: 0.24, frac_height: 30 },
    '煤层气':  { fluid_n: 0.75, fluid_k: 0.2, leakoff_c: 0.00030, youngs_modulus: 8,  poissons_ratio: 0.30, frac_height: 15 },
  };
  const applyPreset = (name: string) => {
    const p = PRESETS[name];
    setSelectedPreset(name);
    if (p) setOpts({ ...opts, ...p });
  };
  const stopSimulationForContextSwitch = async (): Promise<boolean> => {
    simulationContextVersionRef.current += 1;
    const activeJob = streamJob;
    if (activeJob && (activeJob.status === 'PENDING' || activeJob.status === 'RUNNING')) {
      try {
        let stopped = (await fractureAPI.cancelSimulationJob(activeJob.jobId)).data;
        for (let attempt = 0; attempt < 75 && (stopped.status === 'PENDING' || stopped.status === 'RUNNING'); attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 200));
          stopped = (await fractureAPI.getSimulationJob(activeJob.jobId)).data;
        }
        if (stopped.status === 'PENDING' || stopped.status === 'RUNNING') {
          message.error('当前模拟尚未到达安全停止点，暂不启动新的模拟任务');
          return false;
        }
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        message.error(detail || '当前模拟任务未能停止，已取消方案切换');
        return false;
      }
    }
    setStreamJobId(null);
    setStreamJob(null);
    streamContextRef.current = null;
    streamEventIdRef.current = 0;
    setStreamFrames([]);
    setStreamFrameIndex(0);
    setStreamViewerMode('LIVE');
    setSimulationRunState('IDLE');
    simulationClock.pause();
    simulationClock.seek(0);
    return true;
  };

  const switchSimulationContext = async (
    nextDesignId: number | null,
    nextModelType: AvailableFractureModel,
  ) => {
    if (!nextDesignId) return;
    if (nextDesignId === designId && nextModelType === opts.model_type) return;
    if (!await stopSimulationForContextSwitch()) return;
    if (nextDesignId !== handoff.designId) navigate(simulationHandoffUrl(nextDesignId), { replace: true });
    setSimId(null);
    setSelStage(null);
    setSelectedInteraction(null);
    if (nextDesignId !== designId) {
      simulationLoadRequestRef.current += 1;
      setSims([]);
      setDesignId(nextDesignId);
    }
    setOpts((current) => ({ ...current, model_type: nextModelType }));
    setPendingContextRun({
      designId: nextDesignId,
      modelType: nextModelType,
      requestId: simulationContextVersionRef.current,
    });
    message.info('模拟条件已切换，正在启动当前方案计算');
  };

  const selectFractureModel = (modelType: AvailableFractureModel) => {
    if (modelType === 'UFM') {
      setNaturalFracturePhysics(true);
      setShowNaturalFractures(true);
    }
    void switchSimulationContext(designId, modelType);
  };

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <NaturalFractureDesignDialog
        open={naturalFractureDesignOpen}
        value={naturalFractureDesign}
        rawGeologyFractures={rawGeologyNaturalFractures}
        survey={survey}
        perforationMds={(selectedDesign?.stages ?? []).map((stage) => (stage.top_perf + stage.bottom_perf) / 2)}
        geologyLoading={geologyFractureLoadStatus === 'LOADING'}
        onCancel={() => setNaturalFractureDesignOpen(false)}
        onApply={(design) => {
          const normalized = normalizeNaturalFractureDesign(design);
          setNaturalFractureDesign(normalized);
          setOpts((current) => ({
            ...current,
            natural_fracture_source_mode: normalized.source_mode,
            natural_fracture_design: { ...normalized, raw_geology_fractures: [] },
            nf_random_seed: normalized.design_metadata.random_seed,
          }));
          setNaturalFracturePhysics(true);
          setNatFracs([]);
          setNaturalFractureDesignOpen(false);
          message.success('天然裂缝设计已应用到下一次模拟');
        }}
      />
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: TH.headerBg, borderBottom: `1px solid ${TH.headerBorder}`, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>裂缝扩展模拟工作台</h2>
          <select value={opts.model_type}
            onChange={(e) => selectFractureModel(e.target.value as AvailableFractureModel)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${TH.headerBorder}` }}>
            <option value="P3D">P3D 模型</option>
            <option value="PKN">PKN 模型</option>
            <option value="UFM">UFM 模型</option>
          </select>
          <select aria-label="当前压裂方案" value={selectedDesign?.id ?? ''} disabled={!designs.length}
            onChange={(e) => {
              const nextDesignId = e.target.value ? Number(e.target.value) : null;
              void switchSimulationContext(nextDesignId, opts.model_type);
            }}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${designs.length ? TH.headerBorder : '#C0392B'}`, color: designs.length ? TH.text : '#C0392B' }}>
            {designs.length === 0 && <option value="">{designLoadStatus === 'LOADING' ? '正在加载方案…' : designLoadStatus === 'ERROR' ? '方案加载失败' : '缺失压裂方案'}</option>}
            {designs.map((d) => <option key={d.id} value={d.id}>{d.is_active ? '[当前] ' : ''}{d.name}</option>)}
          </select>
          {modelSimulations.length > 0 && (
            <select value={simId ?? ''} onChange={(e) => { setSimId(Number(e.target.value)); setSelStage(null); }}
              style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${TH.headerBorder}` }}>
              {modelSimulations.map((s) => <option key={s.id} value={s.id}>{s.model_type} #{s.id} · {new Date(s.created_at).toLocaleString('zh-CN')}</option>)}
            </select>
          )}
          {!loading && selectedDesign && modelSimulations.length === 0 && (
            <span style={{ fontSize: 11, color: '#A64B32' }}>当前方案尚无 {opts.model_type} 结果，请先运行模拟</span>
          )}
        </div>
        <div aria-hidden="true" style={{ display: 'none' }}>
          {numIn('fluid_n', 'n', 0.05)}
          {numIn('fluid_k', 'K', 0.05)}
          {numIn('leakoff_c', 'C_L', 0.00005)}
          <button onClick={async () => {
            if (!projectId) return;
            try {
              const { data } = await fractureAPI.estimateLeakoff(projectId);
              if (data?.leakoff_c) setOpts({ ...opts, leakoff_c: data.leakoff_c });
              message.info(`C_L 估算值: ${data?.leakoff_c ?? '—'} m/√s (来源: ${data?.source ?? '默认'})`);
            } catch { message.warning('C_L 估算失败, 使用默认值'); }
          }}
            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, cursor: 'pointer',
              border: `1px solid ${TH.headerBorder}`, background: TH.panelHeaderBg, color: TH.textSec,
            }}>估算</button>
          {numIn('youngs_modulus', 'E', 1)}
          {numIn('poissons_ratio', 'ν', 0.01)}
          {opts.model_type === 'PKN' && numIn('frac_height', '缝高', 1)}
          {opts.model_type === 'P3D' && (
            <label
              title="P3D 缝高扩展的初始参考高度；不等于最终缝高，也不一定等于整个地层厚度。留空时按地质力学子层、射孔层段、地层回退值的优先级自动解析。"
              style={{ fontSize: 10, color: TH.textSec, display: 'flex', alignItems: 'center', gap: 3, cursor: 'help' }}
            >
              有效压裂层厚度
              <input
                aria-label="有效压裂层厚度"
                type="number"
                min={0}
                step={0.1}
                placeholder="自动"
                value={seedHeightInput}
                onChange={(event) => setSeedHeightInput(event.target.value)}
                style={{ width: 62, fontSize: 10, padding: '2px 4px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }}
              />
              m <span style={{ color: TH.textMuted }}>?</span>
            </label>
          )}
          <label style={{ fontSize: 11, color: TH.textSec, display: 'flex', alignItems: 'center', gap: 3 }}>
            步数
            <input type="range" min={5} max={60} step={5} value={opts.n_steps ?? 20}
              onChange={(e) => setOpts({ ...opts, n_steps: Number(e.target.value) })}
              style={{ width: 60 }} />
            <span style={{ fontSize: 10, fontFamily: 'monospace', minWidth: 18 }}>{opts.n_steps ?? 20}</span>
          </label>
          <span style={{ color: TH.textMuted, fontSize: 10 }}>|</span>
          <label style={{ fontSize: 10, color: TH.textMuted, display: 'flex', alignItems: 'center', gap: 2 }} title="每簇孔眼数">
            孔
            <input type="number" min={4} max={60} step={2} value={opts.perf_n ?? 12}
              onChange={(e) => setOpts({ ...opts, perf_n: Number(e.target.value) })}
              style={{ width: 36, fontSize: 10, padding: '1px 3px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} />
          </label>
          <label style={{ fontSize: 10, color: TH.textMuted, display: 'flex', alignItems: 'center', gap: 2 }} title="孔眼直径 mm">
            φ
            <input type="number" min={6} max={20} step={1} value={opts.perf_d_mm ?? 10}
              onChange={(e) => setOpts({ ...opts, perf_d_mm: Number(e.target.value) })}
              style={{ width: 36, fontSize: 10, padding: '1px 3px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} />
          </label>
          <select onChange={(e) => { if (e.target.value) applyPreset(e.target.value); e.target.value = ''; }}
            style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, border: `1px solid ${TH.headerBorder}`, color: TH.textSec, maxWidth: 90 }}>
            <option value="">预设…</option>
            {Object.keys(PRESETS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <label title="理想条件: 无滤失(C_L→0)+高黏度, 展示裂缝扩展上限" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, color: TH.textMuted, cursor: 'pointer' }}>
            <input type="checkbox" onChange={(e) => {
              if (e.target.checked) setOpts({ ...opts, leakoff_c: 1e-7, fluid_k: 2.0, fluid_n: 0.8 });
              else setOpts({ ...opts, leakoff_c: 0.00015, fluid_k: 0.5, fluid_n: 0.5 });
            }} style={{ cursor: 'pointer' }} />
            理想
          </label>
          {readiness && (
            <span title={readiness.missingRecommended.map((item) => item.label || item.code).join('\n')}
              style={{ fontSize: 10, color: readiness.simulationReady ? '#1E8449' : '#C2533A' }}>
              {readiness.simulationReady ? '可运行模拟' : `已阻断 · 缺少 ${readiness.missingRequired.length} 个必需项`}
              {readiness.simulationReady && readiness.availableCalibration.length === 0 ? ' · 预测模式' : ''}
            </span>
          )}
          <button onClick={runSim} disabled={!selectedDesign || selectedStageNumbers.length === 0 || busy || streamJobActive || Boolean(handoffIssue) || readiness?.simulationReady === false} style={{
            padding: '5px 16px', borderRadius: 4, fontSize: 12, fontWeight: 600,
            cursor: busy ? 'wait' : !selectedDesign || readiness?.simulationReady === false ? 'not-allowed' : 'pointer', border: 'none',
            background: busy ? '#A89AC0' : TH.sim, color: '#fff',
          }}>{busy ? '启动中…' : streamJobActive ? '实时求解中…' : '运行模拟'}</button>
          {sim && <button onClick={exportSim} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${TH.sim}`, background: '#fff', color: TH.sim,
          }}>JSON</button>}
          {sim && <button onClick={async () => {
            try {
              const { data } = await fractureAPI.exportCsv(sim.id);
              const url = URL.createObjectURL(new Blob([data as BlobPart]));
              const a = document.createElement('a');
              a.href = url; a.download = `fracsim_${sim.id}_${sim.model_type}.csv`; a.click();
              URL.revokeObjectURL(url);
            } catch { message.error('CSV 导出失败'); }
          }} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid #1E8449`, background: '#fff', color: '#1E8449',
          }}>CSV</button>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Popover
            placement="bottomRight"
            trigger="click"
            content={<div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <strong style={{ fontSize: 12, color: TH.text }}>压裂段选择</strong>
                <span style={{ fontSize: 10, color: TH.textSec }}>施工顺序：趾端 → 跟端</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" disabled={busy || streamJobActive}
                  onClick={() => setSelectedStageNumbers(treatmentStageOptions.map((stage) => stage.stage_number))}
                  style={{ ...panelButton, flex: 1 }}>全选</button>
                <button type="button" disabled={busy || streamJobActive}
                  onClick={() => setSelectedStageNumbers([])} style={{ ...panelButton, flex: 1 }}>清空</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                {treatmentStageOptions.map((stage, index) => {
                  const checked = selectedStageNumbers.includes(stage.stage_number);
                  const startMd = Math.min(Number(stage.top_perf), Number(stage.bottom_perf));
                  const endMd = Math.max(Number(stage.top_perf), Number(stage.bottom_perf));
                  return <label key={stage.stage_number} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6, padding: '7px 8px', borderRadius: 3,
                    border: `1px solid ${checked ? TH.sim : TH.headerBorder}`,
                    background: checked ? 'rgba(106,76,147,0.06)' : '#fff', fontSize: 10,
                    color: TH.text, cursor: busy || streamJobActive ? 'not-allowed' : 'pointer',
                    opacity: busy || streamJobActive ? 0.6 : 1,
                  }}>
                    <input type="checkbox" checked={checked} disabled={busy || streamJobActive}
                      onChange={(event) => setSelectedStageNumbers((current) => (
                        event.target.checked
                          ? [...new Set([...current, stage.stage_number])]
                          : current.filter((value) => value !== stage.stage_number)
                      ))} />
                    <span style={{ display: 'grid', gap: 2 }}>
                      <span>第 {stage.stage_number} 段{index === 0 ? ' · 趾端' : index === treatmentStageOptions.length - 1 ? ' · 跟端' : ''}</span>
                      <span style={{ color: TH.textSec }}>{startMd.toFixed(1)}–{endMd.toFixed(1)} m</span>
                    </span>
                  </label>;
                })}
              </div>
              {!treatmentStageOptions.length && <div style={{ fontSize: 10, color: '#C0392B' }}>当前方案未配置有效压裂段。</div>}
              {!!treatmentStageOptions.length && !selectedStageNumbers.length && <div style={{ fontSize: 10, color: '#C0392B' }}>至少选择一个压裂段后方可运行模拟。</div>}
            </div>}
          >
            <button type="button" disabled={!selectedDesign} style={{
              padding: '5px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              cursor: selectedDesign ? 'pointer' : 'not-allowed',
              border: `1px solid ${selectedStageNumbers.length ? TH.sim : '#C0392B'}`,
              background: '#fff', color: selectedStageNumbers.length ? TH.sim : '#C0392B',
            }}>
              压裂段 {selectedStageNumbers.length}/{treatmentStageOptions.length}
            </button>
          </Popover>
          {readiness && (
            <span title={readiness.missingRecommended.map((item) => item.label || item.code).join('\n')}
              style={{ fontSize: 10, color: readiness.simulationReady ? '#1E8449' : '#C2533A' }}>
              {readiness.simulationReady ? '可运行模拟' : `已阻断 · ${readiness.missingRequired.length}`}
            </span>
          )}
          <button onClick={runSim} disabled={!selectedDesign || selectedStageNumbers.length === 0 || busy || streamJobActive || Boolean(handoffIssue) || readiness?.simulationReady === false} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            cursor: busy ? 'wait' : !selectedDesign || readiness?.simulationReady === false ? 'not-allowed' : 'pointer', border: 'none', background: busy ? '#A89AC0' : TH.sim, color: '#fff',
          }}>{busy ? '启动中…' : streamJobActive ? '实时求解中…' : '运行模拟'}</button>
        </div>
      </div>

      {(selectedDesign || handoffIssue) && (
        <div style={{ padding: '7px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
          borderBottom: `1px solid ${TH.headerBorder}`, background: handoffIssue || designInputSummary?.countMismatch ? '#FFF7F4' : '#F7FAFC',
          color: handoffIssue || designInputSummary?.countMismatch ? '#A43F32' : TH.textSec, fontSize: 11 }}>
          {handoffIssue ? <span role="alert">{handoffIssue}</span> : selectedDesign && designInputSummary && <>
            <b>设计输入 · {selectedDesign.name}</b>
            <span>后端段表 {designInputSummary.stageCount} 段</span>
            <span>已保存泵注程序 {designInputSummary.scheduledStageCount} 段</span>
            <span>段表液量 {designInputSummary.fluidM3.toFixed(1)} m³ · 砂量 {designInputSummary.proppantT.toFixed(1)} t</span>
            {designInputSummary.countMismatch && <span role="alert">方案段数与段表不一致，请复核</span>}
            {designInputSummary.scheduledStageCount < designInputSummary.stageCount &&
              <span style={{ color: '#A66A1F' }}>未保存泵注的段会按段表液量和方案排量估算泵注时间：
                {designInputSummary.unscheduledStageNumbers.slice(0, 4).map((stageNumber) =>
                  <button key={stageNumber} type="button" onClick={() => navigate(`/fracture/schedule?design_id=${selectedDesign.id}&stage=${stageNumber}`)}
                    style={{ marginLeft: 5, border: '1px solid #D4AA80', borderRadius: 3, background: '#fff', color: '#945E28', cursor: 'pointer', fontSize: 10 }}>
                    补第 {stageNumber} 段
                  </button>)}
                {designInputSummary.unscheduledStageNumbers.length > 4 && `等 ${designInputSummary.unscheduledStageNumbers.length} 段`}
              </span>}
            {readiness && !readiness.simulationReady && <span role="alert">缺少：{readiness.missingRequired.map((item) => item.label || item.code).join('、')}</span>}
            <span style={{ marginLeft: 'auto', color: TH.textMuted }}>模拟读取后端已保存数据；浏览器中的工程设计草稿不会自动参与本次计算</span>
          </>}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 0, padding: '0 12px', background: '#FAFBFC',
        borderBottom: `1px solid ${TH.headerBorder}`, overflowX: 'auto',
      }}>
        {FRACSIM_WORKFLOW_STEPS.map((item) => {
          const selected = activeWorkflowStep === item.id;
          return <button
            key={item.id}
            aria-current={selected ? 'step' : undefined}
            onClick={() => selectWorkflowStep(item.id)}
            style={{
              minWidth: 112, minHeight: 34, padding: '7px 12px', border: 'none',
              borderBottom: selected ? `2px solid ${TH.sim}` : '2px solid transparent',
              background: selected ? '#F5F3F8' : 'transparent', color: selected ? TH.sim : TH.textSec,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              fontSize: 10, fontWeight: selected ? 700 : 500,
            }}>
            <span style={{ display: 'block', lineHeight: 1.35 }}>{item.label}</span>
          </button>;
        })}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '218px minmax(0, 1fr)', gap: 1, background: TH.statBorder }}>
        <aside style={{ background: '#fff', minHeight: 0, overflow: 'auto', borderRight: `1px solid ${TH.headerBorder}` }}>
          {activeWorkflowStep === 'SIMULATION' ? <>
            <div style={{ padding: '10px', borderBottom: `1px solid ${TH.headerBorder}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: TH.text }}>模拟输入</div>
            </div>
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <section style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  ['地质模型', currentProject?.name, !hasGeologyModelData],
                  ['压裂方案', selectedDesign?.name, !selectedDesign],
                  ['段数', inheritedStageCount > 0 ? `${inheritedStageCount} 段` : null, inheritedStageCount <= 0],
                  ['总簇数', inheritedClusterCount > 0 || totalSolverClusters > 0 ? `${inheritedClusterCount || totalSolverClusters} 簇` : null, inheritedClusterCount <= 0 && totalSolverClusters <= 0],
                  ['簇间距', selectedDesign?.cluster_spacing != null && selectedDesign.cluster_spacing > 0 ? `${selectedDesign.cluster_spacing} m` : null, !(selectedDesign?.cluster_spacing != null && selectedDesign.cluster_spacing > 0)],
                  ['施工排量', selectedDesign?.pump_rate != null && selectedDesign.pump_rate > 0 ? `${selectedDesign.pump_rate} m³/min` : null, !(selectedDesign?.pump_rate != null && selectedDesign.pump_rate > 0)],
                  ['总液量', selectedDesign?.total_fluid != null && selectedDesign.total_fluid > 0 ? `${selectedDesign.total_fluid} m³` : null, !(selectedDesign?.total_fluid != null && selectedDesign.total_fluid > 0)],
                  ['总砂量', selectedDesign?.total_proppant != null && selectedDesign.total_proppant > 0 ? `${selectedDesign.total_proppant} t` : null, !(selectedDesign?.total_proppant != null && selectedDesign.total_proppant > 0)],
                  ['流体体系', selectedDesign?.fluid_type, !selectedDesign?.fluid_type?.trim()],
                  ['支撑剂类型', selectedDesign?.proppant_type, !selectedDesign?.proppant_type?.trim()],
                ].map(([label, value, missing]) => <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: TH.textSec }}>
                  <span>{label}</span><strong style={{ color: missing ? '#C0392B' : TH.text, textAlign: 'right', fontWeight: 700 }}>{missing ? '缺失' : value}</strong>
                </div>)}
                <div style={{ borderTop: `1px solid ${TH.statBorder}`, paddingTop: 7, display: 'grid', gap: 4, fontSize: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>地质数据来源</span><strong style={{ color: hasGeologyModelData ? TH.text : '#C0392B' }}>{hasGeologyModelData ? '地质建模' : '缺失'}</strong></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>施工方案来源</span><strong style={{ color: selectedDesign ? TH.text : '#C0392B' }}>{selectedDesign ? '压裂设计' : '缺失'}</strong></div>
                  {designLoadStatus === 'ERROR' && <div style={{ color: '#C0392B', fontWeight: 600 }}>压裂方案接口加载失败</div>}
                  {geologyLoadStatus === 'ERROR' && <div style={{ color: '#C0392B', fontWeight: 600 }}>地质模型接口加载失败</div>}
                  {mechanicsLoadStatus === 'ERROR' && <div style={{ color: '#C0392B', fontWeight: 600 }}>地质力学接口加载失败</div>}
                </div>
              </section>
              <section style={{ display: 'flex', flexDirection: 'column', gap: 7, borderTop: `1px solid ${TH.headerBorder}`, paddingTop: 9 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: TH.text }}>裂缝扩展参数</div>
                <label style={panelLabel}>裂缝扩展模型
                  <select value={opts.model_type} onChange={(event) => selectFractureModel(event.target.value as AvailableFractureModel)} style={panelControl}>
                    <option value="P3D">P3D 模型</option>
                    <option value="PKN">PKN 模型</option>
                    <option value="UFM">UFM 模型</option>
                  </select>
                </label>
                {sidebarNumIn('leakoff_c', '滤失系数（m/√s）', 0.00005)}
                {opts.model_type === 'PKN' && sidebarNumIn('frac_height', '固定缝高（m）', 1)}
                {[
                  ['杨氏模量（GPa）', currentFormation?.youngs_modulus_gpa ?? currentStress?.E_gpa ?? inheritedMechanics?.youngsModulus],
                  ['泊松比', currentFormation?.poissons_ratio ?? currentStress?.nu ?? inheritedMechanics?.poissonsRatio],
                  ['最小水平主应力（MPa）', currentFormation?.base_shmin_mpa ?? currentStress?.sigma_m_mpa ?? inheritedMechanics?.shmin],
                  ['断裂韧度（MPa·m¹ᐟ²）', currentFormation?.kic_mpa_sqrt_m ?? currentStress?.kic_m ?? inheritedMechanics?.kic],
                  ['应力屏障（MPa）', stressBarrier == null ? null : stressBarrier.toFixed(2)],
                ].map(([label, value]) => <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: TH.textSec }}>
                  <span>{label}</span><strong style={{ color: value == null ? '#C0392B' : TH.text, fontFamily: 'monospace' }}>{value == null ? '缺失' : value}</strong>
                </div>)}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: TH.textSec }}>
                  <span>应力阴影</span><strong style={{ color: selectedDesign ? TH.text : '#C0392B' }}>{selectedDesign ? (selectedDesign.cluster_per_stage ?? 1) > 1 ? '启用' : '单簇不适用' : '缺失'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: TH.textSec }}>
                  <span>簇间流量分配</span><strong style={{ color: selectedDesign ? TH.text : '#C0392B' }}>{selectedDesign ? (selectedDesign.cluster_per_stage ?? 1) > 1 ? '启用' : '单簇不适用' : '缺失'}</strong>
                </div>
              </section>
              {opts.model_type === 'UFM' && <section style={{ borderTop: `1px solid ${TH.headerBorder}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TH.text }}>天然裂缝设计</div>
                  <ClickHelp content={<>
                    <div>当前来源：{naturalFractureSourceLabel(naturalFractureDesign.source_mode)}</div>
                    <div>裂缝组：{naturalFractureDesign.fracture_sets.length} 组</div>
                    <div>地质裂缝：{rawGeologyNaturalFractures.length} 条；自定义裂缝：{naturalFractureDesign.custom_fractures.length} 条。</div>
                    <div>自定义建模范围自动跟随当前压裂水平段。</div>
                  </>} label="查看天然裂缝说明" />
                </div>
                <label style={{ ...checkLabel, fontSize: 11 }}><input type="checkbox" checked disabled />参与天然裂缝交互计算</label>
                <button type="button" onClick={() => setNaturalFractureDesignOpen(true)} style={{
                  ...panelButton, textAlign: 'left', padding: '9px 10px', borderColor: TH.sim,
                  background: 'rgba(106,76,147,0.05)', color: TH.sim, fontSize: 11,
                }}>
                  配置天然裂缝…
                </button>
              </section>}
              <details style={{ borderTop: `1px solid ${TH.headerBorder}`, paddingTop: 9 }}>
                <summary style={{ fontSize: 12, fontWeight: 700, color: TH.text, cursor: 'pointer' }}>求解控制参数</summary>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {opts.model_type === 'P3D' && <label style={panelLabel} title="指定 P3D 模型起裂时刻的初始裂缝高度。未设置时按地层模型计算；扩展阶段的裂缝高度由求解器动态计算。">P3D 初始裂缝高度（m）
                    <input type="number" min={0.1} step={0.1} placeholder="按地层模型计算" value={seedHeightInput}
                      onChange={(event) => setSeedHeightInput(event.target.value)} style={panelControl} />
                  </label>}
                  {sidebarNumIn('fluid_n', '幂律流变指数 n', 0.05)}
                  {sidebarNumIn('fluid_k', '稠度系数 K（Pa·sⁿ）', 0.05)}
                  <label style={panelLabel}>计算时间步数
                    <input type="number" min={5} max={60} step={5} value={opts.n_steps ?? 20}
                      onChange={(event) => setOpts({ ...opts, n_steps: Number(event.target.value) })} style={panelControl} />
                  </label>
                  <label style={checkLabel}><input type="checkbox" checked={opts.surface_model !== 'OFF'} disabled={opts.model_type !== 'P3D'}
                    onChange={(event) => setOpts({ ...opts, surface_model: event.target.checked ? 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' : 'OFF', wing_mode: 'SYMMETRIC_WINGS' })} />方向自适应裂缝扩展</label>
                </div>
              </details>
            </div>
          </> : <>
          <div style={{ padding: '10px 10px 8px', borderBottom: `1px solid ${TH.headerBorder}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TH.text }}>{currentWorkflow.label}</div>
          </div>
          <div style={{ padding: '8px', display: 'grid', gridTemplateColumns: currentWorkflowSections.length > 1 ? '1fr 1fr' : '1fr', gap: 4, borderBottom: `1px solid ${TH.headerBorder}` }}>
            {currentWorkflowSections.map((section) => (
              <button key={`${section.workflowStep}:${section.id}`} onClick={() => setActiveSubSection(section.id)}
                title={section.description} style={{
                padding: '6px 5px', borderRadius: 3, fontSize: 10, textAlign: 'left', cursor: 'pointer',
                border: activeSubSection === section.id ? `1px solid ${TH.sim}` : `1px solid ${TH.headerBorder}`,
                background: activeSubSection === section.id ? 'rgba(106,76,147,0.06)' : '#fff',
                color: activeSubSection === section.id ? TH.sim : TH.textSec,
                fontWeight: activeSubSection === section.id ? 600 : 400,
              }}>{section.label}</button>
            ))}
          </div>
          <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TH.text }}>{currentSubSection.label}</div>
            {activeSubSection === '基础设置' && <>
              <label style={panelLabel}>裂缝扩展模型
                <select value={opts.model_type} onChange={(event) => selectFractureModel(event.target.value as AvailableFractureModel)} style={panelControl}>
                  <option value="P3D">P3D 模型</option>
                  <option value="PKN">PKN 模型</option>
                  <option value="UFM">UFM 模型</option>
                </select>
              </label>
              <label style={panelLabel}>储层参数预设
                <select value={selectedPreset} onChange={(event) => applyPreset(event.target.value)} style={panelControl}>
                  <option value="">自定义 / 未选择</option>
                  {Object.keys(PRESETS).map((key) => <option key={key} value={key}>{key}</option>)}
                </select>
              </label>
              <label style={checkLabel}><input type="checkbox" checked={(opts.n_clusters ?? 1) > 1}
                onChange={(event) => setOpts({ ...opts, n_clusters: event.target.checked ? 3 : 1 })} />多簇同步模拟</label>
              {(opts.n_clusters ?? 1) > 1 && <label style={panelLabel}>每段簇数
                <input type="number" min={2} max={12} value={opts.n_clusters ?? 3}
                  onChange={(event) => setOpts({ ...opts, n_clusters: Math.max(2, Number(event.target.value)) })} style={panelControl} />
              </label>}
            </>}
            {activeSubSection === '地质与力学' && <>
              {numIn('youngs_modulus', '杨氏模量 E（GPa）', 1)}
              {numIn('poissons_ratio', '泊松比 ν', 0.01)}
              {opts.model_type === 'P3D' && <label style={panelLabel} title="P3D 初始参考高度，留空时按现有地质数据解析">有效压裂层厚度 / 初始缝高（m）
                <input type="number" min={0} step={0.1} placeholder="由地质模型确定" value={seedHeightInput}
                  onChange={(event) => setSeedHeightInput(event.target.value)} style={panelControl} />
              </label>}
              {opts.model_type === 'PKN' && numIn('frac_height', '固定缝高（m）', 1)}
            </>}
            {activeSubSection === '井筒与完井' && <>
              <label style={panelLabel}>每簇孔眼数
                <input type="number" min={4} max={60} step={2} value={opts.perf_n ?? 12}
                  onChange={(event) => setOpts({ ...opts, perf_n: Number(event.target.value) })} style={panelControl} />
              </label>
              <label style={panelLabel}>孔眼直径（mm）
                <input type="number" min={6} max={20} value={opts.perf_d_mm ?? 10}
                  onChange={(event) => setOpts({ ...opts, perf_d_mm: Number(event.target.value) })} style={panelControl} />
              </label>
            </>}
            {activeSubSection === '泵注方案' && <>
              {numIn('fluid_n', '幂律流变指数 n', 0.05)}
              {numIn('fluid_k', '稠度系数 K（Pa·sⁿ）', 0.05)}
              {numIn('leakoff_c', 'Carter 滤失系数 C_L', 0.00005)}
              <button onClick={async () => {
                if (!projectId) return;
                try {
                  const { data } = await fractureAPI.estimateLeakoff(projectId);
                  if (data?.leakoff_c) setOpts({ ...opts, leakoff_c: data.leakoff_c });
                  message.info(`C_L: ${data?.leakoff_c ?? '—'} m/√s`);
                } catch { message.warning('C_L 估算失败'); }
              }} style={panelButton}>估算滤失系数</button>
            </>}
            {activeSubSection === '高级物理' && <>
              {opts.model_type === 'UFM' ? <div style={{ padding: 10, border: `1px solid ${TH.sim}`, borderRadius: 4, background: 'rgba(106,76,147,0.05)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong style={{ color: TH.text, fontSize: 12 }}>天然裂缝设计</strong>
                  <ClickHelp content={<>
                    <div>当前来源：{naturalFractureSourceLabel(naturalFractureDesign.source_mode)}</div>
                    <div>自定义条件支持增加多组裂缝，各组参数独立配置。</div>
                    <div>天然裂缝物理计算与三维显示为独立控制项。</div>
                  </>} label="查看天然裂缝说明" />
                </div>
                <label style={{ ...checkLabel, fontSize: 11 }}>
                <input type="checkbox" checked disabled />参与天然裂缝交互计算
                </label>
                <button type="button" onClick={() => setNaturalFractureDesignOpen(true)} style={{ ...panelButton, textAlign: 'left', borderColor: TH.sim, color: TH.sim, fontSize: 11 }}>
                  配置天然裂缝…
                </button>
              </div> : <button type="button" onClick={() => selectFractureModel('UFM')} style={{ ...panelButton, borderColor: TH.sim, color: TH.sim }}>
                切换到 UFM 配置天然裂缝网络
              </button>}
              <details>
                <summary style={{ cursor: 'pointer', color: TH.textSec, fontSize: 11 }}>其他高级物理</summary>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <label style={checkLabel}><input type="checkbox" checked disabled />应力阴影</label>
                  <label style={checkLabel}><input type="checkbox" checked disabled />分层非均质性</label>
                  <label style={checkLabel} title="P3D 仅提供全局 envelope/predictor；后端三维 directional surface 决定实际裂缝面。均匀应力下保持直缝，关闭后保持 Legacy P3D geometry。">
                    <input type="checkbox"
                      checked={opts.surface_model !== 'OFF'}
                      disabled={opts.model_type !== 'P3D'}
                      onChange={(event) => setOpts({
                        ...opts,
                        surface_model: event.target.checked ? 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' : 'OFF',
                        wing_mode: 'SYMMETRIC_WINGS',
                      })} />三维方向自适应裂缝面 V1
                  </label>
                </div>
              </details>
            </>}
            {activeSubSection === '数值设置' && <label style={panelLabel}>时间步数
              <input type="number" min={5} max={60} step={5} value={opts.n_steps ?? 20}
                onChange={(event) => setOpts({ ...opts, n_steps: Number(event.target.value) })} style={panelControl} />
            </label>}
            {activeSubSection === '结果分析' && <>
              <div style={helpText}>结果属性、色标范围和显示控制位于三维视图顶部；切换不会重置相机或播放时间。</div>
              <div style={helpText}>当前数据粒度：{resultVariable.granularity} · {resultVariable.capability}</div>
            </>}
            {(activeSubSection === '优化设计' || activeSubSection === '敏感性分析') && <ScenarioWorkspace
              designId={designId}
              simulationId={simId}
              baseOptions={{
                ...opts,
                natural_fractures_enabled: opts.model_type === 'UFM',
                unified_fracture_network: opts.model_type === 'UFM',
                natural_fracture_branch: opts.model_type === 'UFM',
                natural_fracture_model_id: opts.model_type === 'UFM' ? selectedNfModelId ?? undefined : undefined,
                natural_fracture_source_mode: opts.model_type === 'UFM' ? naturalFractureDesign.source_mode : undefined,
                natural_fracture_design: opts.model_type === 'UFM' ? naturalFractureDesign : undefined,
              }}
              naturalFracturePhysics={opts.model_type === 'UFM'}
              naturalFractureModel={selectedNfModel}
              activeSweetSpotRun={activeSweetSpotRun}
              designMode={designMode}
              onDesignModeChange={setDesignMode}
              workflowStep={scenarioWorkflowStep}
              onWorkflowStepChange={selectScenarioWorkflowStep}
              engineeringConstraints={engineeringConstraints}
              onEngineeringConstraintsChange={setEngineeringConstraints}
              objectiveTemplate={objectiveTemplate}
              onObjectiveTemplateChange={setObjectiveTemplate}
              selectedVariables={selectedOptimizationVariables}
              onSelectedVariablesChange={setSelectedOptimizationVariables}
              topN={topN}
              onTopNChange={setTopN}
              comparisonSchemeIds={comparisonSchemeIds}
              onComparisonSchemeIdsChange={setComparisonSchemeIds}
              onComparisonSimulationIdsChange={setComparisonSimulationIds}
              onSelectSimulation={(simulationId, stageNumber) => { setSimId(simulationId); setSelStage(stageNumber ?? null); }}
              onSimulationsCreated={async () => { if (designId) await loadSims(designId); }}
              onDesignVariantCreated={async (variantId) => {
                await loadDesigns();
                await switchSimulationContext(variantId, opts.model_type);
              }}
            />}
          </div>
          </>}
        </aside>
        <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {!sim ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: TH.textSec }}>
          {busy ? (
            <div role="status" style={{ fontSize: 12, fontWeight: 600, color: TH.sim }}>计算中…</div>
          ) : loading ? null : (
            <>
              <div>当前 {opts.model_type} 模型暂无模拟结果</div>
              <div style={{ fontSize: 13 }}>在左侧设置参数后运行模拟</div>
            </>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '5px 10px', background: '#FAFBFC', borderBottom: `1px solid ${TH.headerBorder}`, overflowX: 'auto' }}>
            {[
              { l: '模型', v: sim.model_type, u: '' },
              { l: '平均半缝长', v: sim.total_half_length, u: 'm' },
              { l: '平均支撑半缝长', v: sim.total_propped_length, u: 'm' },
              { l: '平均缝宽', v: sim.avg_width, u: 'mm' },
              { l: '平均液效', v: sim.avg_efficiency, u: '%' },
              { l: '平均净压', v: String(sim.summary?.avg_net_pressure ?? '-'), u: 'MPa' },
              { l: '平均裂缝导流能力', v: String(sim.summary?.avg_conductivity ?? '-'), u: 'D·mm' },
              { l: '段数', v: String(sim.summary?.stage_count ?? stages.length), u: '段' },
              { l: '有效性', v: String(sim.summary?.effectiveness_score ?? '—'), u: String(sim.summary?.effectiveness_grade ?? '') },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 4, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: 10, color: TH.textMuted }}>{s.l}</span>
                <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: TH.text }}>
                  {s.v}<span style={{ fontSize: 10, fontWeight: 400, color: TH.textSec, marginLeft: 2 }}>{s.u}</span>
                </span>
              </div>
            ))}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: TH.statBorder }}>
            {/* 主视图: 整段水平井 + 全部缝面 + 地层 (three.js) */}
            <div style={{ flex: 1, minHeight: 0, background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.sim, background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>三维裂缝扩展 · {selStage === null ? '全井' : `第 ${selStage} 段`}</span>
              </div>
              <div style={{ padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', borderBottom: `1px solid ${TH.headerBorder}`, background: '#fff' }}>
                <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, color: TH.textSec }}>显示参数</span>
                <select aria-label="显示参数" value={resultVariableId}
                  onChange={(event) => setResultVariableId(event.target.value as ResultPropertyId)} style={toolbarSelect}>
                  {RESULT_GROUPS.map((group) => <optgroup key={group} label={{ Geometry: '几何形态', 'Pressure / Stress': '压力 / 应力', Proppant: '支撑剂', 'Post-Frac': '压后能力', Fluid: '压裂液' }[group]}>
                    {Object.values(RESULT_PROPERTIES).filter((definition) => definition.group === group).map((definition) => (
                      <option key={definition.id} value={definition.id} disabled={definition.capability === 'NOT_AVAILABLE'}
                        title={definition.capabilityReason}>
                        {definition.label}{definition.capability === 'PARTIAL' ? '（部分可用）' : definition.capability === 'NOT_AVAILABLE' ? '（暂无结果）' : ''}
                      </option>
                    ))}
                  </optgroup>)}
                </select>
                <span style={{ fontSize: 10, fontWeight: 700, color: TH.textSec }}>色标数据</span>
                <select aria-label="色标数据范围" value={rangeScope}
                  onChange={(event) => setRangeScope(event.target.value as ResultRangeScope)} style={toolbarSelect}
                  disabled={resultVariable.dataType === 'CATEGORICAL'}>
                  <option value="GLOBAL">全过程</option>
                  <option value="CURRENT_FRAME">当前时刻</option>
                </select>
                <span style={{ fontSize: 10, fontWeight: 700, color: TH.textSec }}>色标范围</span>
                <select aria-label="色标范围" value={rangeMethod}
                  onChange={(event) => setRangeMethod(event.target.value as ResultRangeMethod)} style={toolbarSelect}
                  disabled={resultVariable.dataType === 'CATEGORICAL'}>
                  <option value="MIN_MAX">自动</option>
                  <option value="P2_P98">自动（排除极值）</option>
                  <option value="MANUAL">固定</option>
                </select>
                {comparisonSimulationIds.length > 1 && <select aria-label="方案对比色标范围" value={comparisonRangeMode}
                  onChange={(event) => setComparisonRangeMode(event.target.value as typeof comparisonRangeMode)} style={toolbarSelect}
                  disabled={resultVariable.dataType === 'CATEGORICAL'}>
                  <option value="PER_SCHEME">各方案独立</option>
                  <option value="COMPARISON_GLOBAL">对比全局范围</option>
                  <option value="MANUAL">固定范围</option>
                </select>}
                {(rangeMethod === 'MANUAL' || comparisonRangeMode === 'MANUAL') && resultVariable.dataType !== 'CATEGORICAL' && <>
                  <input aria-label="色标下限" title="色标下限" type="number" value={manualMin}
                    onChange={(event) => setManualMin(event.target.value)} style={{ ...toolbarInput, width: 54 }} />
                  <input aria-label="色标上限" title="色标上限" type="number" value={manualMax}
                    onChange={(event) => setManualMax(event.target.value)} style={{ ...toolbarInput, width: 54 }} />
                </>}
                <label style={toolbarCheck}><input type="checkbox" checked={showHydraulicFractures}
                  onChange={(event) => setShowHydraulicFractures(event.target.checked)} />水力裂缝</label>
                <label style={toolbarCheck}><input type="checkbox" checked={showWellbore}
                  onChange={(event) => setShowWellbore(event.target.checked)} />井筒</label>
                <label title="控制首个射孔点之前显示的上游井段；水平段与趾端始终保留" style={{ ...toolbarCheck, gap: 3 }}>
                  井筒上延范围
                  <select aria-label="井筒上延范围" value={wellboreRangeMode}
                    disabled={!showWellbore}
                    onChange={(event) => setWellboreRangeMode(event.target.value as WellboreRangeMode)} style={toolbarSelect}>
                    <option value="STIMULATED">压裂水平段</option>
                    <option value="UPSTREAM_500">上延 500 m</option>
                    <option value="UPSTREAM_1000">上延 1000 m</option>
                    <option value="FULL">全井筒</option>
                    <option value="CUSTOM">自定义</option>
                  </select>
                </label>
                {wellboreRangeMode === 'CUSTOM' && <label style={{ ...toolbarCheck, gap: 3 }}>
                  上延长度（m）
                  <input aria-label="井筒上延长度（m）" type="number" min={0} step={100}
                    disabled={!showWellbore} value={wellboreCustomUpstreamM}
                    onChange={(event) => setWellboreCustomUpstreamM(Math.max(0, Number(event.target.value) || 0))}
                    style={{ ...toolbarInput, width: 62 }} />
                </label>}
                <label style={toolbarCheck}><input type="checkbox" checked={showGeologyGrid}
                  onChange={(event) => setShowGeologyGrid(event.target.checked)} />地质网格</label>
                <label style={{ ...toolbarCheck, gap: 3 }}>视窗背景
                  <select aria-label="视窗背景" value={sceneBackground}
                    onChange={(event) => setSceneBackground(event.target.value as SceneBackground)} style={toolbarSelect}>
                    <option value="LIGHT">白底</option>
                    <option value="DARK">黑底</option>
                  </select>
                </label>
                {String(sim.model_type).toUpperCase() === 'UFM' && <><label style={toolbarCheck}>
                  <input type="checkbox" checked={showNaturalFractures} disabled={!displayedNaturalFractures.length}
                    onChange={(event) => setShowNaturalFractures(event.target.checked)} />天然裂缝{displayedNaturalFractures.length ? '' : '（缺失）'}
                </label>
                {displayedNaturalFractures.length > 0 && <label style={{ ...toolbarCheck, gap: 3 }}>颜色
                  <input aria-label="天然裂缝颜色" type="color" value={naturalFractureColor}
                    onChange={(event) => setNaturalFractureColor(event.target.value.toUpperCase())}
                    style={{ width: 24, height: 20, padding: 0, border: `1px solid ${TH.headerBorder}`, background: 'transparent', cursor: 'pointer' }} />
                </label>}
                </>}
                <label style={toolbarCheck}><input type="checkbox" checked={showStressDirection}
                  onChange={(event) => setShowStressDirection(event.target.checked)} />应力方向</label>
                {String(sim.model_type).toUpperCase() === 'UFM' && <label style={toolbarCheck}><input type="checkbox" checked={showInteractionPoints}
                  disabled={!displayedInteractions.length}
                  onChange={(event) => setShowInteractionPoints(event.target.checked)} />交互点</label>}
                <button disabled={selStage === null} onClick={() => setSelStage(null)}
                  style={toolbarActionButton('VIEW', selStage === null)}>全井显示</button>
                <button onClick={() => setResetViewNonce((value) => value + 1)} style={toolbarActionButton('VIEW')}>重置视角</button>
                <button onClick={() => setShowModelInfo((value) => !value)} style={toolbarActionButton('INFO', showModelInfo)}>模型信息</button>
                <button onClick={() => setActiveBottomDock((value) => value === 'RESULTS' ? null : 'RESULTS')} style={toolbarActionButton('RESULT', activeBottomDock === 'RESULTS')}>分段结果</button>
                <button disabled={!stages.length} onClick={() => {
                  if (selStage === null && stages[0]) setSelStage(stages[0].stage_number);
                  setActiveBottomDock((value) => value === 'STAGE' ? null : 'STAGE');
                }} style={toolbarActionButton('ANALYSIS', activeBottomDock === 'STAGE')}>单段分析</button>
                <button onClick={() => setActiveBottomDock((value) => value === 'CURVES' ? null : 'CURVES')}
                  style={toolbarActionButton('RESULT', activeBottomDock === 'CURVES')}>动态曲线</button>
                <button onClick={() => {
                  setActiveBottomDock(null);
                  setActiveWorkflowStep('OPTIMIZATION');
                  setActiveSubSection('敏感性分析');
                }} style={toolbarActionButton('ANALYSIS', activeWorkflowStep === 'OPTIMIZATION')}>敏感性分析</button>
                <button onClick={exportSim} style={toolbarActionButton('EXPORT')}>导出 JSON</button>
                <button onClick={async () => {
                  try {
                    const { data } = await fractureAPI.exportCsv(sim.id);
                    const url = URL.createObjectURL(new Blob([data as BlobPart]));
                    const a = document.createElement('a');
                    a.href = url; a.download = `fracsim_${sim.id}_${sim.model_type}.csv`; a.click();
                    URL.revokeObjectURL(url);
                  } catch { message.error('CSV 导出失败'); }
                }} style={toolbarActionButton('EXPORT')}>导出 CSV</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, position: 'relative', background: sceneBackground === 'DARK' ? '#000' : '#fff' }}>
                <WholeWell3DView
                  stages={viewerStages}
                  survey={survey}
                  layers={layers}
                  horizons={horizons}
                  natFracs={displayedNaturalFractures}
                  stageLocalNfModels={displayedStageLocalNfModels}
                  modelType={sim.model_type}
                  selStage={selStage}
                  onPick={setSelStage}
                  playbackClusters={playbackClusters}
                  currentFrame={currentFrame}
                  apertureExaggeration={apertureExaggeration}
                  stressAzimuth={stressAzimuth}
                  resultVariableId={resultVariableId}
                  resultRange={resultRange}
                  showHydraulicFractures={showHydraulicFractures}
                  showWellbore={showWellbore}
                  wellboreRangeMode={wellboreRangeMode}
                  wellboreCustomUpstreamM={wellboreCustomUpstreamM}
                  showGeologyGrid={showGeologyGrid}
                  sceneBackground={sceneBackground}
                  showNaturalFractures={String(sim.model_type).toUpperCase() === 'UFM' && showNaturalFractures}
                  naturalFractureColor={naturalFractureColor}
                  showStressDirection={showStressDirection}
                  interactions={displayedInteractions}
                  showInteractionPoints={String(sim.model_type).toUpperCase() === 'UFM' && showInteractionPoints}
                  onPickInteraction={setSelectedInteraction}
                  onHover={setHoveredFracture}
                  resetViewNonce={resetViewNonce}
                />
                {showGeologyGrid && geologyGridReference && <div style={{
                  position: 'absolute', right: 12, bottom: 12, padding: '5px 8px',
                  border: `1px solid ${sceneBackground === 'DARK' ? '#526173' : TH.headerBorder}`,
                  background: sceneBackground === 'DARK' ? 'rgba(0,0,0,0.78)' : 'rgba(255,255,255,0.9)',
                  color: sceneBackground === 'DARK' ? '#E5EAF0' : TH.textSec,
                  fontSize: 11, fontWeight: 600, pointerEvents: 'none',
                }}>地质网格 · {geologyGridReference}</div>}
                {showModelInfo && <div role="dialog" aria-label="模型信息" style={{
                  position: 'absolute', left: 12, top: 12, width: 280, maxHeight: '78%', overflow: 'auto',
                  background: 'rgba(255,255,255,0.98)', border: `1px solid ${TH.headerBorder}`,
                  boxShadow: '0 4px 14px rgba(25,35,50,0.16)', padding: 10, fontSize: 10, color: TH.textSec,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ color: TH.text }}>模型信息</strong>
                    <button aria-label="关闭模型信息" onClick={() => setShowModelInfo(false)} style={{ ...toolbarButton, padding: '1px 5px' }}>×</button>
                  </div>
                  {[
                    ['裂缝模型', fractureModelLabel(sim.model_type)],
                    ['计算模式', simulationMode === 'Calibrated' ? '标定模拟' : '预测模拟'],
                    ['现场标定', calibrationStatus === 'Field Calibrated' ? '已标定' : calibrationStatus === 'Partially Calibrated' ? '部分标定' : '未标定'],
                    ['预测置信度', { High: '高', Medium: '中', Low: '低' }[modelConfidence?.level ?? 'Low']],
                    ['结果粒度', stages.some((stage) => stage.clusters?.length) ? `簇级 · ${totalSolverClusters} 个真实簇对象` : '段级兼容结果'],
                    ['应力阴影', (selectedDesign?.cluster_per_stage ?? 1) > 1 ? 'Sneddon 应力阴影' : '单簇不适用'],
                    ['簇间流量分配', (selectedDesign?.cluster_per_stage ?? 1) > 1 ? '射孔限流与簇间竞争' : '单簇'],
                    ['滤失模型', 'Carter / Nolte'],
                    ['初始缝高', seedHeightTrace ? `${seedHeightTrace.seed_height_m ?? '—'} m（${seedHeightSourceLabel(seedHeightTrace.seed_height_source)}）` : '暂无结果'],
                  ].map(([label, value]) => <div key={String(label)} style={{ display: 'grid', gridTemplateColumns: '82px 1fr', gap: 8, padding: '4px 0', borderBottom: `1px solid ${TH.statBorder}` }}>
                    <span style={{ color: TH.textMuted }}>{label}</span><span>{value}</span>
                  </div>)}
                  {diagnosticCodes.has('HEIGHT_CLAMP_ACTIVE') && <div style={{ marginTop: 7, color: '#A64B32' }}>缝高计算边界已触发。</div>}
                  {recommendations.length > 0 && <div style={{ marginTop: 8 }}>
                    <strong style={{ color: TH.text }}>工程提示</strong>
                    {recommendations.map((item, index) => <div key={index} style={{ marginTop: 4, color: item.level === 'warn' ? '#A64B32' : TH.textSec }}>{item.text}</div>)}
                  </div>}
                </div>}
                {/* 右侧专业色标 */}
                <div style={{ position: 'absolute', right: 10, top: 10, minWidth: 116, background: 'rgba(255,255,255,0.96)', borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.15)', padding: 7, userSelect: 'none' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: TH.text }}>{resultVariable.label}</div>
                  <div style={{ fontSize: 10, color: TH.textMuted }}>{resultVariable.unit || '分类结果'}</div>
                  {resultVariable.dataType === 'CATEGORICAL' ? (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {categoricalValues.length ? categoricalValues.map((category) => (
                        <span key={category} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: TH.textSec }}>
                          <i style={{ width: 9, height: 9, background: categoryColor(category), display: 'inline-block' }} />{category}
                        </span>
                      )) : <span style={{ fontSize: 10, color: TH.textMuted }}>当前时刻无可用结果</span>}
                    </div>
                  ) : <div style={{ height: 112, marginTop: 6, display: 'flex', alignItems: 'stretch', gap: 4 }}>
                  {/* 刻度标签 */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: 4, fontSize: 10, fontFamily: 'monospace', color: '#333', textAlign: 'right', minWidth: 28 }}>
                    {resultRange.available
                      ? colorBarTicks.map((tick) => <span key={tick} style={{ lineHeight: 1 }}>{formatResultTick(tick, resultRange)}</span>)
                      : <span style={{ writingMode: 'vertical-rl' }}>暂无结果</span>}
                  </div>
                  {/* 色条 + 刻度线 */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative' }}>
                    <div style={{ width: 14, flex: 1, borderRadius: 3, background: resultRange.available ? resultGradient(resultVariableId) : '#D6DAE0', margin: '2px 0' }} />
                    {colorBarTicks.map((_tick, i) => (
                      <div key={i} style={{ position: 'absolute', top: colorBarTicks.length === 1 ? '50%' : `${(i / (colorBarTicks.length - 1)) * 100}%`, right: -5, width: 6, height: 1, background: '#333' }} />
                    ))}
                  </div>
                  </div>}
                  <div style={{ marginTop: 5, fontSize: 10, color: TH.textMuted }}>
                    范围：{resultRange.available ? `${formatResultTick(resultRange.min, resultRange)} – ${formatResultTick(resultRange.max, resultRange)} ${resultVariable.unit}` : '—'}<br />
                    时间：{Math.round(simulationClock.currentTimeS)} s · 压裂段：{selStage ?? '全部'}
                  </div>
                </div>
                <div style={{ position: 'absolute', left: 12, bottom: 12, background: 'rgba(20,28,38,0.78)', color: '#fff', borderRadius: 3, padding: '6px 9px', fontSize: 10, lineHeight: 1.65, fontFamily: 'monospace', pointerEvents: 'none' }}>
                  <div>时间：{formatSimulationTime(simulationClock.currentTimeS)}</div>
                  {statusStageId === null
                    ? <div>范围：全井 · {stages.length} 段</div>
                    : <div>压裂段：{String(statusStageId).padStart(2, '0')}</div>}
                  <div>已起裂簇：{activeClusterCount} / {statusClusterTotal}</div>
                  {!playback.hasTimeSeries && <div style={{ color: '#FFD7B5' }}>无可回放时间步</div>}
                </div>
                {showNaturalFractures && naturalFractureLegendStates.length > 0 && (
                  <div style={{
                    position: 'absolute', right: 10, bottom: 10, padding: 7,
                    background: sceneBackground === 'DARK' ? 'rgba(8,12,18,0.88)' : 'rgba(255,255,255,0.95)',
                    border: `1px solid ${sceneBackground === 'DARK' ? '#566171' : TH.headerBorder}`,
                    borderRadius: 4, fontSize: 10,
                    color: sceneBackground === 'DARK' ? '#F1F3F5' : TH.textSec,
                    pointerEvents: 'none',
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 3 }}>天然裂缝状态</div>
                    {naturalFractureLegendStates.map((state) => {
                      const style = naturalFractureDisplayStyle(state, sceneBackground === 'DARK', passiveNaturalFractureColor);
                      const color = `#${style.color.toString(16).padStart(6, '0')}`;
                      return <span key={state} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <i style={{ width: 13, height: style.emphasized ? 3 : 1, display: 'inline-block', background: color }} />
                        {NF_VISUAL_LABELS[state]}
                      </span>;
                    })}
                    {showInteractionPoints && displayedInteractions.length > 0 && <div style={{ marginTop: 3, opacity: 0.72 }}>球点：交互位置</div>}
                  </div>
                )}
                {selectedInteraction && (
                  <div role="dialog" aria-label="交互点详情" style={{
                    position: 'absolute', right: 10, top: 92, width: 250, maxHeight: '62%', overflow: 'auto',
                    background: 'rgba(255,255,255,0.98)', border: `1px solid ${TH.headerBorder}`,
                    borderRadius: 4, boxShadow: '0 3px 14px rgba(0,0,0,0.18)', padding: 9, fontSize: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong style={{ color: TH.text }}>交互点详情</strong>
                      <button aria-label="关闭交互点详情" onClick={() => setSelectedInteraction(null)} style={{ ...toolbarButton, padding: '1px 5px' }}>×</button>
                    </div>
                    {interactionDetailRows(selectedInteraction).map((row) => <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 7, padding: '3px 0', borderBottom: `1px solid ${TH.statBorder}` }}>
                      <span style={{ color: TH.textMuted }}>{row.label}</span><span style={{ color: TH.text, wordBreak: 'break-word' }}>{row.value}</span>
                    </div>)}
                    <div style={{ marginTop: 6, color: TH.textMuted }}>仅显示模拟结果中真实可用字段。</div>
                  </div>
                )}
                {hoveredFracture && hoveredCluster && (
                  <div style={{
                    position: 'absolute', left: hoveredFracture.x + 12, top: hoveredFracture.y + 12,
                    minWidth: 180, padding: '7px 9px', background: 'rgba(20,28,38,0.92)',
                    color: '#fff', borderRadius: 4, fontSize: 10, lineHeight: 1.55, pointerEvents: 'none',
                  }}>
                    <div style={{ fontWeight: 700 }}>第 {hoveredCluster.stageId} 段 · {hoveredCluster.clusterId}</div>
                    <div>时间：{Math.round(simulationClock.currentTimeS)} s · 测深：{hoveredCluster.perforation.md.toFixed(1)} m</div>
                    <div>半缝长：{hoveredCluster.lengthM.toFixed(1)} m · 双翼展长：{(hoveredCluster.lengthM * 2).toFixed(1)} m</div>
                    <div>缝高：{hoveredCluster.heightM.toFixed(1)} m</div>
                    {hoveredCluster.hasWidthResult && <div>缝宽：{hoveredCluster.actualWidthMm.toFixed(3)} mm</div>}
                    {hoveredCluster.hasFracturePressureResult && <div>裂缝压力：{hoveredCluster.fracturePressureMpa.toFixed(3)} MPa</div>}
                    {hoveredCluster.hasNetPressureResult && <div>净压力：{hoveredCluster.netPressureMpa.toFixed(3)} MPa</div>}
                    {hoveredCluster.proppantConcentrationKgm3 !== undefined && <div>支撑剂浓度：{hoveredCluster.proppantConcentrationKgm3.toFixed(2)} kg/m³</div>}
                    {hoveredCluster.proppedWidthM !== undefined && <div>压后缝宽：{(hoveredCluster.proppedWidthM * 1000).toFixed(3)} mm</div>}
                    {hoveredCluster.conductivity !== undefined && <div>裂缝导流能力（工程估算）：{hoveredCluster.conductivity.toFixed(3)} D·mm</div>}
                    {hoveredCluster.fluidType && <div>压裂液：{hoveredCluster.fluidType}</div>}
                    {hoveredCluster.proppantType && <div>支撑剂：{hoveredCluster.proppantType}</div>}
                  </div>
                )}
              </div>
              {streamJob && simulationRunState !== 'IDLE' ? <StreamingSimulationTimeline
                state={simulationRunState}
                viewerMode={streamViewerMode}
                job={streamJob}
                frames={streamFrames}
                frameIndex={streamDisplayIndex}
                apertureExaggeration={apertureExaggeration}
                onPause={() => {
                  setStreamFrameIndex(Math.max(0, streamFrames.length - 1));
                  setStreamViewerMode('PLAYBACK');
                  setSimulationRunState('PLAYBACK');
                }}
                onReturnLive={() => {
                  setStreamViewerMode('LIVE');
                  setSimulationRunState(
                    streamJob.status === 'RUNNING' ? 'LIVE'
                      : streamJob.status === 'PENDING' ? 'STARTING'
                        : streamJob.status === 'COMPLETED' ? 'COMPLETED'
                          : streamJob.status === 'FAILED' ? 'FAILED' : 'CANCELLED'
                  );
                }}
                onSeek={(index) => {
                  setStreamFrameIndex(index);
                  setStreamViewerMode('PLAYBACK');
                  if (streamJobActive) setSimulationRunState('PLAYBACK');
                }}
                onCancel={cancelSimulation}
                onApertureChange={setApertureExaggeration}
              /> : <SimulationTimeline
                currentTimeS={simulationClock.currentTimeS} durationS={playback.durationS}
                isPlaying={simulationClock.isPlaying} playbackSpeed={simulationClock.playbackSpeed}
                hasTimeSeries={playback.hasTimeSeries} apertureExaggeration={apertureExaggeration}
                onPlay={simulationClock.play} onPause={simulationClock.pause} onReplay={simulationClock.replay}
                onSeek={simulationClock.seek} onSpeedChange={simulationClock.setPlaybackSpeed}
                onApertureChange={setApertureExaggeration}
              />}

            </div>

            {activeBottomDock === 'CURVES' && <section style={{
              flex: '0 0 300px', maxHeight: '48%', minHeight: 230, background: '#fff',
              borderTop: `1px solid ${TH.headerBorder}`, overflow: 'hidden',
            }}>
              <DynamicSimulationCurves
                compact
                stages={stages}
                liveFrames={streamFrames}
                selectedStage={selStage}
                currentTimeS={streamFrames.length ? streamFrames[streamDisplayIndex]?.timelineTimeS : simulationClock.currentTimeS}
                onTimeChange={(timeS) => {
                  if (streamFrames.length) {
                    let closest = 0;
                    streamFrames.forEach((frame, index) => {
                      if (Math.abs((frame.timelineTimeS ?? frame.timeS) - timeS)
                        < Math.abs((streamFrames[closest].timelineTimeS ?? streamFrames[closest].timeS) - timeS)) closest = index;
                    });
                    setStreamFrameIndex(closest);
                    setStreamViewerMode('PLAYBACK');
                    if (streamJobActive) setSimulationRunState('PLAYBACK');
                  } else simulationClock.seek(timeS);
                }}
              />
            </section>}

            {activeBottomDock === 'RESULTS' && <section style={{
              flex: '0 0 220px', maxHeight: '36%', minHeight: 150, background: '#fff',
              borderTop: `1px solid ${TH.headerBorder}`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              <div style={{ padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 11, color: TH.text }}>分段 / 分簇结果</strong>
                  <span style={{ fontSize: 10, color: TH.textMuted }}>{selStage === null ? `全井 ${resultRows.length} 个对象` : `第 ${selStage} 段 ${resultRows.length} 个簇`}</span>
                  {selStage !== null && <button onClick={() => setSelStage(null)} style={toolbarButton}>显示全井</button>}
                </div>
                <button aria-label="收起分段结果" onClick={() => setActiveBottomDock(null)} style={{ ...toolbarButton, padding: '1px 6px' }}>收起</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                    <thead>
                      <tr style={{ background: '#FAFBFC', position: 'sticky', top: 0, zIndex: 1 }}>
                        {['段号', '簇号', '半缝长（m）', '缝高（m）', '最大缝宽（mm）', '净压力（MPa）', '裂缝导流能力（D·mm）', '起裂状态'].map((heading) => (
                          <th key={heading} style={{ padding: '5px 8px', textAlign: 'right', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>{heading}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultRows.map((row) => <tr key={row.clusterId} onClick={() => setSelStage(row.stageNumber)}
                        style={{ cursor: 'pointer', background: row.stageNumber === selStage ? TH.accentLight : '#fff' }}>
                        <td style={miniTd}>{row.stageNumber}</td>
                        <td style={miniTd}>{row.clusterNumber}</td>
                        <td style={miniTd}>{Number.isFinite(row.halfLength) ? row.halfLength.toFixed(1) : '—'}</td>
                        <td style={miniTd}>{Number.isFinite(row.height) ? row.height.toFixed(1) : '—'}</td>
                        <td style={miniTd}>{Number.isFinite(row.widthMm) ? row.widthMm.toFixed(3) : '—'}</td>
                        <td style={miniTd}>{Number.isFinite(row.netPressure) ? row.netPressure.toFixed(3) : '—'}</td>
                        <td style={miniTd}>{Number.isFinite(row.conductivity) ? row.conductivity.toFixed(2) : '—'}</td>
                        <td style={{ ...miniTd, color: row.initiated ? '#1E8449' : '#A64B32', fontFamily: 'inherit', fontWeight: 700 }}>{row.initiated ? '已起裂' : '未起裂'}</td>
                      </tr>)}
                    </tbody>
                  </table>
                </div>
                {selStage && (() => {
                  const selected = stages.find((stage) => stage.stage_number === selStage);
                  return selected ? <div style={{ width: 280, overflow: 'auto', borderLeft: `1px solid ${TH.headerBorder}` }}><StressInfoCard stage={selected} /></div> : null;
                })()}
              </div>
            </section>}
            {activeBottomDock === 'STAGE' && <section style={{
              flex: '0 0 310px', maxHeight: '48%', minHeight: 220, background: '#fff',
              borderTop: `1px solid ${TH.headerBorder}`, overflow: 'hidden',
            }}>
              <FracStageDetailPage
                embedded
                stageResults={stages}
                selectedStage={selStage}
                onSelectStage={setSelStage}
                onClose={() => setActiveBottomDock(null)}
              />
            </section>}
          </div>
        </>
      )}
        </div>
      </div>
    </div>
  );
}

const formatSimulationTime = (seconds: number): string => {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [hours, minutes, secs].map((value) => String(value).padStart(2, '0')).join(':');
};

function StreamingSimulationTimeline({
  state, viewerMode, job, frames, frameIndex, apertureExaggeration,
  onPause, onReturnLive, onSeek, onCancel, onApertureChange,
}: {
  state: SimulationRunState;
  viewerMode: 'LIVE' | 'PLAYBACK';
  job: SimulationJob;
  frames: SimulationPlaybackFrame[];
  frameIndex: number;
  apertureExaggeration: number;
  onPause: () => void;
  onReturnLive: () => void;
  onSeek: (index: number) => void;
  onCancel: () => void;
  onApertureChange: (scale: number) => void;
}) {
  const latestIndex = Math.max(0, frames.length - 1);
  const shownFrame = frames[Math.min(frameIndex, latestIndex)];
  const isRunning = job.status === 'PENDING' || job.status === 'RUNNING';
  const isFinalizing = isRunning && job.totalSteps > 0 && job.currentStep >= job.totalSteps;
  const statusLabel: Record<SimulationRunState, string> = {
    IDLE: '空闲', STARTING: '启动中', LIVE: '实时', PLAYBACK: '历史回放',
    COMPLETED: '已完成', FAILED: '失败', CANCELLED: '已取消',
  };
  const buttonStyle: React.CSSProperties = {
    border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.text,
    borderRadius: 3, padding: '3px 8px', fontSize: 11, cursor: 'pointer',
  };
  const metrics = job.metrics ?? {};
  const lastStep = (metrics.lastStepPerformance ?? {}) as Record<string, unknown>;
  const seconds = (value: unknown) => Number.isFinite(Number(value)) ? `${Number(value).toFixed(3)} s` : '—';
  return <div style={{ flexShrink: 0, borderTop: `1px solid ${TH.headerBorder}`, background: '#F8F9FB', padding: '7px 10px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <strong style={{ fontSize: 10, color: state === 'FAILED' ? '#B42318' : TH.sim }}>
        {isFinalizing ? '正在保存正式结果…' : `${statusLabel[state]}${isRunning ? ' · 计算中…' : ''}`}
      </strong>
      <span style={{ minWidth: 112, fontSize: 10, fontFamily: 'monospace', color: TH.text }}>
        {formatSimulationTime(shownFrame?.timelineTimeS ?? shownFrame?.timeS ?? job.currentTime)} · {job.currentStep}/{job.totalSteps}
      </span>
      {shownFrame?.stageId !== undefined && <span style={{ fontSize: 10, color: TH.textSec }}>
        趾端→跟端 · 施工序 {Number(shownFrame.stageIndex ?? 0) + 1} · 第 {shownFrame.stageId} 段
      </span>}
      <progress value={Math.max(0, Math.min(1, job.progress))} max={1} style={{ width: 90 }} />
      {viewerMode !== 'PLAYBACK' && frames.length > 0 && isRunning
        ? <button type="button" onClick={onPause} style={buttonStyle}>Ⅱ 暂停播放</button>
        : null}
      {viewerMode === 'PLAYBACK'
        ? <button type="button" onClick={onReturnLive} style={buttonStyle}>{isRunning ? '返回实时' : '返回最新'}</button>
        : null}
      {isRunning ? <button
        type="button"
        onClick={onCancel}
        title="终止当前模拟任务"
        style={{
          ...buttonStyle,
          border: '1px solid #B42318',
          background: '#C4322B',
          color: '#fff',
          fontWeight: 700,
          boxShadow: '0 1px 2px rgba(180, 35, 24, 0.22)',
        }}
        onMouseEnter={(event) => { event.currentTarget.style.background = '#A61B14'; }}
        onMouseLeave={(event) => { event.currentTarget.style.background = '#C4322B'; }}
      >取消任务</button> : null}
      <input aria-label="已接收真实帧" type="range" min={0} max={latestIndex} step={1}
        value={Math.min(frameIndex, latestIndex)} disabled={frames.length < 2}
        title={frames.length < 2 ? '至少接收两个真实时间步后可拖动' : '拖动查看已完成的真实时间步'}
        onPointerDown={() => { if (viewerMode !== 'PLAYBACK' && frames.length > 1) onPause(); }}
        onInput={(event) => onSeek(Number((event.target as HTMLInputElement).value))}
        style={{ flex: 1, minWidth: 100 }} />
      <span style={{ fontSize: 10, color: TH.textMuted }}>{frames.length ? `真实帧 ${Math.min(frameIndex, latestIndex) + 1}/${frames.length}` : '计算中…'}</span>
      <label title="仅放大显示缝宽，不改变模拟 actual width" style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: TH.textSec }}>
        缝宽视觉放大
        <select value={apertureExaggeration} onChange={(event) => onApertureChange(Number(event.target.value))}
          style={{ fontSize: 10, padding: '2px 4px', border: `1px solid ${TH.headerBorder}`, borderRadius: 3 }}>
          {[1, 10, 50, 100].map((scale) => <option key={scale} value={scale}>{scale}×</option>)}
        </select>
      </label>
    </div>
    {frames.length > 0 && <div style={{ marginTop: 5, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 9, color: TH.textSec }}>
      <span title="浏览器点击运行到首个真实裂缝帧完成绘制">点击→首帧 {seconds(metrics.clickToFirstRenderedFrameSeconds ?? metrics.timeToFirstCommittedFrameSeconds)}</span>
      <span>首步求解 {seconds(metrics.firstTimeStepSolveSeconds ?? lastStep.timeStepCommitSeconds)}</span>
      <span>网络 {seconds(lastStep.networkSolveSeconds)}</span>
      <span>支撑剂 {seconds(lastStep.proppantSeconds)}</span>
      <span>Mesh/DTO {seconds(lastStep.meshDtoSeconds)}</span>
      <span>前端绘制 {seconds(metrics.frontendFrameToPaintSeconds)}</span>
      <span>事件流 {Number(metrics.eventBytes ?? 0).toLocaleString()} B</span>
    </div>}
    {state === 'FAILED' && <div style={{ marginTop: 4, fontSize: 10, color: '#B42318' }}>{job.failureReason || '求解失败，Viewer 保留最后有效收敛帧'}</div>}
    {state === 'CANCELLED' && <div style={{ marginTop: 4, fontSize: 10, color: TH.textSec }}>已保留最后收敛时间步；暂停仅影响 Viewer，未中断已提交状态。</div>}
  </div>;
}

function SimulationTimeline({
  currentTimeS, durationS, isPlaying, playbackSpeed, hasTimeSeries, apertureExaggeration,
  onPlay, onPause, onReplay, onSeek, onSpeedChange, onApertureChange,
}: {
  currentTimeS: number;
  durationS: number;
  isPlaying: boolean;
  playbackSpeed: PlaybackSpeed;
  hasTimeSeries: boolean;
  apertureExaggeration: number;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  onSeek: (timeS: number) => void;
  onSpeedChange: (speed: PlaybackSpeed) => void;
  onApertureChange: (scale: number) => void;
}) {
  const buttonStyle: React.CSSProperties = {
    border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.text,
    borderRadius: 3, padding: '3px 8px', fontSize: 11, cursor: hasTimeSeries ? 'pointer' : 'not-allowed',
  };
  return (
    <div style={{ flexShrink: 0, borderTop: `1px solid ${TH.headerBorder}`, background: '#F8F9FB', padding: '7px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: TH.textSec, marginRight: 2 }}>模拟时间</span>
        <button type="button" disabled={!hasTimeSeries} onClick={isPlaying ? onPause : onPlay} style={buttonStyle}>
          {isPlaying ? 'Ⅱ 暂停' : '▶ 播放'}
        </button>
        <button type="button" disabled={!hasTimeSeries} onClick={onReplay} style={buttonStyle}>↺ 重播</button>
        <span style={{ minWidth: 112, fontSize: 10, fontFamily: 'monospace', color: TH.text }}>
          {formatSimulationTime(currentTimeS)} / {formatSimulationTime(durationS)}
        </span>
        <input aria-label="模拟时间" type="range" min={0} max={Math.max(durationS, 1)} step="any"
          value={Math.min(currentTimeS, Math.max(durationS, 1))} disabled={!hasTimeSeries}
          onChange={(event) => onSeek(Number(event.target.value))} style={{ flex: 1, minWidth: 100 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: TH.textSec }}>
          速度
          <select value={playbackSpeed} disabled={!hasTimeSeries}
            onChange={(event) => onSpeedChange(Number(event.target.value) as PlaybackSpeed)}
            style={{ fontSize: 10, padding: '2px 4px', border: `1px solid ${TH.headerBorder}`, borderRadius: 3 }}>
            {[1, 2, 5, 10, 20, 50, 100, 250, 500, 1000, 2000].map((speed) => (
              <option key={speed} value={speed}>{speed}×{speed === 2000 ? '（极速预览）' : ''}</option>
            ))}
          </select>
        </label>
        <label title="仅放大显示缝宽，不改变模拟 actual width" style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: TH.textSec }}>
          缝宽视觉放大
          <select value={apertureExaggeration} onChange={(event) => onApertureChange(Number(event.target.value))}
            style={{ fontSize: 10, padding: '2px 4px', border: `1px solid ${TH.headerBorder}`, borderRadius: 3 }}>
            {[1, 10, 50, 100].map((scale) => <option key={scale} value={scale}>{scale}×</option>)}
          </select>
          <span style={{ color: TH.textMuted }}>(仅视觉)</span>
        </label>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// WholeWell3DView — 整段水平井 3D 主视图
//   井筒沿真实测斜走 (CatmullRom 曲线)
//   每段缝面贴在 perf_md 对应的世界坐标, 法向 = 横切井筒水平方向 (即横向缝)
//   地层做半透明薄板 (依据 target/邻层 TVD 范围)
//   点击缝面 → 选段 (onPick)
// ═══════════════════════════════════════════════
function WholeWell3DView({ stages, survey, layers, horizons, natFracs, stageLocalNfModels, modelType, selStage, onPick, playbackClusters, currentFrame, apertureExaggeration, stressAzimuth, resultVariableId, resultRange, showHydraulicFractures, showWellbore, wellboreRangeMode, wellboreCustomUpstreamM, showGeologyGrid, sceneBackground, showNaturalFractures, naturalFractureColor, showStressDirection, interactions, showInteractionPoints, onPickInteraction, onHover, resetViewNonce }: {
  stages: StageResult[];
  survey: SurveyPoint[];
  layers: FormationLayerRow[];
  horizons: HorizonSurfaceRow[];
  natFracs: any[];
  stageLocalNfModels: Array<{ stageId: string; influenceDomain: { bounds: { minXYZ: [number, number, number]; maxXYZ: [number, number, number] } } }>;
  modelType: string;
  selStage: number | null;
  onPick: (n: number | null) => void;
  playbackClusters: SimulationPlaybackCluster[];
  currentFrame: SimulationPlaybackFrame | null;
  apertureExaggeration: number;
  stressAzimuth: number | null;
  resultVariableId: ResultPropertyId;
  resultRange: ResultRange;
  showHydraulicFractures: boolean;
  showWellbore: boolean;
  wellboreRangeMode: WellboreRangeMode;
  wellboreCustomUpstreamM: number;
  showGeologyGrid: boolean;
  sceneBackground: SceneBackground;
  showNaturalFractures: boolean;
  naturalFractureColor: string;
  showStressDirection: boolean;
  interactions: any[];
  showInteractionPoints: boolean;
  onPickInteraction: (value: Record<string, unknown>) => void;
  onHover: (value: { clusterId?: string; stageNo: number; x: number; y: number } | null) => void;
  resetViewNonce: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const [sceneRevision, setSceneRevision] = useState(0);
  const fractureMeshesRef = useRef<Map<string, THREE.Mesh[]>>(new Map());
  const stageFractureMeshesRef = useRef<Map<string, THREE.Mesh[]>>(new Map());
  const sceneRadiusRef = useRef(10);
  const worldOfRef = useRef<(md: number) => THREE.Vector3 | null>(() => null);
  const passiveNaturalFractureColor = Number.parseInt(naturalFractureColor.slice(1), 16);
  const resultVariableRef = useRef(resultVariableId);
  const resultRangeRef = useRef(resultRange);
  const displayRef = useRef({ showHydraulicFractures, showWellbore, showGeologyGrid, showNaturalFractures, showStressDirection, showInteractionPoints });
  const onHoverRef = useRef(onHover);
  const onPickInteractionRef = useRef(onPickInteraction);
  resultVariableRef.current = resultVariableId;
  resultRangeRef.current = resultRange;
  displayRef.current = { showHydraulicFractures, showWellbore, showGeologyGrid, showNaturalFractures, showStressDirection, showInteractionPoints };
  onHoverRef.current = onHover;
  onPickInteractionRef.current = onPickInteraction;
  const topologyRevision = useMemo(
    () => fractureRenderTopologyKey(playbackClusters),
    [playbackClusters],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    if (!stages.length) {
      el.innerHTML = `<div style="display:flex;height:100%;align-items:center;justify-content:center;font-size:13px;color:${TH.textMuted}">无段数据</div>`;
      return;
    }
    if (!survey.length) {
      el.innerHTML = `<div style="display:flex;height:100%;align-items:center;justify-content:center;font-size:13px;color:${TH.textMuted}">未导入测斜, 无法定位水平段</div>`;
      return;
    }

    // 坐标: world X = ew (东), world Y = -tvd (TVD 朝下, 翻转使地表为 0 朝上),
    //       world Z = ns  (北).  three.js 默认 Y-up.
    const worldOf = (md: number): THREE.Vector3 | null => {
      const ss = survey;
      if (!ss.length) return null;
      let i = 0;
      while (i < ss.length - 1 && ss[i + 1].md < md) i++;
      const a = ss[i];
      const b = ss[Math.min(i + 1, ss.length - 1)];
      if (a === b) return new THREE.Vector3(a.ew, -a.tvd, a.ns);
      const t = (md - a.md) / Math.max(1e-6, b.md - a.md);
      return new THREE.Vector3(
        a.ew + (b.ew - a.ew) * t,
        -(a.tvd + (b.tvd - a.tvd) * t),
        a.ns + (b.ns - a.ns) * t,
      );
    };
    worldOfRef.current = worldOf;

    // 井筒水平切线 — 决定缝面朝向 (横切缝, 垂直水平投影)
    const tanHorizOf = (md: number) => {
      if (survey.length < 2) return new THREE.Vector3(1, 0, 0);
      let i = 0;
      while (i < survey.length - 2 && survey[i + 1].md < md) i++;
      const a = survey[i], b = survey[i + 1] ?? a;
      const dx = b.ew - a.ew, dz = b.ns - a.ns;
      const len = Math.hypot(dx, dz) || 1;
      return new THREE.Vector3(dx / len, 0, dz / len);
    };

    const W = el.clientWidth, H = el.clientHeight;
    let viewportWidth = W;
    let viewportHeight = H;
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const darkScene = sceneBackground === 'DARK';
    scene.background = new THREE.Color(darkScene ? 0x000000 : 0xffffff);

    const fractureRenderTemplates = buildFractureRenderTemplates(stages, playbackClusters, selStage);
    const showClusterDetail = fractureRenderTemplates.some((template) => template.dynamicClusterId !== undefined);
    const perforationMds = fractureRenderTemplates.map((template) => template.md);
    const sortedPerforationMds = [...perforationMds].sort((a, b) => a - b);
    const adjacentMdSpacings = sortedPerforationMds
      .slice(1)
      .map((md, index) => md - sortedPerforationMds[index])
      .filter((spacing) => spacing > 1e-6);
    const minPerforationMd = Math.min(...perforationMds);
    const maxPerforationMd = Math.max(...perforationMds);
    const minPerforationSpacing = adjacentMdSpacings.length
      ? Math.min(...adjacentMdSpacings)
      : Number.POSITIVE_INFINITY;
    const perfPoints = perforationMds.map((md) => worldOf(md)!).filter(Boolean);
    if (!perfPoints.length) {
      el.innerHTML = `<div style="padding:20px;color:${TH.textMuted}">射孔点位超出测斜范围</div>`;
      return;
    }
    const center = new THREE.Vector3();
    perfPoints.forEach((p) => center.add(p));
    center.multiplyScalar(1 / perfPoints.length);

    const maxHalfL = Math.max(...stages.map((s) => s.half_length));
    const lateralExtent = perfPoints[0].distanceTo(perfPoints[perfPoints.length - 1]);
    const sceneRadius = Math.max(lateralExtent * 0.6, maxHalfL * 1.3, 200);
    sceneRadiusRef.current = sceneRadius;

    const cam = new THREE.PerspectiveCamera(38, W / H, 1, sceneRadius * 40);
    cam.position.set(
      center.x + sceneRadius * 1.5,
      center.y + sceneRadius * 1.2,
      center.z + sceneRadius * 2.0,
    );
    cam.lookAt(center);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.autoClear = false;
    el.appendChild(renderer.domElement);
    (renderer.domElement as any).__scene = scene;  // 供增量更新使用

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dl = new THREE.DirectionalLight(0xffffff, 0.75);
    dl.position.set(center.x + sceneRadius, center.y + sceneRadius * 1.5, center.z + sceneRadius);
    scene.add(dl);
    const dl2 = new THREE.DirectionalLight(0xffffff, 0.25);
    dl2.position.set(center.x - sceneRadius, center.y - sceneRadius * 0.5, center.z - sceneRadius);
    scene.add(dl2);

    // ── 1) 坐标轴指示器 ──
    const groundY = -Math.min(...survey.map((s) => s.tvd)) + sceneRadius * 0.2;
    const makeTextSprite = (textValue: string, color: string, height: number) => {
      const canvas = document.createElement('canvas');
      canvas.width = 320;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'bold 36px Arial, "Microsoft YaHei", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 7;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = darkScene ? 'rgba(0,0,0,0.96)' : 'rgba(255,255,255,0.98)';
      ctx.strokeText(textValue, canvas.width / 2, canvas.height / 2);
      ctx.fillStyle = color;
      ctx.fillText(textValue, canvas.width / 2, canvas.height / 2);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: new THREE.CanvasTexture(canvas),
        transparent: true,
        depthTest: false,
      }));
      sprite.scale.set(height * 5.0, height, 1);
      return sprite;
    };
    const addArrow = (origin: THREE.Vector3, direction: THREE.Vector3, length: number, color: number, label: string, labelColor: string) => {
      const unit = direction.clone().normalize();
      const material = new THREE.MeshBasicMaterial({ color });
      const shaftLength = length * 0.78;
      const shaftRadius = Math.max(length * 0.055, 1.2);
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(shaftRadius, shaftRadius, shaftLength, 10), material);
      shaft.userData.displayCategory = 'stress';
      shaft.position.copy(origin).addScaledVector(unit, shaftLength * 0.5);
      shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), unit);
      scene.add(shaft);
      const head = new THREE.Mesh(new THREE.ConeGeometry(Math.max(length * 0.11, 1.8), length * 0.22, 10), material);
      head.userData.displayCategory = 'stress';
      head.position.copy(origin).addScaledVector(unit, shaftLength + length * 0.11);
      head.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), unit);
      scene.add(head);
      const sprite = makeTextSprite(label, labelColor, length * 0.22);
      if (sprite) {
        sprite.userData.displayCategory = 'stress';
        sprite.position.copy(origin).addScaledVector(unit, length * 1.12);
        scene.add(sprite);
      }
    };
    // 井筒始终沿真实测斜绘制。默认保留完整水平段和趾端，只限制首个
    // 射孔点之前的上游井段，避免垂直段把裂缝主体压缩得过小。
    const surveyMinMd = Math.min(...survey.map((point) => point.md));
    const surveyMaxMd = Math.max(...survey.map((point) => point.md));
    const upstreamLength = wellboreRangeMode === 'UPSTREAM_500'
      ? 500
      : wellboreRangeMode === 'UPSTREAM_1000'
        ? 1000
        : wellboreRangeMode === 'CUSTOM'
          ? Math.max(0, wellboreCustomUpstreamM)
          : 0;
    const wellStartMd = wellboreRangeMode === 'FULL'
      ? surveyMinMd
      : Math.max(surveyMinMd, minPerforationMd - upstreamLength);
    const wellPts = [
      worldOf(wellStartMd),
      ...survey
        .filter((point) => point.md > wellStartMd && point.md < surveyMaxMd)
        .map((point) => new THREE.Vector3(point.ew, -point.tvd, point.ns)),
      worldOf(surveyMaxMd),
    ].filter((point): point is THREE.Vector3 => point !== null);
    if (wellPts.length >= 2) {
      const curve = new THREE.CatmullRomCurve3(wellPts);
      const wbR = Math.max(2, sceneRadius * 0.008);
      const tubeGeom = new THREE.TubeGeometry(curve, Math.max(64, wellPts.length * 2), wbR, 12, false);
      const wellMesh = new THREE.Mesh(tubeGeom, new THREE.MeshPhongMaterial({
        color: darkScene ? 0x2855C7 : 0x1D3550, shininess: 90, specular: darkScene ? 0x6D8FFF : 0x444444,
      }));
      wellMesh.userData.displayCategory = 'wellbore';
      scene.add(wellMesh);
    }

    // ── 3) Stage-local 天然裂缝（Canonical finite planar ellipse） ──
    stageLocalNfModels.forEach((model) => {
      const bounds = model.influenceDomain?.bounds;
      if (!bounds) return;
      const [minX, minY, minZ] = bounds.minXYZ;
      const [maxX, maxY, maxZ] = bounds.maxXYZ;
      const box = new THREE.Box3(
        new THREE.Vector3(minX, -maxZ, minY),
        new THREE.Vector3(maxX, -minZ, maxY),
      );
      const helper = new THREE.Box3Helper(box, 0x6A4C93);
      const helperMaterial = helper.material as THREE.LineBasicMaterial;
      helperMaterial.transparent = true;
      helperMaterial.opacity = 0.32;
      helper.userData.displayCategory = 'nf-domain';
      scene.add(helper);
    });
    const committedBranches = playbackClusters.flatMap((cluster) => cluster.surfaceFrame?.branches ?? []);
    const currentCommittedTimeS = currentFrame?.timeS;
    natFracs.forEach((nf: any) => {
      const visualState = resolveNaturalFractureVisualState(
        nf.id, interactions, committedBranches, currentCommittedTimeS,
      );
      const displayStyle = naturalFractureDisplayStyle(visualState, darkScene, passiveNaturalFractureColor);
      const displayData = {
        displayCategory: 'natural-fracture',
        naturalFractureId: nf.id,
        naturalFractureSource: nf.source,
        naturalFractureState: visualState,
      };
      if (Array.isArray(nf.centerXYZ) && nf.centerXYZ.length >= 3) {
        const [centerX, centerY, centerZ] = nf.centerXYZ.map(Number);
        const dipDirection = Number(nf.dipDirectionDeg);
        const dip = Number(nf.dipDeg);
        const majorLength = Number(nf.majorLengthM);
        const minorLength = Number(nf.minorLengthM);
        if (![centerX, centerY, centerZ, dipDirection, dip, majorLength, minorLength].every(Number.isFinite)) return;
        const dipDirectionRad = dipDirection * Math.PI / 180;
        const dipRad = dip * Math.PI / 180;
        const strikeRad = dipDirectionRad - Math.PI / 2;
        const majorAxis = new THREE.Vector3(Math.sin(strikeRad), 0, Math.cos(strikeRad));
        const dipAxis = new THREE.Vector3(
          Math.cos(dipRad) * Math.sin(dipDirectionRad),
          -Math.sin(dipRad),
          Math.cos(dipRad) * Math.cos(dipDirectionRad),
        );
        const center = new THREE.Vector3(centerX, -centerZ, centerY);
        const points = Array.from({ length: 65 }, (_, index) => {
          const angle = index / 64 * Math.PI * 2;
          return center.clone()
            .addScaledVector(majorAxis, Math.cos(angle) * majorLength / 2)
            .addScaledVector(dipAxis, Math.sin(angle) * minorLength / 2);
        });
        // Passive NFs use thin black/white traces like the geological
        // background in the reference views. Solver interaction state owns
        // the colored emphasis; the viewer does not infer activation.
        const trace = new THREE.LineSegments(
          new THREE.BufferGeometry().setFromPoints([
            center.clone().addScaledVector(majorAxis, -majorLength / 2),
            center.clone().addScaledVector(majorAxis, majorLength / 2),
            center.clone().addScaledVector(dipAxis, -minorLength / 2),
            center.clone().addScaledVector(dipAxis, minorLength / 2),
          ]),
          new THREE.LineBasicMaterial({
            color: displayStyle.color,
            transparent: true,
            opacity: displayStyle.opacity,
            depthWrite: false,
          }),
        );
        trace.renderOrder = displayStyle.emphasized ? 5 : 3;
        trace.userData = { ...displayData, naturalFractureRole: 'trace' };
        scene.add(trace);
        const ellipse = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(points),
          new THREE.LineBasicMaterial({
            color: displayStyle.color,
            transparent: true,
            opacity: displayStyle.emphasized ? displayStyle.opacity : displayStyle.opacity * 0.34,
            depthWrite: false,
          }),
        );
        ellipse.renderOrder = displayStyle.emphasized ? 5 : 2;
        ellipse.userData = { ...displayData, naturalFractureRole: 'ellipse' };
        scene.add(ellipse);
        {
          const triangleVertices: number[] = [];
          for (let index = 0; index < points.length - 1; index += 1) {
            [center, points[index], points[index + 1]].forEach((point) => {
              triangleVertices.push(point.x, point.y, point.z);
            });
          }
          const fillGeometry = new THREE.BufferGeometry();
          fillGeometry.setAttribute('position', new THREE.Float32BufferAttribute(triangleVertices, 3));
          const openedPlane = new THREE.Mesh(
            fillGeometry,
            new THREE.MeshBasicMaterial({
              color: displayStyle.color,
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 0.16,
              depthWrite: false,
            }),
          );
          openedPlane.renderOrder = 4;
          openedPlane.visible = visualState === 'OPEN';
          openedPlane.userData = { ...displayData, naturalFractureRole: 'fill' };
          scene.add(openedPlane);
        }
        return;
      }
      const md = nf.md || nf.depth;
      const pos = worldOf(md);
      if (!pos) return;
      const radiusM = Number(nf.radius_m);
      if (!Number.isFinite(radiusM) || radiusM <= 0) return;
      const strikeDeg = Number.isFinite(Number(nf.strike))
        ? Number(nf.strike)
        : Number.isFinite(Number(nf.dip_azimuth)) ? Number(nf.dip_azimuth) - 90 : null;
      if (strikeDeg === null) return;
      const strikeRad = (strikeDeg * Math.PI) / 180;
      const direction = new THREE.Vector3(Math.sin(strikeRad), 0, Math.cos(strikeRad)).normalize();
      const traceGeometry = new THREE.BufferGeometry().setFromPoints([
        pos.clone().addScaledVector(direction, -radiusM),
        pos.clone().addScaledVector(direction, radiusM),
      ]);
      const trace = new THREE.Line(traceGeometry, new THREE.LineDashedMaterial({
        color: displayStyle.color,
        dashSize: Math.max(0.8, radiusM * 0.18),
        gapSize: Math.max(0.5, radiusM * 0.1),
        transparent: true,
        opacity: displayStyle.opacity,
        depthWrite: false,
      }));
      trace.computeLineDistances();
      trace.renderOrder = displayStyle.emphasized ? 5 : 3;
      trace.userData = displayData;
      scene.add(trace);
    });

    const interactionMeshes: THREE.Mesh[] = [];
    interactions.forEach((interaction: any, interactionIndex) => {
      const md = Number(interaction?.intersectionPosition?.md);
      const xyz = interaction?.intersectionPosition;
      const position = [xyz?.x, xyz?.y, xyz?.z].every((value) => Number.isFinite(Number(value)))
        ? new THREE.Vector3(Number(xyz.x), -Number(xyz.z), Number(xyz.y))
        : worldOf(md);
      if (!position) return;
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(2, sceneRadius * 0.009), 12, 8),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(categoryColor(String(interaction.interactionType || 'UNKNOWN'))) }),
      );
      marker.position.copy(position);
      marker.userData = {
        displayCategory: 'interaction',
        interactionId: interaction.interactionId,
        interactionType: interaction.interactionType,
        interactionRenderKey: interactionRenderKey(interaction, interactionIndex),
        stageNo: interaction.stage,
        interaction,
      };
      scene.add(marker);
      interactionMeshes.push(marker);
    });

    // Geometry is consumed directly from the committed solver DTO. The
    // viewer never synthesizes or extrapolates a hydraulic NF branch.
    playbackClusters.forEach((cluster) => {
      const surfaceFrame = cluster.surfaceFrame;
      (surfaceFrame?.branches ?? []).forEach((branch) => {
        const points = (branch.nodesXYZ ?? [])
          .filter((xyz) => xyz.length >= 3 && xyz.every((value) => Number.isFinite(Number(value))))
          .map((xyz) => new THREE.Vector3(Number(xyz[0]), -Number(xyz[2]), Number(xyz[1])));
        if (points.length < 2) return;
        const displayRadius = Math.max(
          sceneRadius * 0.0035,
          Number(branch.hydraulicApertureM || branch.aperture || 0) * apertureExaggeration * 0.5,
        );
        const fallbackColor = branch.status === 'ARRESTED' || branch.status === 'CLOSED'
          ? naturalFractureDisplayStyle('ARREST', darkScene).color
          : branch.status === 'HYDRAULICALLY_INACTIVE'
            ? naturalFractureDisplayStyle('UNACTIVATED', darkScene).color
            : naturalFractureDisplayStyle('OPEN', darkScene).color;
        const colorForValue = (value: number | undefined) => (
          value !== undefined && resultRangeRef.current.available
            ? new THREE.Color(resultColor(
              value, resultRangeRef.current, resultVariableRef.current,
            )).getHex()
            : fallbackColor
        );
        const addBranchTube = (
          curve: THREE.Curve<THREE.Vector3>, color: number, segmentIndex?: number,
        ) => {
          const branchMesh = new THREE.Mesh(
            new THREE.TubeGeometry(curve, Math.max(2, points.length * 3), displayRadius, 8, false),
            new THREE.MeshPhongMaterial({
              color, emissive: color, emissiveIntensity: 0.35,
              transparent: true, opacity: 0.96, depthWrite: false,
            }),
          );
          branchMesh.renderOrder = 6;
          branchMesh.userData = {
            displayCategory: 'hydraulic-branch',
            branchId: branch.branchId,
            sourceNaturalFractureId: branch.sourceNaturalFractureId,
            branchSegmentIndex: segmentIndex,
            minimumRadius: sceneRadius * 0.0035,
            branch,
          };
          scene.add(branchMesh);
        };
        const segmentStates = branch.proppant?.segments ?? [];
        const renderSolverSegments = Boolean(
          RESULT_PROPERTIES[resultVariableRef.current].branchGranularity
          && segmentStates.length
          && points.length === segmentStates.length + 1,
        );
        if (renderSolverSegments) {
          segmentStates.forEach((segment, index) => {
            addBranchTube(
              new THREE.LineCurve3(points[index], points[index + 1]),
              colorForValue(branchSegmentResultValue(segment, resultVariableRef.current)),
              index,
            );
          });
        } else {
          addBranchTube(
            new THREE.CatmullRomCurve3(points, false, 'centripetal'),
            colorForValue(branchResultValue(branch, resultVariableRef.current)),
          );
        }
      });
      (surfaceFrame?.junctions ?? []).forEach((junction) => {
        const xyz = junction.xyz;
        if (!Array.isArray(xyz) || xyz.length < 3
          || !xyz.every((value) => Number.isFinite(Number(value)))) return;
        const marker = new THREE.Mesh(
          new THREE.SphereGeometry(Math.max(1.5, sceneRadius * 0.007), 12, 8),
          new THREE.MeshBasicMaterial({ color: 0xFFF176 }),
        );
        marker.position.set(Number(xyz[0]), -Number(xyz[2]), Number(xyz[1]));
        marker.renderOrder = 7;
        marker.userData = {
          displayCategory: 'junction', junctionId: junction.junctionId, junction,
        };
        scene.add(marker);
      });
    });

    // ── 4) 每段缝面 ──
    const fracMeshes: THREE.Mesh[] = [];
    const fracturePickMeshes: THREE.Mesh[] = [];
    const stageLabelSprites: THREE.Sprite[] = [];
    const WIDTH_EXAGG = apertureExaggeration;
    fractureMeshesRef.current.clear();
    stageFractureMeshesRef.current.clear();
    fractureRenderTemplates.forEach((renderTemplate) => {
      const stage = stages.find((candidate) => String(candidate.stage_number) === String(renderTemplate.stageId));
      if (!stage) return;
      const P = worldOf(renderTemplate.md);
      if (!P) return;
      const T = tanHorizOf(renderTemplate.md);
      const dynamicClusterId = renderTemplate.dynamicClusterId;
      const U = new THREE.Vector3().crossVectors(T, new THREE.Vector3(0, 1, 0)).normalize();
      const V = new THREE.Vector3(0, 1, 0);

      const isSel = selStage === stage.stage_number;
      const baseColor = new THREE.Color(effColor(stage.efficiency_pct));
      const opacity = selStage === null ? 0.72 : isSel ? 0.98 : 0.16;

      // 宽度→颜色: HSL 高饱和度, 蓝(0mm)→青→绿→黄→红(15mm+)
      const widthColor = (w_mm: number) => {
        const t = Math.min(1, Math.max(0, w_mm / 15));
        const hue = (1 - t) * 0.6;  // 0.6(蓝) → 0(红)
        return new THREE.Color().setHSL(hue, 0.95, 0.45 + t * 0.15);
      };

      const mesh3d = dynamicClusterId ? undefined : stage.mesh;
      let gridNL = 0, gridNH = 0;
      const positions: number[] = [];
      const colors: number[] = [];
      const indices: number[] = [];

      if (mesh3d && mesh3d.x?.length) {
        // P3D: 沿 x 离散 + 椭圆截面, 按时间缩放
        const nx = mesh3d.nx;
        const nth = 24;
        const maxX = mesh3d.x[mesh3d.x.length - 1] || 1;
        for (let i = 0; i < nx; i++) {
          const xi = mesh3d.x[i];
          const hi = Math.max(0.3, mesh3d.h[i]);
          const wi = Math.max(0.001, mesh3d.w0_mm[i] / 1000) * WIDTH_EXAGG;
          const col = widthColor(mesh3d.w0_mm[i]);
          for (let k = 0; k < nth; k++) {
            const th = (2 * Math.PI * k) / nth;
            const localY = (hi / 2) * Math.cos(th);
            const localT = (wi / 2) * Math.sin(th);
            const world = P.clone()
              .addScaledVector(U, xi)
              .addScaledVector(V, localY)
              .addScaledVector(T, localT);
            positions.push(world.x, world.y, world.z);
            colors.push(col.r, col.g, col.b);
          }
        }
        for (let i = 0; i < nx - 1; i++) {
          for (let k = 0; k < nth; k++) {
            const k2 = (k + 1) % nth;
            const a = i * nth + k;
            const b = i * nth + k2;
            const c = (i + 1) * nth + k;
            const d = (i + 1) * nth + k2;
            indices.push(a, b, d, a, d, c);
          }
        }
        const tipR = positions.length / 3;
        const tip = P.clone().addScaledVector(U, mesh3d.x[nx - 1]);
        positions.push(tip.x, tip.y, tip.z);
        const tipColor = new THREE.Color().setHSL(0.6, 0.95, 0.45);
        colors.push(tipColor.r, tipColor.g, tipColor.b);  // 缝尖: 蓝
        for (let k = 0; k < nth; k++) {
          const k2 = (k + 1) % nth;
          indices.push((nx - 1) * nth + k, (nx - 1) * nth + k2, tipR);
        }
      } else {
        // PKN 退化: 椭圆缝面 grid
        const L = Math.max(0.001, stage.half_length), Hf = Math.max(0.001, stage.height);
        const w0 = (stage.w0_mm || 1) / 1000 * WIDTH_EXAGG;
        const nL = 18, nH = 10;
        gridNL = nL; gridNH = nH;
        for (let i = 0; i <= nL; i++) {
          const xi = (i / nL) * L;
          const hi = Hf * Math.sqrt(Math.max(0, 1 - (xi / L) ** 2));
          for (let j = 0; j <= nH; j++) {
            const yj = -hi / 2 + (j / nH) * hi;
            const wj = w0 * Math.sqrt(Math.max(0, 1 - (xi / L) ** 2)) * 0.5;
            const wld = P.clone()
              .addScaledVector(U, xi)
              .addScaledVector(V, yj)
              .addScaledVector(T, wj);
            positions.push(wld.x, wld.y, wld.z);
            const wColor = widthColor(wj / WIDTH_EXAGG * 1000);
            colors.push(wColor.r, wColor.g, wColor.b);
          }
        }
        for (let i = 0; i < nL; i++) {
          for (let j = 0; j < nH; j++) {
            const a = i * (nH + 1) + j;
            const b = a + 1;
            const c = a + (nH + 1);
            const d = c + 1;
            indices.push(a, b, d, a, d, c);
          }
        }
      }

      const buildWing = (signU: 1 | -1) => {
        const verts: number[] = [];
        const wingColors: number[] = [];
        for (let i = 0; i < positions.length; i += 3) {
          if (signU === -1) {
            const w = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
            const dx = w.clone().sub(P);
            const uComp = dx.dot(U);
            const reflected = w.clone().addScaledVector(U, -2 * uComp);
            verts.push(reflected.x, reflected.y, reflected.z);
          } else {
            verts.push(positions[i], positions[i + 1], positions[i + 2]);
          }
          // 颜色跟随顶点
          const ci = i;
          wingColors.push(colors[ci] ?? 0.12, colors[ci + 1] ?? 0.36, colors[ci + 2] ?? 0.62);
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
        g.setAttribute('color', new THREE.Float32BufferAttribute(wingColors, 3));
        g.setIndex(indices);
        g.computeVertexNormals();
        g.computeBoundingBox();
        g.computeBoundingSphere();
        const m = new THREE.MeshPhongMaterial({
          vertexColors: true,
          transparent: true, opacity,
          side: THREE.DoubleSide, shininess: 50,
          emissive: isSel ? baseColor : new THREE.Color(0x000000),
          emissiveIntensity: isSel ? 0.25 : 0,
          depthWrite: isSel,        // 选中缝面写深度, 透明缝面不写
        });
        const mesh = new THREE.Mesh(g, m);
        mesh.frustumCulled = false;
        mesh.renderOrder = isSel ? 0 : 1;
        (mesh as any).userData = {
          stageNo: stage.stage_number,
          isFracture: true,
          fractureAnchor: P.clone(),
          clusterId: dynamicClusterId ?? renderTemplate.id,
          wingSign: signU,
          axisU: U.clone(),
          axisV: V.clone(),
          axisT: T.clone(),
          gridNL,
          gridNH,
          actualWidthMm: 0,
          displayCategory: 'hydraulic-fracture',
        };
        mesh.visible = !dynamicClusterId;
        return mesh;
      };

      const right = buildWing(1);
      const left = buildWing(-1);
      scene.add(right); scene.add(left);
      fracMeshes.push(right, left);
      if (dynamicClusterId) {
        fractureMeshesRef.current.set(dynamicClusterId, [right, left]);
      } else {
        stageFractureMeshesRef.current.set(String(stage.stage_number), [right, left]);
      }

      [right, left].forEach((fractureMesh) => {
        const box = fractureMesh.geometry.boundingBox;
        if (!box) return;
        const size = new THREE.Vector3();
        const boxCenter = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(boxCenter);
        const pickThickness = Math.max(sceneRadius * 0.025, 8);
        size.x = Math.max(size.x, pickThickness);
        size.y = Math.max(size.y, pickThickness * 0.5);
        size.z = Math.max(size.z, pickThickness);
        const pickGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
        pickGeometry.translate(boxCenter.x, boxCenter.y, boxCenter.z);
        const pickMesh = new THREE.Mesh(pickGeometry, new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0,
          depthWrite: false,
          colorWrite: false,
        }));
        pickMesh.frustumCulled = false;
        pickMesh.userData = {
          stageNo: Number(stage.stage_number),
          isFracturePickProxy: true,
          fractureAnchor: P.clone(),
        };
        scene.add(pickMesh);
        fracturePickMeshes.push(pickMesh);
      });

      // 射孔簇用贴合井筒的环形刻线表达，避免球体被误认为桥塞或坐封球。
      const markerRadius = showClusterDetail && Number.isFinite(minPerforationSpacing)
        ? Math.max(0.8, Math.min(sceneRadius * 0.005, minPerforationSpacing * 0.07))
        : Math.max(1.5, sceneRadius * 0.006);
      const perfMarker = new THREE.Mesh(
        new THREE.TorusGeometry(markerRadius, Math.max(0.18, markerRadius * 0.18), 6, 18),
        new THREE.MeshBasicMaterial({ color: 0xC2533A }),
      );
      perfMarker.position.copy(P);
      perfMarker.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), T.clone().normalize());
      perfMarker.userData = { stageNo: stage.stage_number, clusterId: dynamicClusterId ?? renderTemplate.id, isPerforation: true };
      perfMarker.userData.displayCategory = 'hydraulic-fracture';
      scene.add(perfMarker);
      fracMeshes.push(perfMarker);

      // 全井簇级视图不铺满标签；选中单段后显示每簇编号。
      if (selStage !== null || !dynamicClusterId) {
        const lc = document.createElement('canvas');
        lc.width = 64; lc.height = 64;
        const lctx = lc.getContext('2d')!;
        lctx.font = dynamicClusterId ? 'bold 22px Arial' : 'bold 26px Arial';
        lctx.textAlign = 'center'; lctx.textBaseline = 'middle';
        const labelText = dynamicClusterId
          ? `C${(renderTemplate.clusterIndex ?? 0) + 1}`
          : String(stage.stage_number);
        lctx.lineWidth = 5;
        lctx.strokeStyle = 'rgba(255,255,255,0.95)';
        lctx.strokeText(labelText, 32, 34);
        lctx.fillStyle = isSel ? '#A84331' : '#263746';
        lctx.fillText(labelText, 32, 34);
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture(lc), transparent: true,
        }));
        const labelSize = showClusterDetail && Number.isFinite(minPerforationSpacing)
          ? Math.max(6, Math.min(sceneRadius * 0.035, minPerforationSpacing * 0.55))
          : sceneRadius * 0.05;
        const labOff = showClusterDetail ? Math.max(labelSize * 0.7, markerRadius * 2.2) : sceneRadius * 0.04;
        sp.position.copy(P).add(new THREE.Vector3(0, labOff, 0));
        sp.scale.set(labelSize, labelSize, 1);
        sp.userData = { stageNo: Number(stage.stage_number), isStageLabel: true };
        sp.userData.displayCategory = 'hydraulic-fracture';
        scene.add(sp);
        stageLabelSprites.push(sp);
      }
    });

    // ── 4) 地质模型总览网格 ──
    // Keep the result workspace focused on the stimulated interval. Including
    // the entire vertical well section makes the fracture geometry appear tiny
    // and pushes it to the bottom of the viewport.
    const viewBounds = new THREE.Box3();
    fracMeshes.forEach((mesh) => viewBounds.expandByObject(mesh));
    const stimulatedWellPoints = [
      worldOf(minPerforationMd),
      ...survey
        .filter((point) => point.md >= minPerforationMd && point.md <= maxPerforationMd)
        .map((point) => new THREE.Vector3(point.ew, -point.tvd, point.ns)),
      worldOf(maxPerforationMd),
    ].filter((point): point is THREE.Vector3 => point !== null);
    stimulatedWellPoints.forEach((point) => viewBounds.expandByPoint(point));
    if (viewBounds.isEmpty()) {
      wellPts.forEach((point) => viewBounds.expandByPoint(point));
    }
    const resultFocusSize = new THREE.Vector3();
    viewBounds.getSize(resultFocusSize);
    const cameraBounds = viewBounds.clone();
    wellPts.forEach((point) => cameraBounds.expandByPoint(point));
    const extendVerticalGrid = wellboreRangeMode !== 'STIMULATED';
    // 相机只纳入用户选择的井筒显示范围；网格仍裁剪到压裂段范围。
    const focusCenter = new THREE.Vector3();
    cameraBounds.getCenter(focusCenter);
    const focusSize = new THREE.Vector3();
    cameraBounds.getSize(focusSize);
    const focusRadius = Math.max(focusSize.length() * 0.5, 10);
    const defaultViewDirection = new THREE.Vector3(1.5, 1.2, 2.0).normalize();
    const fitCameraToModel = (direction = defaultViewDirection) => {
      const verticalFov = THREE.MathUtils.degToRad(cam.fov);
      const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * cam.aspect);
      const limitingHalfFov = Math.min(verticalFov, horizontalFov) / 2;
      const distance = Math.max((focusRadius / Math.sin(limitingHalfFov)) * 1.02, focusRadius * 1.25, 10);
      cam.position.copy(focusCenter).addScaledVector(direction.clone().normalize(), distance);
      cam.lookAt(focusCenter);
      cam.far = Math.max(cam.far, distance * 6);
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld(true);
    };
    fitCameraToModel();

    // 只绘制一个模型总览线框，不叠加分层面或属性体。优先采用地质建模
    // 中覆盖范围最大的真实层位网格；垂向边界来自真实层位 TVD 与地层顶底。
    const validHorizons = horizons.filter((horizon) => (
      Number.isInteger(horizon.nx) && horizon.nx > 1
      && Number.isInteger(horizon.ny) && horizon.ny > 1
      && Number.isFinite(horizon.origin_east) && Number.isFinite(horizon.origin_north)
      && Number.isFinite(horizon.dx) && horizon.dx > 0
      && Number.isFinite(horizon.dy) && horizon.dy > 0
      && Array.isArray(horizon.grid_z) && horizon.grid_z.length === horizon.nx * horizon.ny
    ));
    const referenceHorizon = validHorizons.reduce<HorizonSurfaceRow | null>((largest, horizon) => {
      if (!largest) return horizon;
      const area = (horizon.nx - 1) * horizon.dx * (horizon.ny - 1) * horizon.dy;
      const largestArea = (largest.nx - 1) * largest.dx * (largest.ny - 1) * largest.dy;
      return area > largestArea ? horizon : largest;
    }, null);
    const validLayers = layers.filter((layer) => (
      Number.isFinite(layer.top_depth) && Number.isFinite(layer.bottom_depth)
      && layer.bottom_depth > layer.top_depth
      && Number.isFinite(layer.extent_x) && Number(layer.extent_x) > 0
      && Number.isFinite(layer.extent_y) && Number(layer.extent_y) > 0
    ));
    let geologicalDepthMin = Number.POSITIVE_INFINITY;
    let geologicalDepthMax = Number.NEGATIVE_INFINITY;
    validHorizons.forEach((horizon) => horizon.grid_z.forEach((depth) => {
      if (depth === null || !Number.isFinite(depth)) return;
      geologicalDepthMin = Math.min(geologicalDepthMin, depth);
      geologicalDepthMax = Math.max(geologicalDepthMax, depth);
    }));
    validLayers.forEach((layer) => {
      geologicalDepthMin = Math.min(geologicalDepthMin, layer.top_depth);
      geologicalDepthMax = Math.max(geologicalDepthMax, layer.bottom_depth);
    });
    let xMin = Number.NaN;
    let xMax = Number.NaN;
    let northMin = Number.NaN;
    let northMax = Number.NaN;
    let xTicks: number[] = [];
    let northTicks: number[] = [];
    const coordinateTicks = (minimum: number, maximum: number, step: number): number[] => {
      if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || !Number.isFinite(step) || step <= 0) return [];
      const values = [minimum];
      for (let value = Math.ceil(minimum / step) * step; value < maximum - step * 1e-6; value += step) {
        if (Math.abs(value - minimum) > step * 1e-6) values.push(value);
      }
      if (Math.abs(maximum - values[values.length - 1]) > step * 1e-6) values.push(maximum);
      return values;
    };

    if (referenceHorizon) {
      xMin = referenceHorizon.origin_east;
      xMax = xMin + (referenceHorizon.nx - 1) * referenceHorizon.dx;
      northMin = referenceHorizon.origin_north;
      northMax = northMin + (referenceHorizon.ny - 1) * referenceHorizon.dy;
      xTicks = Array.from({ length: referenceHorizon.nx }, (_, index) => xMin + index * referenceHorizon.dx);
      northTicks = Array.from({ length: referenceHorizon.ny }, (_, index) => northMin + index * referenceHorizon.dy);
    } else if (validLayers.length) {
      const extentX = Math.max(...validLayers.map((layer) => Number(layer.extent_x)));
      const extentNorth = Math.max(...validLayers.map((layer) => Number(layer.extent_y)));
      xMin = -extentX / 2;
      xMax = extentX / 2;
      northMin = -extentNorth / 2;
      northMax = extentNorth / 2;
      xTicks = coordinateTicks(xMin, xMax, niceGridStep(extentX, 32));
      northTicks = coordinateTicks(northMin, northMax, niceGridStep(extentNorth, 32));
    }

    // 完整地质模型可能远大于当前压裂段。只截取与当前井/裂缝相交的真实
    // 坐标网格，边界仍落在模型网格线上，避免总览尺度挤压裂缝主体。
    const cropCoordinateAxis = (
      ticks: number[], modelMin: number, modelMax: number,
      resultMin: number, resultMax: number, resultSpan: number,
    ): { ticks: number[]; min: number; max: number } => {
      if (ticks.length < 2) return { ticks, min: modelMin, max: modelMax };
      const step = Math.abs(ticks[1] - ticks[0]);
      const padding = Math.max(resultSpan * 0.45, step * 6);
      const wantedMin = Math.max(modelMin, resultMin - padding);
      const wantedMax = Math.min(modelMax, resultMax + padding);
      const clipped = ticks.filter((value) => value >= wantedMin - 1e-6 && value <= wantedMax + 1e-6);
      return clipped.length >= 2
        ? { ticks: clipped, min: clipped[0], max: clipped[clipped.length - 1] }
        : { ticks, min: modelMin, max: modelMax };
    };
    if (Number.isFinite(xMin) && Number.isFinite(northMin)) {
      // Upstream display extends only the TVD wall/ruler.  The horizontal
      // footprint remains cropped to the stimulated interval so selecting
      // 500/1000 m does not create a huge plan-view grid around the well.
      const croppedX = cropCoordinateAxis(xTicks, xMin, xMax, viewBounds.min.x, viewBounds.max.x, resultFocusSize.x);
      const croppedNorth = cropCoordinateAxis(northTicks, northMin, northMax, viewBounds.min.z, viewBounds.max.z, resultFocusSize.z);
      xTicks = croppedX.ticks;
      xMin = croppedX.min;
      xMax = croppedX.max;
      northTicks = croppedNorth.ticks;
      northMin = croppedNorth.min;
      northMax = croppedNorth.max;
    }

    if (Number.isFinite(geologicalDepthMin) && Number.isFinite(geologicalDepthMax)) {
      const modelDepthMin = geologicalDepthMin;
      const modelDepthMax = geologicalDepthMax;
      const resultDepthMin = -viewBounds.max.y;
      const horizontalWellDepth = perfPoints.reduce((sum, point) => sum - point.y, 0) / perfPoints.length;
      if (extendVerticalGrid) {
        const visibleDepths = wellPts.map((point) => -point.y).filter(Number.isFinite);
        // 上延 500/1000 m（或自定义距离）只把垂向网格延伸到所选
        // 井筒段的最高点；不能再沿地质模型顶界继续向上铺满。
        geologicalDepthMin = Math.min(horizontalWellDepth, ...visibleDepths);
        geologicalDepthMax = Math.max(horizontalWellDepth, geologicalDepthMin + 1);
      } else {
        const gridBottomDepth = Math.min(modelDepthMax, Math.max(modelDepthMin, horizontalWellDepth));
        const fractureAboveWellSpan = Math.max(
          horizontalWellDepth - resultDepthMin,
          resultFocusSize.y * 0.5,
          1,
        );
        const depthStep = niceGridStep(fractureAboveWellSpan, 8);
        const paddingAbove = Math.max(fractureAboveWellSpan * 0.35, depthStep * 2);
        const croppedTop = Math.max(modelDepthMin, Math.min(resultDepthMin - paddingAbove, gridBottomDepth - depthStep));
        geologicalDepthMin = croppedTop;
        geologicalDepthMax = gridBottomDepth;
      }
    }

    const overviewPositions: number[] = [];
    const addOverviewSegment = (
      x1: number, y1: number, z1: number,
      x2: number, y2: number, z2: number,
    ) => overviewPositions.push(x1, y1, z1, x2, y2, z2);

    if (Number.isFinite(xMin) && Number.isFinite(xMax)
      && Number.isFinite(northMin) && Number.isFinite(northMax)
      && Number.isFinite(geologicalDepthMin) && Number.isFinite(geologicalDepthMax)) {
      const yTop = -geologicalDepthMin;
      const yBottom = -geologicalDepthMax;
      const depthTicks = coordinateTicks(
        geologicalDepthMin,
        geologicalDepthMax,
        niceGridStep(
          geologicalDepthMax - geologicalDepthMin,
          extendVerticalGrid ? 20 : 12,
        ),
      );

      // X=东向、Y=北向、Z=TVD；Three.js 内部映射为 (X, -Z, Y)。
      // Petrel 类开放式坐标框架：水平井深度处为基准面，只向井筒上方
      // 展开后墙和侧墙；不绘制顶盖、前墙或井筒以下网格。
      xTicks.forEach((x) => addOverviewSegment(x, yBottom, northMin, x, yBottom, northMax));
      northTicks.forEach((north) => addOverviewSegment(xMin, yBottom, north, xMax, yBottom, north));
      xTicks.forEach((x) => {
        addOverviewSegment(x, yBottom, northMin, x, yTop, northMin);
      });
      northTicks.forEach((north) => {
        addOverviewSegment(xMin, yBottom, north, xMin, yTop, north);
      });
      depthTicks.forEach((depth) => {
        const worldY = -depth;
        addOverviewSegment(xMin, worldY, northMin, xMax, worldY, northMin);
        addOverviewSegment(xMin, worldY, northMin, xMin, worldY, northMax);
      });

      const gridGroup = new THREE.Group();
      gridGroup.userData.displayCategory = 'geology-grid';
      gridGroup.visible = displayRef.current.showGeologyGrid;
      const overviewGeometry = new THREE.BufferGeometry();
      overviewGeometry.setAttribute('position', new THREE.Float32BufferAttribute(overviewPositions, 3));
      const overviewGrid = new THREE.LineSegments(overviewGeometry, new THREE.LineBasicMaterial({
        color: darkScene ? 0x76879C : 0x66778A,
        transparent: true, opacity: darkScene ? 0.55 : 0.38, depthWrite: false,
      }));
      overviewGrid.renderOrder = -20;
      gridGroup.add(overviewGrid);

      const labelHeight = Math.max(7, focusRadius * 0.035);
      const labelOffset = labelHeight * 1.1;
      const addGridLabel = (textValue: string, position: THREE.Vector3, color = darkScene ? '#D7DEE8' : '#33465A', scale = 1) => {
        const label = makeTextSprite(textValue, color, labelHeight * scale);
        if (!label) return;
        label.position.copy(position);
        label.renderOrder = 30;
        gridGroup.add(label);
      };
      const xLabelStride = Math.max(1, Math.ceil(xTicks.length / 10));
      const northLabelStride = Math.max(1, Math.ceil(northTicks.length / 10));
      const depthLabelStride = Math.max(1, Math.ceil(depthTicks.length
        / (extendVerticalGrid ? 12 : 8)));
      xTicks.forEach((x, index) => {
        if (index % xLabelStride === 0 || index === xTicks.length - 1) {
          addGridLabel(formatMetres(x), new THREE.Vector3(x, yBottom + labelOffset * 0.25, northMin - labelOffset));
        }
      });
      northTicks.forEach((north, index) => {
        if (index % northLabelStride === 0 || index === northTicks.length - 1) {
          addGridLabel(formatMetres(north), new THREE.Vector3(xMin - labelOffset * 2.6, yBottom + labelOffset * 0.25, north));
        }
      });
      depthTicks.forEach((depth, index) => {
        if (index % depthLabelStride === 0 || index === depthTicks.length - 1) {
          addGridLabel(formatMetres(depth), new THREE.Vector3(xMin - labelOffset * 2.6, -depth, northMin - labelOffset));
        }
      });

      const axisOrigin = new THREE.Vector3(xMin, yTop, northMin);
      const horizontalAxisLength = Math.max(labelHeight * 5, Math.min(xMax - xMin, northMax - northMin) * 0.1);
      const verticalAxisLength = Math.max(labelHeight * 4, Math.min(geologicalDepthMax - geologicalDepthMin, horizontalAxisLength));
      const addGridAxis = (direction: THREE.Vector3, length: number, color: number, label: string) => {
        const arrow = new THREE.ArrowHelper(direction, axisOrigin, length, color, length * 0.16, length * 0.07);
        gridGroup.add(arrow);
        addGridLabel(label, axisOrigin.clone().addScaledVector(direction, length * 1.2), `#${color.toString(16).padStart(6, '0')}`, 1.2);
      };
      addGridAxis(new THREE.Vector3(1, 0, 0), horizontalAxisLength, 0xB84A3A, 'X 东向');
      addGridAxis(new THREE.Vector3(0, 0, 1), horizontalAxisLength, 0x2F7D57, 'Y 北向');
      addGridAxis(new THREE.Vector3(0, -1, 0), verticalAxisLength, 0x326AA8, 'Z 垂深');
      scene.add(gridGroup);
    }

    // ── 5) 3D 地应力方位罗盘 (随场景旋转) ──
    const compassPos = center.clone();
    compassPos.y = groundY;
    const compassR = sceneRadius * 0.08;
    const ringGeom = new THREE.TorusGeometry(compassR, compassR * 0.08, 8, 32);
    const ring = new THREE.Mesh(ringGeom, new THREE.MeshBasicMaterial({ color: 0x888888 }));
    ring.userData.displayCategory = 'stress';
    ring.rotation.x = Math.PI / 2;
    ring.position.copy(compassPos);
    scene.add(ring);

    // Geological azimuth: 0°=North(+Z), 90°=East(+X), clockwise.
    const azimuthToWorld = (azimuth: number) => {
      const rad = (azimuth * Math.PI) / 180;
      return new THREE.Vector3(Math.sin(rad), 0, Math.cos(rad));
    };
    const stressOrigin = compassPos.clone().add(new THREE.Vector3(0, compassR * 0.04, 0));
    if (stressAzimuth !== null) {
      const shmax = stressAzimuth;
      const shmin = (shmax + 90) % 360;
      addArrow(stressOrigin, azimuthToWorld(shmax), compassR * 1.65, 0xC2533A, 'SHmax ' + shmax.toFixed(1) + ' deg', '#C2533A');
      addArrow(stressOrigin, azimuthToWorld(shmin), compassR * 1.20, 0x2874A6, 'Shmin ' + shmin.toFixed(1) + ' deg', '#2874A6');
    } else {
      const unavailable = makeTextSprite('SHmax / Shmin unavailable', '#6B7280', compassR * 0.16);
      if (unavailable) {
        unavailable.position.copy(compassPos).add(new THREE.Vector3(compassR * 0.8, compassR * 0.4, 0));
        scene.add(unavailable);
      }
    }
    // Sv is vertical in the engineering coordinate system (world +Y).
    addArrow(stressOrigin, new THREE.Vector3(0, 1, 0), compassR * 1.35, 0x2049B6, 'Sv', '#2049B6');
    // Fixed lower-left orientation widget. Its group follows the main camera,
    // so it stays on screen while the wellbore scene rotates.
    const orientationScene = new THREE.Scene();
    const orientationCamera = new THREE.OrthographicCamera(-1.25, 1.25, 1.25, -1.25, 0.1, 10);
    orientationCamera.position.set(0, 0, 4);
    orientationCamera.lookAt(0, 0, 0);
    const orientationGroup = new THREE.Group();
    orientationScene.add(orientationGroup);
    const orientationOrigin = new THREE.Vector3(0, 0, 0);
    const addOrientationArrow = (direction: THREE.Vector3, color: number, label: string, labelColor: string) => {
      const length = 0.66;
      const unit = direction.clone().normalize();
      const shaftLength = length * 0.76;
      const material = new THREE.MeshBasicMaterial({ color });
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, shaftLength, 10), material);
      shaft.position.copy(orientationOrigin).addScaledVector(unit, shaftLength * 0.5);
      shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), unit);
      orientationGroup.add(shaft);
      const head = new THREE.Mesh(new THREE.ConeGeometry(0.105, length * 0.24, 10), material);
      head.position.copy(orientationOrigin).addScaledVector(unit, shaftLength + length * 0.12);
      head.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), unit);
      orientationGroup.add(head);
      const sprite = makeTextSprite(label, labelColor, 0.19);
      if (sprite) {
        sprite.position.copy(unit).multiplyScalar(length * 1.14);
        orientationGroup.add(sprite);
      }
    };
    // Engineering labels: X(E)=world +X, Y(N)=world +Z, Z=world +Y.
    addOrientationArrow(new THREE.Vector3(1, 0, 0), 0xE53935, 'X(E)', '#E53935');
    addOrientationArrow(new THREE.Vector3(0, 0, 1), 0x43A047, 'Y(N)', '#43A047');
    addOrientationArrow(new THREE.Vector3(0, 1, 0), 0x2457D6, 'Z', '#2457D6');
    const controls = new OrbitControls(cam, renderer.domElement);
    controls.target.copy(focusCenter);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = Math.max(focusRadius * 0.1, 1);
    controls.maxDistance = Math.max(sceneRadius * 8, focusRadius * 20);
    controls.update();

    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const selectableFractures = fracMeshes.filter((mesh) => mesh.userData?.isFracture);
    const pickStageAtPointer = (ev: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, cam);
      const stagePickObjects: THREE.Object3D[] = [
        ...fracturePickMeshes,
        ...fracMeshes,
        ...stageLabelSprites,
      ];
      scene.traverse((object) => {
        if (object.userData?.displayCategory === 'interaction') stagePickObjects.push(object);
      });
      const hits = raycaster.intersectObjects(stagePickObjects, false);
      const interactionHit = hits.find((hit) => hit.object.userData?.interaction);
      if (interactionHit) {
        onPickInteractionRef.current(interactionHit.object.userData.interaction as Record<string, unknown>);
        return;
      }
      const hitStage = hits.length ? Number(hits[0].object.userData?.stageNo) : NaN;
      let stageNo = Number.isFinite(hitStage) ? hitStage : undefined;

      // Fracture planes can be nearly edge-on. Keep a small screen-space
      // tolerance so clicking the visible surface still selects its stage.
      if (typeof stageNo !== 'number') {
        const pointerX = ev.clientX - rect.left;
        const pointerY = ev.clientY - rect.top;
        let bestDistance = Infinity;
        selectableFractures.forEach((mesh) => {
          const box = mesh.geometry.boundingBox;
          if (!box) return;
          mesh.updateWorldMatrix(true, false);
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          for (const x of [box.min.x, box.max.x]) {
            for (const y of [box.min.y, box.max.y]) {
              for (const z of [box.min.z, box.max.z]) {
                const point = new THREE.Vector3(x, y, z).applyMatrix4(mesh.matrixWorld).project(cam);
                const screenX = ((point.x + 1) * 0.5) * rect.width;
                const screenY = ((1 - point.y) * 0.5) * rect.height;
                minX = Math.min(minX, screenX);
                maxX = Math.max(maxX, screenX);
                minY = Math.min(minY, screenY);
                maxY = Math.max(maxY, screenY);
              }
            }
          }
          const tolerance = 16;
          if (pointerX < minX - tolerance || pointerX > maxX + tolerance || pointerY < minY - tolerance || pointerY > maxY + tolerance) return;
          const distance = Math.hypot(pointerX - (minX + maxX) * 0.5, pointerY - (minY + maxY) * 0.5);
          if (distance < bestDistance) {
            bestDistance = distance;
            const candidateStage = Number(mesh.userData?.stageNo);
            if (Number.isFinite(candidateStage)) stageNo = candidateStage;
          }
        });
      }

      if (typeof stageNo === 'number') {
        onPick(selStage === stageNo ? null : stageNo);
      }
    };
    const pointerDown = new THREE.Vector2();
    let pointerIsDown = false;
    const onPointerDown = (ev: PointerEvent) => {
      pointerDown.set(ev.clientX, ev.clientY);
      pointerIsDown = true;
    };
    const onPointerUp = (ev: PointerEvent) => {
      if (!pointerIsDown) return;
      pointerIsDown = false;
      if (pointerDown.distanceTo(new THREE.Vector2(ev.clientX, ev.clientY)) > 6) return;
      pickStageAtPointer(ev);
    };
    const onPointerMove = (ev: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, cam);
      const hit = raycaster.intersectObjects(selectableFractures.filter((object) => object.visible), false)[0];
      if (!hit) {
        onHoverRef.current(null);
        return;
      }
      const stageNo = Number(hit.object.userData?.stageNo);
      if (!Number.isFinite(stageNo)) {
        onHoverRef.current(null);
        return;
      }
      onHoverRef.current({
        clusterId: hit.object.userData?.clusterId,
        stageNo,
        x: ev.clientX - rect.left,
        y: ev.clientY - rect.top,
      });
    };
    const onPointerLeave = () => onHoverRef.current(null);
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);

    let frameId = 0;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controls.update();
      orientationGroup.quaternion.copy(cam.quaternion).invert();
      renderer.clear();
      renderer.render(scene, cam);
      renderer.clearDepth();

      // setViewport/setScissor accept CSS-pixel dimensions and apply DPR
      // internally. Passing drawing-buffer pixels here scales the viewport
      // twice and shifts the main scene toward the upper-right on HiDPI screens.
      const widgetSize = Math.round(Math.min(132, Math.max(100, Math.min(viewportWidth, viewportHeight) * 0.22)));
      const inset = 10;
      renderer.setScissorTest(true);
      renderer.setViewport(inset, inset, widgetSize, widgetSize);
      renderer.setScissor(inset, inset, widgetSize, widgetSize);
      renderer.render(orientationScene, orientationCamera);
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, viewportWidth, viewportHeight);
    };
    animate();

    const onResize = () => {
      if (!el) return;
      const w = el.clientWidth, h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      const currentDirection = cam.position.clone().sub(controls.target);
      viewportWidth = w;
      viewportHeight = h;
      cam.aspect = w / h;
      renderer.setSize(w, h);
      fitCameraToModel(currentDirection.lengthSq() > 0 ? currentDirection : defaultViewDirection);
      controls.target.copy(focusCenter);
      controls.update();
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(el);
    onResize();
    window.addEventListener('resize', onResize);
    // 场景重建（背景切换、重置视角或数据切换）后，强制把当前物理帧
    // 重新写入新建的 BufferGeometry，避免必须拖动时间轴才显示裂缝面。
    setSceneRevision((revision) => revision + 1);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      onHoverRef.current(null);
      controls.dispose();
      renderer.dispose();
      const disposeScene = (target: THREE.Scene) => {
        target.traverse((obj: any) => {
          if (obj.geometry) obj.geometry.dispose?.();
          if (obj.material) {
            (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach((m: any) => {
              m.map?.dispose?.();
              m.dispose?.();
            });
          }
        });
      };
      disposeScene(scene);
      disposeScene(orientationScene);
      el.innerHTML = '';
      sceneRef.current = null;
      fractureMeshesRef.current.clear();
      stageFractureMeshesRef.current.clear();
      worldOfRef.current = () => null;
    };
  }, [stages, survey, layers, horizons, natFracs, stageLocalNfModels, modelType, selStage, stressAzimuth, topologyRevision, sceneBackground, wellboreRangeMode, wellboreCustomUpstreamM, resetViewNonce]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.traverse((object) => {
      const category = object.userData?.displayCategory;
      if (category === 'wellbore') object.visible = showWellbore;
      if (category === 'geology-grid') object.visible = showGeologyGrid;
      if (category === 'natural-fracture') object.visible = showNaturalFractures
        && (object.userData?.naturalFractureRole !== 'fill' || object.userData?.naturalFractureState === 'OPEN');
      if (category === 'nf-domain') object.visible = showNaturalFractures;
      if (category === 'stress') object.visible = showStressDirection;
      if (category === 'hydraulic-fracture') object.visible = showHydraulicFractures;
      if (category === 'interaction') object.visible = showInteractionPoints;
      if (category === 'junction') object.visible = showInteractionPoints;
      if (category === 'hydraulic-branch') object.visible = showHydraulicFractures && showNaturalFractures;
    });
  }, [showHydraulicFractures, showWellbore, showGeologyGrid, showNaturalFractures, showStressDirection, showInteractionPoints, stages, playbackClusters, interactions]);

  // 每个 Cluster 复用固定 BufferGeometry，只更新当前物理状态对应的顶点。
  useEffect(() => {
    if (!currentFrame) return;
    const scene = sceneRef.current;
    if (!scene) return;
    const world = new THREE.Vector3();
    currentFrame.clusters.forEach((cluster) => {
      const meshes = fractureMeshesRef.current.get(cluster.clusterId);
      if (!meshes) return;
      meshes.forEach((mesh) => {
        const data = mesh.userData;
        const surfaceFrame = cluster.surfaceFrame;
        const directionalSurface = surfaceFrame?.model === 'DIRECTIONAL_ADAPTIVE_SURFACE_V1';
        const wingName = Number(data.wingSign) < 0 ? 'LEFT' : 'RIGHT';
        const solverSurface = directionalSurface
          ? surfaceFrame?.wings?.[wingName]
          : surfaceFrame;
        if ((surfaceFrame?.model === 'ADAPTIVE_PLANAR_SURFACE_V1' || directionalSurface)
          && solverSurface?.nodes?.length && solverSurface.faces?.length) {
          const wingSign = Number(data.wingSign) < 0 ? -1 : 1;
          const anchor = data.fractureAnchor as THREE.Vector3;
          const U = data.axisU as THREE.Vector3;
          const V = data.axisV as THREE.Vector3;
          const T = data.axisT as THREE.Vector3;
          const definition = RESULT_PROPERTIES[resultVariableRef.current];
          const clusterValue = definition.getValue?.(cluster);
          const category = definition.getCategory?.(cluster);
          const supportsSurfaceField = [
            'dynamic_width', 'fracture_pressure', 'net_pressure', 'local_shmin',
            'proppant_concentration', 'proppant_areal_concentration',
            'propped_width', 'conductivity',
          ]
            .includes(resultVariableRef.current);
          const available = definition.dataType === 'CATEGORICAL'
            ? Boolean(category)
            : supportsSurfaceField
              ? resultRangeRef.current.available
              : clusterValue !== undefined && resultRangeRef.current.available;
          const renderState = resolveHydraulicFractureRenderState({
            show: displayRef.current.showHydraulicFractures,
            initiated: cluster.isInitiated,
            hasGeometry: true,
            resultAvailable: available,
          });
          mesh.visible = renderState.visible;
          if (!renderState.visible) return;
          const vertices: number[] = [];
          const vertexColors: number[] = [];
          solverSurface.nodes.forEach((node) => {
            const displayAperture = Math.max(0, Number(node.width) || 0) * apertureExaggeration;
            world.copy(anchor)
              .addScaledVector(U, directionalSurface
                ? (Number(node.x) || 0)
                : wingSign * (Number(node.x) || 0))
              .addScaledVector(T, directionalSurface ? (Number(node.y) || 0) : 0)
              .addScaledVector(V, Number(node.z) || 0)
              .addScaledVector(T, displayAperture * 0.5);
            vertices.push(world.x, world.y, world.z);
            const nodeValue = surfaceNodeResultValue(node, resultVariableRef.current);
            const mapped = new THREE.Color(renderState.useNeutralColor
              ? '#8A94A3'
              : definition.dataType === 'CATEGORICAL'
                ? categoryColor(category!)
                : resultColor(
                  supportsSurfaceField ? (nodeValue ?? 0) : (clusterValue ?? 0),
                  resultRangeRef.current,
                  resultVariableRef.current,
                ));
            vertexColors.push(mapped.r, mapped.g, mapped.b);
          });
          const surfaceIndices: number[] = [];
          solverSurface.faces.forEach((face) => {
            if (face.length === 3) surfaceIndices.push(face[0], face[1], face[2]);
            else if (face.length >= 4) {
              for (let index = 1; index < face.length - 1; index += 1) {
                surfaceIndices.push(face[0], face[index], face[index + 1]);
              }
            }
          });
          const currentPosition = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
          const currentColor = mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
          const currentIndex = mesh.geometry.getIndex();
          const canReuse = currentPosition?.count === vertices.length / 3
            && currentColor?.count === vertexColors.length / 3
            && currentIndex?.count === surfaceIndices.length;
          if (canReuse) {
            (currentPosition.array as Float32Array).set(vertices);
            (currentColor.array as Float32Array).set(vertexColors);
            (currentIndex.array as Uint16Array | Uint32Array).set(surfaceIndices);
            currentPosition.needsUpdate = true;
            currentColor.needsUpdate = true;
            currentIndex.needsUpdate = true;
            mesh.geometry.computeVertexNormals();
            mesh.geometry.computeBoundingBox();
            mesh.geometry.computeBoundingSphere();
          } else {
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(vertexColors, 3));
            geometry.setIndex(surfaceIndices);
            geometry.computeVertexNormals();
            geometry.computeBoundingBox();
            geometry.computeBoundingSphere();
            mesh.geometry.dispose();
            mesh.geometry = geometry;
          }
          data.actualWidthMm = Math.max(...solverSurface.nodes.map((node) => Number(node.width) * 1000 || 0));
          data.geometrySource = directionalSurface
            ? 'SOLVER_DIRECTIONAL_FRACTURE_SURFACE_V1'
            : 'SOLVER_FRACTURE_SURFACE_GRID_V1';
          const material = mesh.material as THREE.MeshPhongMaterial;
          material.vertexColors = true;
          material.color.set(0xffffff);
          material.emissive.set(0x000000);
          material.needsUpdate = true;
          return;
        }
        if (data.geometrySource === 'SOLVER_FRACTURE_SURFACE_GRID_V1'
          || data.geometrySource === 'SOLVER_DIRECTIONAL_FRACTURE_SURFACE_V1') {
          mesh.visible = false;
          return;
        }
        const nL = Number(data.gridNL);
        const nH = Number(data.gridNH);
        const position = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
        if (!position || nL <= 0 || nH <= 0) {
          mesh.visible = false;
          return;
        }
        const wingSign = Number(data.wingSign) < 0 ? -1 : 1;
        const length = wingSign < 0 ? cluster.leftWingLengthM : cluster.rightWingLengthM;
        const height = Math.max(0, cluster.heightM);
        const { upperM: upper, lowerM: lower } = resolveFractureVerticalExtents(cluster);
        const displayWidth = getDisplayWidthM(cluster.actualWidthM, apertureExaggeration);
        const hasGeometry = length > 0 && height > 0 && displayWidth > 0;
        const definition = RESULT_PROPERTIES[resultVariableRef.current];
        const fieldValue = definition.getValue?.(cluster);
        const category = definition.getCategory?.(cluster);
        const resultAvailable = definition.dataType === 'CATEGORICAL'
          ? Boolean(category)
          : fieldValue !== undefined && resultRangeRef.current.available;
        const renderState = resolveHydraulicFractureRenderState({
          show: displayRef.current.showHydraulicFractures,
          initiated: cluster.isInitiated,
          hasGeometry,
          resultAvailable,
        });
        mesh.visible = renderState.visible;
        if (!renderState.visible) return;
        const anchor = data.fractureAnchor as THREE.Vector3;
        const U = data.axisU as THREE.Vector3;
        const V = data.axisV as THREE.Vector3;
        const T = data.axisT as THREE.Vector3;
        for (let i = 0; i <= nL; i += 1) {
          const fraction = i / nL;
          const radial = Math.sqrt(Math.max(0, 1 - fraction * fraction));
          const xi = length * fraction;
          for (let j = 0; j <= nH; j += 1) {
            const theta = (2 * Math.PI * j) / nH;
            const localY = radial * ((upper - lower) / 2 + ((upper + lower) / 2) * Math.cos(theta));
            const localT = radial * (displayWidth / 2) * Math.sin(theta);
            world.copy(anchor)
              .addScaledVector(U, wingSign * xi)
              .addScaledVector(V, localY)
              .addScaledVector(T, localT);
            position.setXYZ(i * (nH + 1) + j, world.x, world.y, world.z);
          }
        }
        position.needsUpdate = true;
        data.actualWidthMm = cluster.actualWidthMm;
        const material = mesh.material as THREE.MeshPhongMaterial;
        const mappedColor = new THREE.Color(renderState.useNeutralColor
          ? '#8A94A3'
          : definition.dataType === 'CATEGORICAL'
            ? categoryColor(category!)
            : resultColor(fieldValue!, resultRangeRef.current, resultVariableRef.current));
        material.vertexColors = false;
        material.color.copy(mappedColor);
        material.emissive.copy(mappedColor);
        material.needsUpdate = true;
        mesh.geometry.computeVertexNormals();
      });
    });
    stageFractureMeshesRef.current.forEach((meshes, stageId) => {
      const clusters = currentFrame.clusters.filter((cluster) => String(cluster.stageId) === stageId && cluster.isInitiated);
      const definition = RESULT_PROPERTIES[resultVariableRef.current];
      const numericValues = definition.getValue
        ? clusters.map(definition.getValue).filter((value): value is number => value !== undefined && Number.isFinite(value))
        : [];
      const categories = definition.getCategory
        ? clusters.map(definition.getCategory).filter((value): value is string => Boolean(value))
        : [];
      const available = definition.dataType === 'CATEGORICAL'
        ? categories.length > 0
        : numericValues.length > 0 && resultRangeRef.current.available;
      const mappedColor = definition.dataType === 'CATEGORICAL'
        ? (categories.length ? categoryColor(categories[0]) : '#8A94A3')
        : (numericValues.length
          ? resultColor(numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length, resultRangeRef.current, resultVariableRef.current)
          : '#8A94A3');
      meshes.forEach((mesh) => {
        mesh.visible = displayRef.current.showHydraulicFractures && clusters.length > 0;
        const material = mesh.material as THREE.MeshPhongMaterial;
        material.vertexColors = false;
        material.color.set(mappedColor);
        material.emissive.set(mappedColor);
        material.needsUpdate = true;
      });
    });

    const currentBranches = currentFrame.clusters.flatMap((cluster) => cluster.surfaceFrame?.branches ?? []);
    const currentJunctions = currentFrame.clusters.flatMap((cluster) => cluster.surfaceFrame?.junctions ?? []);
    const existingBranchKeys = new Set<string>();
    const existingJunctionIds = new Set<string>();
    const existingInteractionKeys = new Set<string>();
    scene.traverse((object) => {
      if (object.userData?.displayCategory === 'hydraulic-branch') {
        existingBranchKeys.add(`${object.userData.branchId}:${object.userData.branchSegmentIndex ?? 'all'}`);
      } else if (object.userData?.displayCategory === 'junction') {
        existingJunctionIds.add(String(object.userData.junctionId));
      } else if (object.userData?.displayCategory === 'interaction') {
        existingInteractionKeys.add(String(object.userData.interactionRenderKey ?? object.userData.interactionId ?? ''));
      }
    });
    const minimumBranchRadius = sceneRadiusRef.current * 0.0035;
    currentBranches.forEach((branch) => {
      const points = (branch.nodesXYZ ?? [])
        .filter((xyz) => xyz.length >= 3 && xyz.every((value) => Number.isFinite(Number(value))))
        .map((xyz) => new THREE.Vector3(Number(xyz[0]), -Number(xyz[2]), Number(xyz[1])));
      if (points.length < 2) return;
      const segments = branch.proppant?.segments ?? [];
      const renderSegments = Boolean(
        RESULT_PROPERTIES[resultVariableRef.current].branchGranularity
        && segments.length && points.length === segments.length + 1,
      );
      const targets = renderSegments
        ? segments.map((segment, index) => ({
          key: `${branch.branchId}:${index}`,
          segmentIndex: index as number | undefined,
          curve: new THREE.LineCurve3(points[index], points[index + 1]) as THREE.Curve<THREE.Vector3>,
          value: branchSegmentResultValue(segment, resultVariableRef.current),
        }))
        : [{
          key: `${branch.branchId}:all`, segmentIndex: undefined as number | undefined,
          curve: new THREE.CatmullRomCurve3(points, false, 'centripetal') as THREE.Curve<THREE.Vector3>,
          value: branchResultValue(branch, resultVariableRef.current),
        }];
      targets.forEach((target) => {
        if (existingBranchKeys.has(target.key)) return;
        const fallback = branch.status === 'ARRESTED' || branch.status === 'CLOSED'
          ? naturalFractureDisplayStyle('ARREST', sceneBackground === 'DARK').color
          : naturalFractureDisplayStyle('OPEN', sceneBackground === 'DARK').color;
        const color = target.value !== undefined && resultRangeRef.current.available
          ? new THREE.Color(resultColor(target.value, resultRangeRef.current, resultVariableRef.current)).getHex()
          : fallback;
        const radius = Math.max(
          minimumBranchRadius,
          Number(branch.hydraulicApertureM || branch.aperture || 0) * apertureExaggeration * 0.5,
        );
        const mesh = new THREE.Mesh(
          new THREE.TubeGeometry(target.curve, Math.max(2, points.length * 3), radius, 8, false),
          new THREE.MeshPhongMaterial({
            color, emissive: color, emissiveIntensity: 0.35,
            transparent: true, opacity: 0.96, depthWrite: false,
          }),
        );
        mesh.renderOrder = 6;
        mesh.userData = {
          displayCategory: 'hydraulic-branch', branchId: branch.branchId,
          sourceNaturalFractureId: branch.sourceNaturalFractureId,
          branchSegmentIndex: target.segmentIndex, minimumRadius: minimumBranchRadius, branch,
        };
        mesh.visible = displayRef.current.showHydraulicFractures && displayRef.current.showNaturalFractures;
        scene.add(mesh);
        existingBranchKeys.add(target.key);
      });
    });
    currentJunctions.forEach((junction) => {
      const id = String(junction.junctionId ?? '');
      const xyz = junction.xyz;
      if (!id || existingJunctionIds.has(id) || !Array.isArray(xyz) || xyz.length < 3
        || !xyz.every((value) => Number.isFinite(Number(value)))) return;
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(1.5, sceneRadiusRef.current * 0.007), 12, 8),
        new THREE.MeshBasicMaterial({ color: 0xFFF176 }),
      );
      marker.position.set(Number(xyz[0]), -Number(xyz[2]), Number(xyz[1]));
      marker.renderOrder = 7;
      marker.visible = displayRef.current.showInteractionPoints;
      marker.userData = { displayCategory: 'junction', junctionId: id, junction };
      scene.add(marker);
      existingJunctionIds.add(id);
    });
    interactions.forEach((interaction: any, interactionIndex) => {
      const key = interactionRenderKey(interaction, interactionIndex);
      if (existingInteractionKeys.has(key)) return;
      const xyz = interaction?.intersectionPosition;
      const md = Number(xyz?.md);
      const position = [xyz?.x, xyz?.y, xyz?.z].every((value) => Number.isFinite(Number(value)))
        ? new THREE.Vector3(Number(xyz.x), -Number(xyz.z), Number(xyz.y))
        : worldOfRef.current(md);
      if (!position) return;
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(2, sceneRadiusRef.current * 0.009), 12, 8),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(categoryColor(String(interaction.interactionType || 'UNKNOWN'))) }),
      );
      marker.position.copy(position);
      marker.userData = {
        displayCategory: 'interaction', interactionId: interaction.interactionId,
        interactionRenderKey: key, interactionType: interaction.interactionType,
        stageNo: interaction.stage, interaction,
      };
      marker.visible = displayRef.current.showInteractionPoints;
      scene.add(marker);
      existingInteractionKeys.add(key);
    });
    const branchById = new Map(currentBranches.map((branch) => [branch.branchId, branch]));
    scene.traverse((object) => {
      const category = object.userData?.displayCategory;
      if (category === 'hydraulic-branch' && object instanceof THREE.Mesh) {
        const branch = branchById.get(String(object.userData.branchId));
        if (!branch) { object.visible = false; return; }
        const points = (branch.nodesXYZ ?? [])
          .filter((xyz) => xyz.length >= 3 && xyz.every((value) => Number.isFinite(Number(value))))
          .map((xyz) => new THREE.Vector3(Number(xyz[0]), -Number(xyz[2]), Number(xyz[1])));
        if (points.length < 2) { object.visible = false; return; }
        const segmentIndex = Number(object.userData.branchSegmentIndex);
        const useSegment = Number.isInteger(segmentIndex) && segmentIndex >= 0 && segmentIndex + 1 < points.length;
        const radius = Math.max(
          Number(object.userData.minimumRadius) || 0.1,
          Number(branch.hydraulicApertureM || branch.aperture || 0) * apertureExaggeration * 0.5,
        );
        const curve: THREE.Curve<THREE.Vector3> = useSegment
          ? new THREE.LineCurve3(points[segmentIndex], points[segmentIndex + 1])
          : new THREE.CatmullRomCurve3(points, false, 'centripetal');
        const nextGeometry = new THREE.TubeGeometry(curve, Math.max(2, points.length * 3), radius, 8, false);
        object.geometry.dispose();
        object.geometry = nextGeometry;
        const segment = useSegment ? branch.proppant?.segments?.[segmentIndex] : undefined;
        const value = segment
          ? branchSegmentResultValue(segment, resultVariableRef.current)
          : branchResultValue(branch, resultVariableRef.current);
        const fallback = branch.status === 'ARRESTED' || branch.status === 'CLOSED'
          ? naturalFractureDisplayStyle('ARREST', sceneBackground === 'DARK').color
          : naturalFractureDisplayStyle('OPEN', sceneBackground === 'DARK').color;
        const color = value !== undefined && resultRangeRef.current.available
          ? new THREE.Color(resultColor(value, resultRangeRef.current, resultVariableRef.current))
          : new THREE.Color(fallback);
        const material = object.material as THREE.MeshPhongMaterial;
        material.color.copy(color);
        material.emissive.copy(color);
        object.visible = displayRef.current.showHydraulicFractures && displayRef.current.showNaturalFractures;
      }
      if (category === 'natural-fracture') {
        const state = resolveNaturalFractureVisualState(
          object.userData?.naturalFractureId, interactions, currentBranches, currentFrame.timeS,
        );
        const style = naturalFractureDisplayStyle(state, sceneBackground === 'DARK', passiveNaturalFractureColor);
        const material = (object as THREE.Mesh | THREE.Line).material as THREE.Material & { color?: THREE.Color; opacity?: number };
        material?.color?.set(style.color);
        if (material && 'opacity' in material) {
          material.opacity = object.userData?.naturalFractureRole === 'ellipse' && !style.emphasized
            ? style.opacity * 0.34 : style.opacity;
          material.needsUpdate = true;
        }
        object.visible = displayRef.current.showNaturalFractures
          && (object.userData?.naturalFractureRole !== 'fill' || state === 'OPEN');
        object.userData.naturalFractureState = state;
      }
      if (category === 'interaction') {
        const eventTime = Number(object.userData?.interaction?.time ?? 0);
        object.visible = displayRef.current.showInteractionPoints
          && (!Number.isFinite(eventTime) || eventTime <= currentFrame.timeS + 1e-9);
      }
    });
  }, [currentFrame, apertureExaggeration, resultVariableId, resultRange, resetViewNonce, sceneRevision, showHydraulicFractures, interactions, sceneBackground, naturalFractureColor]);

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
}
